import {create} from 'zustand';
import type {AuditAdjustmentLine} from '@/services/auditService/auditAdjustmentService';
import type {AuditAdjustHeader, AuditAdjustLine} from '@/services/auditService/auditAdjustOrchestrate';
import {AdjustmentMode} from '@/services/auditService/adjustmentModes';
import {
    applyToPaperAssemblyOutcome,
    computeToPaperFieldsForAssembly,
    fetchToPaperWpOutcome,
    isToPaperAssemblyReadOnlyMode,
    verifyToPaperWpDetail,
    type ToPaperAssemblyStatus,
    type ToPaperPendingUserAction,
} from '@/store/audit/toPaperAssemblyCore';
import {
    getActiveWpSpanType,
    getActiveWpYearOfAssessment,
} from '@/services/auditService/wpPeriodYoaBridge';
import {resolveAdjAssembleYearOfAssessment} from '@/utils/auditPeriodWpMulti';
import {
    buildLoanMultiYearAdjDetailsMap,
    isLoanMultiYearAdjDetailsMap,
} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/bankLoanMultiYearSave';
import {
    applyEmptyRecordToPaperDetail,
    applyToPaperRowDetail,
    createCommonInvestmentInitialData,
    createCommonInvestmentInitialDataWithSelection,
    createInitialDetailForCode,
    getCommonInvestmentRowOptions,
    getMainArrayNameForCode,
    isCommonInvestmentCode,
    isEmptyRecordSelectionRow,
    isIaOrAiaToPaperCode,
    isItlTaxiDepToPaper,
    isIssuedCapitalToPaperCode,
    isPlAeRenCode,
    isPlAeRenSubmenuFetchCode,
    applyIaToPaperRowDetail,
    applyPpeToPaperRowDetail,
    isPpeCategoryFetchCode,
    isPpeOrDepToPaperCode,
    resolvePpeCategoryFetchCode,
    tryBuildIaToPaperWpFetchResult,
    tryBuildIcToPaperWpFetchResult,
    tryBuildIaAmorNbvCrossToPaperResult,
    tryBuildInboundPpeRootToPaperResult,
    tryBuildPlainRenFieldToPaperResult,
    tryBuildPpeDepnNbvCrossToPaperResult,
    isInboundPpeRootLineOnlyToPaper,
    unwrapWorkingPaperDetail,
    WP_METADATA_ONLY_KEYS,
} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/initial-data-factories';
import {processScheduleToLine} from '@/store/audit/adjustLine';
import {useFsAdjustStore} from '@/store/audit/fsAdjustStore';
import {
    computeEffectiveEntry,
    hasAlCommonRowDetail,
    isCommonRowSyntheticEditCode,
    isCommonRowSyntheticEditDetail,
    isFlatEquityAdjustmentCode,
    isFlatEquityDetailForCode,
    normalizeDetailForSubmit,
    resolveFlatEquityAdjustment,
    resolveFlatEquityRenderEntry,
    scanAllRows,
} from '@/pages/components/audit/adjustment/adjust/utils/classifyRows';
    import {consolidateWpDetailAdjustments, consolidateWpRowAdjustments, createPlFcLiAdjustmentDetail, ensurePlFcLiLoanFieldAdjustmentDetail, isLoanFieldFsCode, isLoanFieldSyntheticDetail, isLoanWpDetail, isPpeFieldFsCode, isPpeFieldSyntheticDetail, isPpeDepnNbvCrossFieldDetail, isPpeDepnNbvCrossOnlyFieldHeader, isPpeDepScheduleLevelHeader, resolvePpeDepScheduleSubmitAmount, toPpeDepnNbvCrossSlimDetailFromBackend, buildPpeSlimFieldAdjDetail, isRentalFieldSyntheticDetail, rentalFieldAttrMapsOutOfSync, rescalePpeFieldAdjKeysForAmount, rescaleRentalFieldAttrMapsForAmount, toPpeFieldAdjSubmitDetail, transformFlatPpeFieldAttrDetails, collectNestedPpeFieldAdjScalars, PPE_FIELD_ADJ_KEYS, PPE_NBV_SPLIT_FLAG, isIaFieldFsCode, isIaFieldSyntheticDetail, isIaFieldAdjHeader, isIaAmorNbvCrossFieldDetail, rescaleIaFieldAdjKeysForAmount, toIaFieldAdjSubmitDetail, toIaFieldSlimDetailFromBackend, isIaNamedNestShellDetail, isItlDepIaFieldCode, isItlDepIaFieldTabsNonDtdDetail, isIaScheduleLevelNbvOrAmorContext, isPpeScheduleLevelNbvOrDepnContext, isAssetLineOnlyAmountSubmit, isBlankWpDetail, isIaAiaScheduleLevelHeader, isItlDepScheduleLevelHeader, isIaAmorNbvCrossOnlyHeader, iaOwnedFieldKey} from '@/pages/components/audit/adjustment/adjust/utils/consolidateWpAdjustments';
// TODO fromPaper debug 使用
// import {logFromPaperAssembly} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/fromPaper-assembly-log';
import {
    isDueToDirectorsCode,
    normalizeDueFromToDirDetail,
    normalizeDueFromToDirRow,
    resolveFromPaperAdjustmentFields,
    isNoWpDetailCode,
    resolveEntryFromFsCategory,
    isDtdClFsCode,
    usesDtdAdjustmentPathScoped,
    resolveAdjustLineEntry,
    resolveDueFromToDirectorsFromEntry,
    resolveSignedAdjustmentFromBackendLine,
    resolveNonDtdSignedAdjustmentFromRenderEntry,
    flipDtdDetailRowAdjustment,
    isTurnoverIncomeFsCode,
    pickExistingTurnoverAnnualInfo,
    withTurnoverAnnualInfoIfMissing,
    resolveAppendEffectiveEntry,
    resolveFsNodeOriginalEntry,
    coerceDrCrEntry,
    resolveLineFsId,
    resolveToPaperFieldsFromPairing,
    alignTaxPayableBfToPaperAdjustment,
    isTaxPayableBfDtdCode,
} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/adjustment-helpers';
import {
    logFromPaperAssembly,
    logToPaperAssembly,
    logToPaperAssemblyBatch,
} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/fromPaper-assembly-log';
import {findCommonInvestmentOptionByBinding} from '@/pages/components/audit/adjustment/adjust/support/RowSelector';
import {
    collectLineCodes,
    isCompanionHiddenCode,
    normalizeFsCode,
    resolveAdjustmentOrigin,
    isInvExplicitPurchasesToPaper,
    isInvExplicitPurchasesHeaderLines,
    resolveCompanionHiddenCodes,
    resolveInvPurchasesCompanionHiddenForUi,
} from '@/pages/components/audit/adjustment/adjust/utils/companion-codes';
import {resolveRemarkForEdit} from '@/pages/components/audit/adjustment/adjust/utils/remarkDisplayUtils';
import {
    isTaxWpFsCode,
    isTaxWpBfSpecialAdjustmentLine,
    resolveTaxWpToPaperDefaultLogicalIndex,
} from '@/pages/components/audit/adjustment/adjust/dialogs/tax/taxWpFromPaper';
import {sanitizeSpecialAdjustFlowToPaperDetail, isSpecialAdjustScopedEligible, isTaxBfSpecialDtdCode} from '@/pages/components/audit/adjustment/adjust/utils/specialAdjustFlow';

function normalizeLineCode(input: any): string {
    const raw = String(input || '').trim();
    if (!raw) return '';
    const upper = raw.toUpperCase();
    if (upper.startsWith('BS-') || upper.startsWith('PL-') || upper.startsWith('FS-')) return upper;
    const mapped = useFsAdjustStore.getState().getLineByCode(raw)?.code;
    const mappedUpper = String(mapped || '').trim().toUpperCase();
    if (mappedUpper.startsWith('BS-') || mappedUpper.startsWith('PL-') || mappedUpper.startsWith('FS-')) return mappedUpper;
    return upper;
}

/**
 * 仅 PPE 分类 discriminator（xxxRow）保留 camelCase；其它 wpFetchCode 仍走原 normalizeLineCode。
 * 不影响 REN / MPF / loan 等原有逻辑。
 */
function normalizeWpFetchCodeForStore(raw: string | undefined | null): string {
    const trimmed = String(raw || '').trim();
    if (!trimmed) return '';
    const ppeCategory = resolvePpeCategoryFetchCode(trimmed);
    if (ppeCategory) return ppeCategory;
    return normalizeLineCode(trimmed);
}

/**
 * NO_WP_DETAIL 中需拉 WP 的特殊路径（保持原口径，勿扩大到 plain REN）：
 * - REN rental|other 二级菜单（遗留）
 * - PPE xxxRow 分类
 * - IA/AIA 作为 toPaper 叶子（无分类 discriminator；fromPaper 仍走普通空 details）
 *
 * plain PL-AE-REN field shell 仅在 syncToPaperAssembly.needsWpFetch /
 * fetchAndAssembleToPaper（tryBuildPlainRenFieldToPaperResult）局部处理。
 *
 * 注意：BS-EQ-IC toPaper 拉 WP 仅走 syncToPaperAssembly.needsWpFetch / fetchAndAssembleToPaper，
 * 不进此函数，以免改变 IC 原有 NO_WP 校验与空 details 提交路径。
 */
function isNoWpDetailCategoryFetchPath(code: string, state: {toPaper?: ToPaperData | null}): boolean {
    const wpFetch = lookupToPaperMap(state.toPaper?.wpFetchCodes, code);
    const isIaToPaperFetch = isIaOrAiaToPaperCode(code)
        && toPaperContainsCode(state.toPaper?.codes, code);
    const isInvToPaperFetch = isCommonInvestmentCode(code)
        && toPaperContainsCode(state.toPaper?.codes, code);
    const isItlDepFetch = isItlTaxiDepToPaper(code, wpFetch)
        && toPaperContainsCode(state.toPaper?.codes, code);
    return (
        (isPlAeRenCode(code) && isPlAeRenSubmenuFetchCode(wpFetch))
        || (isPpeOrDepToPaperCode(code) && isPpeCategoryFetchCode(wpFetch))
        || isIaToPaperFetch
        || isInvToPaperFetch
        || isItlDepFetch
    );
}

/** Plain PL-AE-REN toPaper（无 rental|other）：需 seed `{rows, adjustment}` field shell。 */
function needsPlainRenFieldShellAssemble(
    code: string,
    state: {toPaper?: ToPaperData | null},
): boolean {
    if (!isPlAeRenCode(code)) return false;
    if (!toPaperContainsCode(state.toPaper?.codes, code)) return false;
    const wpFetch = lookupToPaperMap(state.toPaper?.wpFetchCodes, code);
    return !isPlAeRenSubmenuFetchCode(wpFetch);
}

/** BS-EQ-IC GET bag from getAdjustmentWithWorkingPapers (shareholders/directors/…). Not slim `{adjustment}`. */
function isIssuedCapitalWpBagDetail(detail: any): boolean {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return false;
    return Array.isArray(detail.shareholders)
        || Array.isArray(detail.directors)
        || (detail.registeredInfo != null && typeof detail.registeredInfo === 'object')
        || (detail.holdingCompany != null && typeof detail.holdingCompany === 'object');
}

/**
 * 收集 draft 中已有的 Turnover annualInfo 候选（fromPaper / 对端 SI·SE / header lines）。
 * 仅回填已有值，不发明默认 annualInfo，避免误写 account。
 */
function collectTurnoverAnnualInfoCandidates(
    getUnifiedDetail: (code: string) => any,
    fromPaper?: {
        code?: string;
        annualId?: number;
        adjustmentLine?: {details?: any[] | null} | null;
    } | null,
    header?: {annualId?: number; lines?: Array<{code?: string; details?: any[] | null}> | null} | null,
    preferCode?: string,
): any[] {
    const candidates: any[] = [];
    const pushDetail = (detail: any) => {
        if (!detail) return;
        if (Array.isArray(detail)) {
            candidates.push(...detail);
            return;
        }
        candidates.push(detail);
    };

    if (preferCode) {
        pushDetail(getUnifiedDetail(preferCode));
    }

    const fromCode = String(fromPaper?.code || '').trim();
    if (fromCode) {
        pushDetail(getUnifiedDetail(fromCode));
        pushDetail(fromPaper?.adjustmentLine?.details);
    }

    for (const turnoverCode of ['PL-TURN-SI', 'PL-TURN-SE']) {
        pushDetail(getUnifiedDetail(turnoverCode));
    }

    for (const line of header?.lines || []) {
        if (!isTurnoverIncomeFsCode(line?.code)) continue;
        pushDetail(line?.details);
    }

    return candidates;
}

/**
 * 仅当 toPaper 含未手改的 BS-EQ-IC：按发起方当前 adjustment 重算 IC 的
 * entry / adjustmentAmount / details.adjustment（保留 WP bag）。
 * 不触碰其它 toPaper code，不影响通用 recomputeToPaperAdjustments。
 */
function buildIssuedCapitalToPaperSyncPatch(
    state: {
        fromPaper?: {
            code?: string;
            adjustment?: number;
            entry?: string | null;
            companyId?: number;
            annualId?: number;
        } | null;
        toPaper?: ToPaperData | null;
        toPaperUserEditedCodes?: Record<string, boolean>;
        specialDtdPathCodes?: Set<string>;
        draftToPaperAdjustments?: Record<string, number | undefined>;
        componentDetails?: Record<string, {detail?: any}>;
    },
    fromAdjustment: number,
): {
    draftToPaperAdjustments: Record<string, number | undefined>;
    toPaper: ToPaperData;
} | null {
    const fromCode = state.fromPaper?.code;
    const toPaper = state.toPaper;
    if (!fromCode || !toPaper?.codes?.length) return null;

    const icCodes = toPaper.codes
        .map((c) => normalizeLineCode(c))
        .filter((c): c is string => !!c && isIssuedCapitalToPaperCode(c));
    if (icCodes.length === 0) return null;

    const fsAdjustState = useFsAdjustStore.getState();
    const fromLine = fsAdjustState.getLineByCode(fromCode);
    const fromEntry = fromLine?.entry || state.fromPaper?.entry;

    const nextDraft = {...(state.draftToPaperAdjustments || {})};
    const nextDetails = {...(toPaper.details || {})};
    const nextEntries = {...(toPaper.entries || {})};
    const nextAdjLines = {...(toPaper.adjustmentLines || {})};
    let touched = false;

    for (const icCode of icCodes) {
        if (state.toPaperUserEditedCodes?.[icCode]) continue;

        const toLineForEntry = fsAdjustState.getLineByCode(icCode);
        const originalToEntry = toLineForEntry?.entry
            || lookupToPaperMap(toPaper.entries, icCode)
            || null;
        const paired = computeToPaperFieldsForAssembly(
            fromCode,
            fromAdjustment,
            fromEntry,
            icCode,
            lookupToPaperMap(toPaper.entries, icCode) || originalToEntry,
            originalToEntry,
            state.specialDtdPathCodes,
        );

        nextDraft[icCode] = paired.adjustment;
        nextEntries[icCode] = paired.entry;

        const existingDetail = lookupToPaperMap(nextDetails, icCode)
            ?? lookupToPaperMap(state.componentDetails, icCode)?.detail
            ?? null;
        if (existingDetail && typeof existingDetail === 'object' && !Array.isArray(existingDetail)) {
            nextDetails[icCode] = {...existingDetail, adjustment: paired.adjustment};
        }

        const existingLine = lookupToPaperMap(nextAdjLines, icCode);
        const detailForLine = nextDetails[icCode]
            ?? (existingLine?.details
                ? (Array.isArray(existingLine.details) ? existingLine.details[0] : existingLine.details)
                : null);
        nextAdjLines[icCode] = {
            ...(existingLine || {}),
            companyId: existingLine?.companyId ?? state.fromPaper?.companyId ?? 0,
            annualId: existingLine?.annualId ?? state.fromPaper?.annualId ?? 0,
            scheduleCode: icCode,
            fsId: existingLine?.fsId
                ?? toLineForEntry?.fsId
                ?? lookupToPaperMap(toPaper.lineMetas, icCode)?.fsId
                ?? 0,
            currency: existingLine?.currency || 'HKD',
            adjustment: paired.adjustment,
            adjustmentAmount: Math.abs(Number(paired.adjustment) || 0),
            entry: paired.entry,
            details: detailForLine != null ? [detailForLine] : (existingLine?.details || []),
            statement: existingLine?.statement ?? 'BS',
        };
        touched = true;
    }

    if (!touched) return null;
    return {
        draftToPaperAdjustments: nextDraft,
        toPaper: {
            ...toPaper,
            entries: nextEntries,
            details: nextDetails,
            adjustmentLines: nextAdjLines,
        },
    };
}

function isSameLineCode(a: any, b: any): boolean {
    const na = normalizeLineCode(a);
    const nb = normalizeLineCode(b);
    return !!na && na === nb;
}

function toPaperContainsCode(codes: string[] | undefined, code: string): boolean {
    const normalized = normalizeLineCode(code);
    if (!normalized) return false;
    return (codes || []).some((c) => normalizeLineCode(c) === normalized);
}

function lookupToPaperMap<T>(map: Record<string, T> | null | undefined, code: string): T | undefined {
    if (!map) return undefined;
    const normalized = normalizeLineCode(code);
    if (!normalized) return undefined;
    if (map[normalized] !== undefined) return map[normalized];
    const matchedKey = Object.keys(map).find((k) => normalizeLineCode(k) === normalized);
    return matchedKey ? map[matchedKey] : undefined;
}

function isCodeAssembled(assembledCodes: string[] | undefined, code: string): boolean {
    const normalized = normalizeLineCode(code);
    if (!normalized) return false;
    return (assembledCodes || []).some((c) => normalizeLineCode(c) === normalized);
}

function filterAssembledCodes(assembledCodes: string[] | undefined, code: string): string[] {
    const normalized = normalizeLineCode(code);
    if (!normalized) return assembledCodes || [];
    return (assembledCodes || []).filter((c) => normalizeLineCode(c) !== normalized);
}

function getBackendLineCode(line: any): string {
    return normalizeLineCode((line as any)?.code || (line as any)?.scheduleCode);
}

/**
 * 合并 payload 中数组字段的 id 信息
 * 保留正数 id（后端返回的），清除负数或零的 id
 */
function mergePayloadIds(newPayload: any, existingPayload: any): any {
    if (!newPayload || typeof newPayload !== 'object') return newPayload;
    if (!existingPayload || typeof existingPayload !== 'object') return newPayload;

    const merged: any = {...newPayload};

    for (const key of Object.keys(merged)) {
        const newValue = merged[key];
        const existingValue = existingPayload[key];

        // 处理数组字段（如 commonRow, bankRow 等）
        if (Array.isArray(newValue) && Array.isArray(existingValue)) {
            merged[key] = newValue.map((newItem: any, index: number) => {
                if (!newItem || typeof newItem !== 'object') return newItem;

                // 尝试按 id 或 index 匹配 existing item
                let existingItem = existingValue.find((e: any) =>
                    e && e.id && newItem.id && e.id === newItem.id
                );
                if (!existingItem && existingValue[index]) {
                    existingItem = existingValue[index];
                }

                if (!existingItem) return newItem;

                const mergedItem: any = {...newItem};

                // 保留正数 id，清除负数或零的 id
                if ((!newItem.id || newItem.id <= 0) && existingItem.id && existingItem.id > 0) {
                    mergedItem.id = existingItem.id;
                } else if (newItem.id && newItem.id <= 0) {
                    // 负数或零的 id 不保留
                    delete mergedItem.id;
                }

                return mergedItem;
            });
        }
    }

    return merged;
}

interface FromPaperData {
    companyId: number;
    annualId: number;
    code: string;
    adjustmentLine: AuditAdjustmentLine;
    adjustment: number;
    entry?: string | null; // 添加 entry 字段（Dr 或 Cr）
    // 添加 line 元数据，用于从后端数据初始化时保留完整信息
    lineMeta?: {
        name?: string;
        scheduleName?: string;
        fsId?: number;
    };
}

/** Phase 5 §5.7 — WP session period (set by DialogLauncher while WP open) */
export type WpSessionPeriod = {
    spanType: 'SINGLE' | 'MULTI';
    statement: 'BS' | 'PL';
    code: string;
    sliceKey: string | null;
    periods: Array<{
        index: number;
        key: string;
        periodFrom: string;
        periodTo: string;
        yearOfAssessment: string | null;
        label: string;
    }>;
};

/** Phase 5 §5.7 — Drawer period lock for one adjustment session */
export type AdjPeriodLockState = {
    spanType: 'SINGLE' | 'MULTI';
    initiator: {
        statement: 'BS' | 'PL';
        code: string;
        sliceKey: string | null;
    };
    anchorSliceKey: string | null;
    periods: WpSessionPeriod['periods'];
};

interface ToPaperData {
    codes: string[];
    entries?: Record<string, string | null>; // 有效 entry（Dr/Cr），可随后端 line.entry 或用户编辑更新
    /** FS 节点原始 entry，与 FsSelectionDrawer 路径一致；不随后端 line.entry 或 updateToPaperEntry 变化 */
    originalEntries?: Record<string, string | null>;
    adjustmentLines?: Record<string, AuditAdjustmentLine>; // 新增：每个 code 的 adjustmentLine
    details?: Record<string, any>; // 新增：每个 code 的详细数据
    // 添加 line 元数据映射，用于从后端数据初始化时保留完整信息
    lineMetas?: Record<string, {
        name?: string;
        scheduleName?: string;
        fsId?: number;
    }>;
    /**
     * PL-FC-LI 等：line code → 实际拉取 WP 的 code（如 BS-CL-BLS / BS-CL-OFL）
     */
    wpFetchCodes?: Record<string, string>;
}

// ✅ 新增：组件详情数据结构
interface ComponentDetailData {
    code: string;
    detail: any;
    lastUpdated: number;
}

/**
 * Row-level binding for pair-based adjustment (§4.3)
 * Identifies a specific row within a working paper component.
 */
export interface RowBinding {
    rowId?: number;        // Backend row id (positive) or temp id (negative)
    rowIndex?: number;     // Index in the row array
    rowKey?: string;       // Unique key for the row (e.g., item name)
}

/**
 * One endpoint (from or to) in an AdjustmentPair.
 */
export interface PairEndpoint {
    code: string;                    // Schedule code (e.g., 'BS-CA-CASH')
    rowBinding?: RowBinding;         // Which row this endpoint targets
    adjustmentAmount: number;        // The adjustment value for this endpoint
    rowData?: any;                   // Snapshot of the row data
    entry?: string | null;           // Dr or Cr
}

/**
 * An AdjustmentPair links one from-row to one or more to-rows.
 * Each pair represents a single adjustment relationship.
 * See design doc §4.3.
 */
export interface AdjustmentPair {
    id: string;                      // Unique pair id (client-generated)
    from: PairEndpoint;              // The source row
    to: PairEndpoint[];              // One or more target rows
}

let _pairIdCounter = 0;

function generatePairId(): string {
    return `pair_${Date.now()}_${++_pairIdCounter}`;
}

function getPaperContextIds(state: AdjustDraftState): { companyId?: number; annualId?: number } {
    return {
        companyId: state.fromPaper?.companyId ?? state.header?.companyId,
        annualId: state.fromPaper?.annualId ?? state.header?.annualId,
    };
}

function unwrapDetailsPayload(details: unknown): any {
    if (Array.isArray(details) && details.length > 0) {
        return details[0];
    }
    return details;
}

function hasNonEmptyUnifiedDetail(detail: any): boolean {
    if (detail == null) return false;
    if (typeof detail === 'object' && !Array.isArray(detail) && Object.keys(detail).length === 0) {
        return false;
    }
    return true;
}

/** True when fromPaper was opened as line-only (NBV Grand Total / no attr): details intentionally []. */
function isFromPaperIntentionalLineOnly(
    fromPaper: {code?: string; adjustmentLine?: {details?: any} | null} | null | undefined,
): boolean {
    const code = normalizeLineCode(fromPaper?.code);
    if (
        code !== 'BS-NCA-PPE'
        && code !== 'BS-NCA-IA'
        && code !== 'BS-NCA-ITL'
        && !isCommonInvestmentCode(code || '')
    ) {
        return false;
    }
    const raw = fromPaper?.adjustmentLine?.details;
    if (Array.isArray(raw) && raw.length === 0) return true;
    if (raw == null) return false;
    const unwrapped = Array.isArray(raw) ? (raw[0] ?? null) : raw;
    return isAssetLineOnlyAmountSubmit(code, unwrapped);
}

/** fromPaper detail for schedule-level NBV/Amor checks (unified or empty adjustmentLine.details). */
function resolveFromDetailForScheduleContext(
    fromPaper: {code?: string; adjustmentLine?: {details?: any} | null} | null | undefined,
    getUnifiedDetail: (code: string) => any,
): unknown {
    const fromCode = normalizeLineCode(fromPaper?.code);
    if (!fromCode) return null;
    const raw = fromPaper?.adjustmentLine?.details;
    // Line-only fromPaper (NBV Grand Total / no attr): empty details win over Tabs WP hydrate
    if (isFromPaperIntentionalLineOnly(fromPaper)) return null;
    const unified = getUnifiedDetail(fromCode);
    if (hasNonEmptyUnifiedDetail(unified)) return unified;
    if (raw == null) return null;
    if (Array.isArray(raw) && raw.length === 0) return null;
    return Array.isArray(raw) ? (raw[0] ?? null) : raw;
}

/** Schedule-level NBV/Amor opposite leaves: empty details are intentional (line-level adj only). */
function isScheduleLevelEmptyDetailContext(
    fromCode: string | null | undefined,
    fromDetail: unknown,
    toCode: string | null | undefined,
    opts?: {allowBlankAiaToIa?: boolean; allowBlankDepToPpe?: boolean; allowBlankDepToItl?: boolean},
): boolean {
    const to = normalizeLineCode(toCode);
    if (!to) return false;
    const from = normalizeLineCode(fromCode);
    // PPE Grand Total NBV (blank details only) → any toPaper (was DEP-only; ACC/others need line amount)
    if (isPpeScheduleLevelNbvOrDepnContext(fromCode, fromDetail) && from === 'BS-NCA-PPE') return true;
    // IA / ITL / CommonInvestment Total NBV (blank details only) → any toPaper
    if (isIaScheduleLevelNbvOrAmorContext(fromCode, fromDetail)
        && (from === 'BS-NCA-IA' || from === 'BS-NCA-ITL' || isCommonInvestmentCode(from || ''))) {
        return true;
    }
    // Outbound Grand Total / Total NBV pair (kept for DEP counterpart clarity)
    if (isPpeScheduleLevelNbvOrDepnContext(fromCode, fromDetail) && to === 'PL-AE-DEP') return true;
    // Amor chg skip-Drawer: AIA→IA stays line-only even when fromPaper has row amorChgAdj
    if (opts?.allowBlankAiaToIa && from === 'PL-AE-AIA' && to === 'BS-NCA-IA') return true;
    // Depn chg skip-Drawer: DEP→PPE stays line-only even when fromPaper has row depnChgAdj
    if (opts?.allowBlankDepToPpe && from === 'PL-AE-DEP' && to === 'BS-NCA-PPE') return true;
    // Depn chg skip-Drawer (Taxi): DEP→ITL stays line-only (session flag; avoid PPE clash)
    if (opts?.allowBlankDepToItl && from === 'PL-AE-DEP' && to === 'BS-NCA-ITL') return true;
    if (isIaScheduleLevelNbvOrAmorContext(fromCode, fromDetail)) {
        if (from === 'BS-NCA-IA' && to === 'PL-AE-AIA') return true;
        if (from === 'BS-NCA-ITL' && to === 'PL-AE-DEP') return true;
    }
    return false;
}

/**
 * Amor↔NBV (IA+AIA) / ITL↔DEP pair: when one line has abs amount and the sibling is 0,
 * copy the non-zero abs amount onto the empty side (schedule / field assemble safety).
 */
function syncIaAmorNbvPairAdjustmentAmounts(
    getState: () => {header: AuditAdjustHeader | null; upsertLine: (line: AuditAdjustLine) => void},
): void {
    const lines = getState().header?.lines;
    if (!isIaAmorNbvCrossOnlyHeader(lines)) return;

    const findByCode = (code: string) =>
        (lines || []).find(
            (l) => normalizeLineCode(String((l as any)?.code || '')) === code,
        );

    const pairs: Array<[string, string]> = [
        ['BS-NCA-IA', 'PL-AE-AIA'],
        ['BS-NCA-ITL', 'PL-AE-DEP'],
    ];
    for (const [a, b] of pairs) {
        const lineA = findByCode(a);
        const lineB = findByCode(b);
        if (!lineA || !lineB) continue;
        const amtA = Math.abs(Number((lineA as any).adjustmentAmount ?? (lineA as any).adjustment) || 0);
        const amtB = Math.abs(Number((lineB as any).adjustmentAmount ?? (lineB as any).adjustment) || 0);
        if (amtA >= 0.001 && amtB < 0.001) {
            getState().upsertLine({...lineB, adjustmentAmount: amtA} as AuditAdjustLine);
        } else if (amtB >= 0.001 && amtA < 0.001) {
            getState().upsertLine({...lineA, adjustmentAmount: amtB} as AuditAdjustLine);
        }
    }
}

/**
 * REN / loan field → CommonWp（如 BS-CA-PPM）配对：toPaper.adjustmentAmount 须与 from 绝对值相等。
 * MULTI 下 toPaper 空壳/组装失败时常见 amount=0，提交前镜像补齐（不扩大到其它 WP 配对）。
 */
function syncFieldWpCommonPairAdjustmentAmounts(
    getState: () => {
        header: AuditAdjustHeader | null;
        fromPaper: {code?: string} | null;
        toPaper: {codes?: string[]} | null;
        upsertLine: (line: AuditAdjustLine) => void;
    },
): void {
    const state = getState();
    if (!stateInvolvesFieldWpNonDtdSign(state)) return;
    const lines = state.header?.lines;
    if (!Array.isArray(lines) || lines.length === 0) return;

    const fromCode = normalizeLineCode(state.fromPaper?.code);
    if (!fromCode) return;
    const fromLine = lines.find(
        (l) => normalizeLineCode(String((l as any)?.code || '')) === fromCode,
    );
    if (!fromLine) return;
    const fromAmt = Math.abs(
        Number((fromLine as any).adjustmentAmount ?? (fromLine as any).adjustment) || 0,
    );
    if (fromAmt < 0.001) return;

    for (const line of lines) {
        const code = normalizeLineCode(String((line as any)?.code || ''));
        if (!code || code === fromCode) continue;
        if (!toPaperContainsCode(state.toPaper?.codes, code)) continue;
        if (getMainArrayNameForCode(code) !== 'commonRow') continue;
        const amt = Math.abs(
            Number((line as any).adjustmentAmount ?? (line as any).adjustment) || 0,
        );
        if (amt < 0.001) {
            getState().upsertLine({...line, adjustmentAmount: fromAmt} as AuditAdjustLine);
        }
    }
}

/** 组装 Dr/Cr 渲染 / 提交对齐的 header JSON（含 lines） */
function buildDrCrRenderPayload(
    state: AdjustDraftState,
    getUnifiedDetail: (code: string) => any,
): AuditAdjustHeader | null {
    const fromPaper = state.fromPaper;
    const toPaper = state.toPaper;
    if (!fromPaper?.code || !toPaper?.codes?.length) return null;

    const companionHiddenCodes = resolveInvPurchasesCompanionHiddenForUi(
        collectLineCodes(state.header?.lines, [fromPaper.code]),
        fromPaper.code,
        toPaper.codes,
    );
    const fsAdjustState = useFsAdjustStore.getState();
    const lines: AuditAdjustLine[] = [];

    const fromCode = fromPaper.code;
    const fromLineFromStore = fsAdjustState.getLineByCode(fromCode);
    const fromAdjLine = fromPaper.adjustmentLine as any;
    const fromAdjustment = Number(
        fromPaper.adjustment ?? fromAdjLine?.adjustment ?? fromAdjLine?.adjustmentAmount ?? 0,
    );
    const fromDetailRaw = getUnifiedDetail(fromCode);
    // 仅 PL-FC-LI：确保 details 带 bankLoans|vehicleLoans + adjustment（供 inAdj attr）
    const fromDetail = normalizeLineCode(fromCode) === 'PL-FC-LI'
        ? ensurePlFcLiLoanFieldAdjustmentDetail(fromDetailRaw, fromAdjustment)
        : fromDetailRaw;
    const fromBackendEntry = (fromPaper.entry === 'Dr' || fromPaper.entry === 'Cr')
        ? fromPaper.entry
        : null;
    const fromLineEntry = !state.fromPaperUserEdited && fromBackendEntry
        ? fromBackendEntry
        : resolveAdjustLineEntry(fromCode, fromAdjustment, state.fromPaperUserEdited, undefined, state.specialDtdPathCodes);
    const loanAwareSign = (
        stateInvolvesFieldWpNonDtdSign(state)
        || (stateInvolvesItlDepCommonCompanion(state) && hasAlCommonRowDetail(fromDetailRaw))
    )
        && !usesDtdAdjustmentPathScoped(fromCode, state.specialDtdPathCodes);
    const fromLineAmount = !state.fromPaperUserEdited && fromBackendEntry
        ? (loanAwareSign
            ? resolveNonDtdSignedAdjustmentFromRenderEntry(fromBackendEntry, Math.abs(fromAdjustment), fromCode)
            : resolveSignedAdjustmentFromBackendLine(fromBackendEntry, Math.abs(fromAdjustment), fromCode, state.specialDtdPathCodes))
        : (fromPaper.adjustment ?? fromAdjLine?.adjustmentAmount ?? 0);

    // PPE Grand Total Depn / IA Grand Total Amor↔NBV: from stays line-only (no WP invent)
    // NBV / no-attr fromPaper (details intentionally []): ignore Tabs-hydrated WP shell
    const fromCodeNorm = normalizeLineCode(fromCode);
    const fromForceEmptyDetails = (
        (state.ppeDepnChgDirectAssembleActive || isPpeDepScheduleLevelHeader(state.header?.lines))
        && (fromCodeNorm === 'PL-AE-DEP' || fromCodeNorm === 'BS-NCA-PPE')
    ) || (
        (state.iaAmorChgDirectAssembleActive || isIaAiaScheduleLevelHeader(state.header?.lines))
        && (fromCodeNorm === 'BS-NCA-IA' || fromCodeNorm === 'PL-AE-AIA')
    ) || (
        (state.itlDepnChgDirectAssembleActive || isItlDepScheduleLevelHeader(state.header?.lines))
        && (fromCodeNorm === 'PL-AE-DEP' || fromCodeNorm === 'BS-NCA-ITL')
    ) || isFromPaperIntentionalLineOnly(fromPaper);

    lines.push({
        fsId: fromPaper.lineMeta?.fsId ?? fromLineFromStore?.fsId ?? fromAdjLine?.fsId ?? 0,
        code: fromCode,
        currency: fromAdjLine?.currency || 'HKD',
        adjustmentAmount: fromLineAmount,
        entry: fromLineEntry,
        details: fromForceEmptyDetails
            ? []
            : ((fromDetail != null && !isBlankWpDetail(fromDetail)) ? [fromDetail] : []),
    });

    for (const code of toPaper.codes) {
        if (isCompanionHiddenCode(code, companionHiddenCodes)) continue;

        const detailRaw = getUnifiedDetail(code);
        const toLineFromStore = fsAdjustState.getLineByCode(code);
        const toLineMeta = toPaper.lineMetas?.[code];
        const adjLine = toPaper.adjustmentLines?.[code] as any;

        const normalizedToCode = normalizeLineCode(code) || code;
        const pendingAppendAdj = lookupToPaperMap(state.pendingAppendAdjustments, normalizedToCode);
        const draftAdj = lookupToPaperMap(state.draftToPaperAdjustments, normalizedToCode);
        const firstToCode = toPaper.codes?.length
            ? normalizeLineCode(toPaper.codes[0])
            : '';
        const isAdditionalTo = !!firstToCode && normalizedToCode !== firstToCode;
        const hasPendingAppend = pendingAppendAdj !== undefined && Math.abs(Number(pendingAppendAdj)) >= 0.001;
        const toUserEdited = !!state.toPaperUserEditedCodes[normalizedToCode];
        const backendEntry = lookupToPaperMap(toPaper.entries, normalizedToCode)
            ?? adjLine?.entry
            ?? toLineFromStore?.entry;
        const backendEntryDrCr = backendEntry === 'Dr' || backendEntry === 'Cr'
            ? backendEntry
            : null;
        const isBackendHydratedLine = state.draftHydratedFromBackend
            && !hasPendingAppend
            && !toUserEdited
            && (state.staticPositionCodes.has(normalizedToCode) || isDtdClFsCode(normalizedToCode));

        let signedLineAdj: number;
        let lineEntry: 'Dr' | 'Cr' | undefined;

        if (isBackendHydratedLine && backendEntryDrCr) {
            const absAmount = Math.abs(Number(
                draftAdj ?? adjLine?.adjustmentAmount ?? adjLine?.adjustment ?? 0,
            ));
            lineEntry = backendEntryDrCr;
            const toLoanAwareSign = (
                stateInvolvesFieldWpNonDtdSign(state)
                || (stateInvolvesItlDepCommonCompanion(state) && hasAlCommonRowDetail(detailRaw))
            )
                && !usesDtdAdjustmentPathScoped(normalizedToCode, state.specialDtdPathCodes);
            signedLineAdj = toLoanAwareSign
                ? resolveNonDtdSignedAdjustmentFromRenderEntry(backendEntryDrCr, absAmount, normalizedToCode)
                : resolveSignedAdjustmentFromBackendLine(
                    backendEntryDrCr,
                    absAmount,
                    normalizedToCode,
                    state.specialDtdPathCodes,
                );
        } else {
            const authoritativeAppendAdj = isAdditionalTo && hasPendingAppend
                ? pendingAppendAdj
                : undefined;
            signedLineAdj = authoritativeAppendAdj !== undefined
                ? authoritativeAppendAdj
                : (pendingAppendAdj
                    ?? draftAdj
                    ?? adjLine?.adjustment
                    ?? adjLine?.adjustmentAmount
                    ?? 0);
            const resolvedEntry = lookupToPaperMap(toPaper.entries, normalizedToCode);
            lineEntry = resolvedEntry === 'Dr' || resolvedEntry === 'Cr' ? resolvedEntry : undefined;
            // Append：保留 appendToPaperRecord 写入的 entry，勿用默认 Dr 的 FsCategory 重算
            if (!lineEntry
                && isAdditionalTo
                && hasPendingAppend
                && Math.abs(Number(pendingAppendAdj)) >= 0.001) {
                const origEntry = lookupToPaperMap(toPaper.originalEntries, normalizedToCode)
                    ?? resolveFsNodeOriginalEntry(normalizedToCode);
                lineEntry = resolveAppendEffectiveEntry(
                    normalizedToCode,
                    Number(pendingAppendAdj),
                    origEntry,
                );
            }
        }

        const detail = normalizeLineCode(normalizedToCode) === 'PL-FC-LI'
            ? ensurePlFcLiLoanFieldAdjustmentDetail(
                detailRaw,
                Number(signedLineAdj) || 0,
                {wpFetchCode: lookupToPaperMap(toPaper.wpFetchCodes, normalizedToCode)},
            )
            : detailRaw;

        lines.push({
            fsId: toLineMeta?.fsId ?? toLineFromStore?.fsId ?? adjLine?.fsId ?? 0,
            code: normalizedToCode,
            currency: adjLine?.currency || 'HKD',
            adjustmentAmount: signedLineAdj,
            entry: lineEntry,
            details: (
                ((state.ppeDepnChgDirectAssembleActive
                        || isPpeDepScheduleLevelHeader(state.header?.lines))
                    && (normalizedToCode === 'PL-AE-DEP' || normalizedToCode === 'BS-NCA-PPE'))
                || ((state.iaAmorChgDirectAssembleActive
                        || isIaAiaScheduleLevelHeader(state.header?.lines))
                    && (normalizedToCode === 'BS-NCA-IA' || normalizedToCode === 'PL-AE-AIA'))
                || ((state.itlDepnChgDirectAssembleActive
                        || isItlDepScheduleLevelHeader(state.header?.lines))
                    && (normalizedToCode === 'PL-AE-DEP' || normalizedToCode === 'BS-NCA-ITL'))
            )
                ? []
                : ((detail != null && !isBlankWpDetail(detail)) ? [detail] : []),
        });
    }

    if (lines.length < 2) return null;

    const header = state.header;
    return {
        id: header?.id,
        companyId: fromPaper.companyId,
        annualId: fromPaper.annualId,
        adjustmentMode: header?.adjustmentMode ?? state.currentMode ?? AdjustmentMode.ADJUST_WP_TO_WP,
        refNo: header?.refNo ?? '',
        status: header?.status ?? 0,
        preparedUserId: header?.preparedUserId,
        preparedDate: header?.preparedDate,
        reviewedUserId: header?.reviewedUserId ?? 0,
        reviewedDate: header?.reviewedDate ?? '',
        lines,
        remark: state.draftRemark,
    };
}

const TAX_FS_CODES = new Set(['BS-CA-TAXR', 'BS-CL-TAXP', 'PL-TAX']);

function unwrapLineDetailForPayload(details: unknown): any {
    if (details == null) return null;
    if (Array.isArray(details)) {
        if (details.length === 0) return null;
        let d: any = details[0];
        if (d && typeof d === 'object' && !Array.isArray(d) && d.payload && typeof d.payload === 'object') {
            d = d.payload;
        }
        return d;
    }
    if (typeof details === 'object' && (details as any).payload) {
        return (details as any).payload;
    }
    return details;
}

function extractBackendLineIds(lines: any[] | undefined | null): Record<string, { id?: string; headerId?: string }> {
    const backendLineIds: Record<string, { id?: string; headerId?: string }> = {};
    if (!lines) return backendLineIds;
    lines.forEach((line: any) => {
        const code = normalizeLineCode(line.code || line.scheduleCode);
        if (code && (line.id || line.headerId)) {
            backendLineIds[code] = {id: line.id, headerId: line.headerId};
        }
    });
    return backendLineIds;
}

function mergeBackendLineIds(
    lines: AuditAdjustLine[],
    backendLineIds: Record<string, { id?: string; headerId?: string }>,
): AuditAdjustLine[] {
    if (Object.keys(backendLineIds).length === 0) return lines;
    return lines.map((line: any) => {
        const code = normalizeLineCode(line.code || '');
        const backendIds = backendLineIds[code];
        if (backendIds) {
            return {
                ...line,
                id: line.id || backendIds.id,
                headerId: line.headerId || backendIds.headerId,
            };
        }
        return line;
    });
}

/** 当前调整是否涉及 loan field 节点（含 CommonWp→loan / loan→其他 配对） */
function adjustmentInvolvesLoanFieldCode(
    codes: Array<string | null | undefined>,
): boolean {
    return codes.some((c) => isLoanFieldFsCode(c));
}

function stateInvolvesLoanField(state: {
    fromPaper?: {code?: string} | null;
    toPaper?: {codes?: string[]} | null;
}): boolean {
    return adjustmentInvolvesLoanFieldCode([
        state.fromPaper?.code,
        ...(state.toPaper?.codes || []),
    ]);
}

/** 当前调整是否涉及 PL-AE-REN（from 或 to），仅用于该配对两端的非 DTD 符号 */
function stateInvolvesRentalField(state: {
    fromPaper?: {code?: string} | null;
    toPaper?: {codes?: string[]} | null;
    header?: {lines?: Array<{code?: string; scheduleCode?: string} | null>} | null;
}): boolean {
    const codes: Array<string | null | undefined> = [
        state.fromPaper?.code,
        ...(state.toPaper?.codes || []),
        ...(state.header?.lines || []).map((l) => l?.code || l?.scheduleCode),
    ];
    return codes.some((c) => normalizeLineCode(c) === 'PL-AE-REN');
}

/** loan 或 REN 配对：buildDrCr / hydrate 两端走非 DTD FS-aware 符号（不扩大到其它 WP） */
function stateInvolvesFieldWpNonDtdSign(state: {
    fromPaper?: {code?: string} | null;
    toPaper?: {codes?: string[]} | null;
    header?: {lines?: Array<{code?: string; scheduleCode?: string; details?: unknown} | null>} | null;
}): boolean {
    // PPE FS codes are shared with normal PPE WP — do not enable via code alone.
    // PPE field WP→WP FS-aware signing is gated in hydrateDraftFromHeader via detail evidence.
    return stateInvolvesLoanField(state) || stateInvolvesRentalField(state);
}

/**
 * CommonWp ↔ Taxi PL-AE-DEP (iaField:amorChgAdj / ITL nest) only.
 * Does NOT match BS-NCA-IA / PL-AE-AIA / BS-NCA-ITL slim field pairs or other IA headers.
 */
function stateInvolvesItlDepCommonCompanion(state: {
    header?: {lines?: AuditAdjustLine[] | null} | null;
    toPaper?: {wpFetchCodes?: Record<string, string> | null} | null;
}): boolean {
    const lines = state.header?.lines;
    if (Array.isArray(lines) && lines.some((l) => {
        const code = normalizeLineCode((l as any)?.code || (l as any)?.scheduleCode);
        const raw = (l as any)?.details;
        const detail = Array.isArray(raw) ? raw[0] : raw;
        if (isItlDepIaFieldCode(code, detail)) return true;
        // loadFromHeader may already slim DEP; wpFetchCode=BS-NCA-ITL still marks Taxi path
        if (code === 'PL-AE-DEP') {
            const fetch = lookupToPaperMap(state.toPaper?.wpFetchCodes, code);
            if (String(fetch || '').trim().toUpperCase() === 'BS-NCA-ITL') return true;
        }
        return false;
    })) {
        return true;
    }
    return Object.entries(state.toPaper?.wpFetchCodes || {}).some(([code, fetch]) =>
        normalizeLineCode(code) === 'PL-AE-DEP'
        && String(fetch || '').trim().toUpperCase() === 'BS-NCA-ITL'
    );
}

/** Header lines include Taxi DEP field-adj evidence (for hydrate before toPaper wpFetch is set). */
function headerHasItlDepIaFieldLine(
    lines: AuditAdjustLine[] | null | undefined,
): boolean {
    if (!Array.isArray(lines) || lines.length === 0) return false;
    return lines.some((l) => {
        const code = normalizeLineCode((l as any)?.code || (l as any)?.scheduleCode);
        const raw = (l as any)?.details;
        const detail = Array.isArray(raw) ? raw[0] : raw;
        return isItlDepIaFieldCode(code, detail);
    });
}

/** 从后端 header.lines 解析草稿字段（loadFromHeader 使用） */
function hydrateDraftFromHeader(
    headerData: AuditAdjustHeader,
    ctx: {
        fromCode: string;
        visibleToCodes: string[];
    },
): {
    backendLineIds: Record<string, { id?: string; headerId?: string }>;
    draftSimpleAdjustments: Record<string, number>;
    draftToPaperAdjustments: Record<string, number | undefined>;
    staticPositionCodes: Set<string>;
    specialDtdPathCodes: Set<string>;
} {
    const fromCodeUpper = String(ctx.fromCode || '').trim().toUpperCase();
    const visibleToSet = new Set(
        ctx.visibleToCodes.map((c) => String(c || '').trim().toUpperCase()).filter(Boolean),
    );

    const backendLineIds = extractBackendLineIds(headerData.lines);
    const draftSimpleAdjustments: Record<string, number> = {};
    const draftToPaperAdjustments: Record<string, number | undefined> = {};
    const staticPositionCodes = new Set<string>();
    const specialDtdPathCodes = new Set<string>();

    // 仅当本 header 含 loan field 节点时，对全部 line 使用 FS-aware 符号（修复 OP↔BLS 等配对）
    // 含 PL-AE-REN 的 header 同样对两端使用 FS-aware（与 loan 配对一致；不影响无 REN 的其它调整）
    // PPE：仅当 line.details 带 field-adj 证据时启用（BS-NCA-PPE/PL-AE-DEP 与普通 PPE WP 共用 code）
    // 常规非 loan/REN/PPE-field 调整保持原 resolveSignedAdjustmentFromBackendLine
    const loanPairedHeader = adjustmentInvolvesLoanFieldCode([
        fromCodeUpper,
        ...ctx.visibleToCodes,
        ...(headerData.lines || []).map((l) =>
            normalizeLineCode((l as any)?.code || (l as any)?.scheduleCode),
        ),
    ]);
    const rentalPairedHeader = [fromCodeUpper, ...ctx.visibleToCodes, ...(headerData.lines || []).map((l) =>
        normalizeLineCode((l as any)?.code || (l as any)?.scheduleCode),
    )].some((c) => c === 'PL-AE-REN');
    const ppeFieldPairedHeader = (headerData.lines || []).some((l) => {
            const code = normalizeLineCode((l as any)?.code || (l as any)?.scheduleCode);
            const raw = (l as any)?.details;
            const detail = Array.isArray(raw) ? raw[0] : raw;
            if (isPpeFieldSyntheticDetail(detail, code)) return true;
            // Flat attr GET shape before transformFlat
            const rc = String((detail as any)?.rowClass || '').toLowerCase();
            return isPpeFieldFsCode(code) && rc.startsWith('ppefield:');
        });
    const iaFieldPairedHeader = isIaFieldAdjHeader(headerData.lines)
        || (headerData.lines || []).some((l) => {
            const code = normalizeLineCode((l as any)?.code || (l as any)?.scheduleCode);
            const raw = (l as any)?.details;
            const detail = Array.isArray(raw) ? raw[0] : raw;
            // Flat attr GET, or Taxi nest with amorChgAdjs / nbvCfAdjs (before slim)
            return isItlDepIaFieldCode(code, detail)
                || (
                    String((detail as any)?.rowClass || '').toLowerCase().startsWith('iafield:')
                    && (isIaFieldFsCode(code) || code === 'PL-AE-DEP')
                );
        });
    // Do NOT fold IA into fieldWpPairedHeader — that would force FS-aware sign on
    // every line in the header (e.g. PL-OI-DI). IA uses per-code gates below.
    const fieldWpPairedHeader = loanPairedHeader || rentalPairedHeader || ppeFieldPairedHeader;
    // Taxi DEP field only — CommonWp companion non-DTD (not IA/AIA headers)
    const itlDepFieldHeader = headerHasItlDepIaFieldLine(headerData.lines);

    for (const line of headerData.lines || []) {
        const codeUpper = normalizeLineCode((line as any)?.code || (line as any)?.scheduleCode);
        if (!codeUpper) continue;

        // TAXR/TAXP：按节点 code 注册 scoped special（不依赖 item 文案匹配，与 TaxWp 组装对称）
        if (isTaxBfSpecialDtdCode(codeUpper) || isTaxWpBfSpecialAdjustmentLine(codeUpper, (line as any).details)) {
            specialDtdPathCodes.add(codeUpper);
        }

        const amount = Number((line as any).adjustmentAmount) || 0;
        const backendEntry = (line as any).entry as string | null | undefined;
        const rawLineDetail = Array.isArray((line as any)?.details)
            ? (line as any).details[0]
            : (line as any)?.details;
        const isIaFieldLine = iaFieldPairedHeader && (
            isIaFieldFsCode(codeUpper)
            || isItlDepIaFieldCode(codeUpper, rawLineDetail)
        );
        // 仅 CommonWp ↔ Taxi DEP：配对端非 DTD FS-aware + 解锁动态分列
        const isItlDepCommonCompanion = itlDepFieldHeader && hasAlCommonRowDetail(rawLineDetail);
        const useFsAwareNonDtdSign = (fieldWpPairedHeader || isIaFieldLine || isItlDepCommonCompanion)
            && !usesDtdAdjustmentPathScoped(codeUpper, specialDtdPathCodes);
        const signedAmount = useFsAwareNonDtdSign
            ? resolveNonDtdSignedAdjustmentFromRenderEntry(backendEntry, amount, codeUpper)
            : resolveSignedAdjustmentFromBackendLine(
                backendEntry,
                amount,
                codeUpper,
                specialDtdPathCodes,
            );
        // DTD / scoped DTD 节点走动态分列（与新建 adjustment 一致），不锁定 staticPositionCodes
        // Depn↔NBV field pair only: also dynamic non-DTD（按符号切列），与新建 WP→WP 一致
        // IA field：仅解锁 IA/AIA/ITL/Taxi-DEP 本节点；Taxi DEP 的 CommonWp 配对端一并解锁
        if (
            !usesDtdAdjustmentPathScoped(codeUpper, specialDtdPathCodes)
            && !isPpeDepnNbvCrossOnlyFieldHeader(headerData.lines)
            && !isIaFieldLine
            && !isItlDepCommonCompanion
        ) {
            staticPositionCodes.add(codeUpper);
        }

        if (codeUpper === fromCodeUpper) {
            draftSimpleAdjustments[ctx.fromCode] = signedAmount;
            continue;
        }

        if (visibleToSet.has(codeUpper)) {
            const matchedCode = ctx.visibleToCodes.find(
                (c) => String(c || '').trim().toUpperCase() === codeUpper,
            ) || codeUpper;
            draftToPaperAdjustments[matchedCode] = signedAmount;
            continue;
        }

        draftSimpleAdjustments[codeUpper] = signedAmount;
    }

    return {
        backendLineIds,
        draftSimpleAdjustments,
        draftToPaperAdjustments,
        staticPositionCodes,
        specialDtdPathCodes,
    };
}

/** 将配对 adjustment 写入 detail 目标行并规范化符号 */
function applyPairedAdjustmentToDetail(
    detail: any,
    pairedAdjustment: number,
    originalEntry: string,
    pairedEntry: 'Dr' | 'Cr',
    options?: {targetRowIndex?: number; toPaperCode?: string},
): any {
    if (!detail || typeof detail !== 'object') {
        return detail;
    }

    const cloned: Record<string, any> = {...detail};
    let rowUpdated = false;

    const taxDefaultIndex = options?.toPaperCode
        ? resolveTaxWpToPaperDefaultLogicalIndex(options.toPaperCode)
        : null;

    // Prefer explicit index → rowSelection 已写入的非零行 → Tax 节点默认 b/f/expenses
    let preferredIndex = options?.targetRowIndex;
    if (
        preferredIndex == null
        && isTaxWpFsCode(options?.toPaperCode)
        && Array.isArray(cloned.commonRow)
    ) {
        const existingIdx = (cloned.commonRow as any[]).findIndex(
            (row) => row && typeof row === 'object' && Math.abs(Number(row.adjustment) || 0) >= 0.001,
        );
        if (existingIdx >= 0) {
            preferredIndex = existingIdx;
        }
    }
    if (preferredIndex == null && taxDefaultIndex != null) {
        preferredIndex = taxDefaultIndex;
    }

    for (const [key, value] of Object.entries(cloned)) {
        if (WP_METADATA_ONLY_KEYS.has(key)) continue;
        if (!Array.isArray(value) || value.length === 0) continue;
        const first = value[0];
        if (!first || typeof first !== 'object') continue;
        if (!('adjustment' in first) && !('perClient' in first)) continue;

        if (
            preferredIndex != null
            && preferredIndex >= 0
            && preferredIndex < value.length
            && (key === 'commonRow' || isTaxWpFsCode(options?.toPaperCode))
        ) {
            cloned[key] = value.map((row: any, idx: number) => {
                if (!row || typeof row !== 'object') return row;
                if (idx === preferredIndex) {
                    return {...row, adjustment: pairedAdjustment};
                }
                return {...row, adjustment: Number(row.adjustment) || 0};
            });
            rowUpdated = true;
            break;
        }

        cloned[key] = [{...first, adjustment: pairedAdjustment}, ...value.slice(1)];
        rowUpdated = true;
        break;
    }

    if (!rowUpdated && Math.abs(pairedAdjustment) >= 0.001) {
        const origForNormalize = isTaxBfSpecialDtdCode(options?.toPaperCode) ? 'Dr' : originalEntry;
        const toCode = options?.toPaperCode ? normalizeLineCode(options.toPaperCode) : '';
        // CommonWp 族（含 BS-CA-PPM）：空 commonRow 时 invent 一行，勿退成 flat equity 形态
        if (toCode && getMainArrayNameForCode(toCode) === 'commonRow') {
            const fsLine = useFsAdjustStore.getState().getLineByCode(toCode);
            const invented = createInitialDetailForCode(toCode, fsLine, pairedAdjustment);
            return normalizeDetailForSubmit(invented, origForNormalize, pairedEntry, toCode).detail;
        }
        return normalizeDetailForSubmit(
            {perClient: 0, adjustment: pairedAdjustment},
            origForNormalize,
            pairedEntry,
        ).detail;
    }

    // BS-CL-TAXP / BS-CA-TAXR toPaper：按 Dr-side 规范化符号
    const origForNormalize = isTaxBfSpecialDtdCode(options?.toPaperCode) ? 'Dr' : originalEntry;
    return normalizeDetailForSubmit(cloned, origForNormalize, pairedEntry).detail;
}

/** 提交前规范化 getDrCrRenderPayload 中的单行 */
function normalizeSubmitPayloadLine(
    line: AuditAdjustLine,
    state: AdjustDraftState,
): { detail: any; entry: string } {
    const code = normalizeLineCode(line.code || '');
    const fromCode = state.fromPaper?.code || '';
    const fsLine = useFsAdjustStore.getState().getLineByCode(code);
    const originalEntry = resolveFsNodeOriginalEntry(code) || fsLine?.entry || 'Dr';
    const renderPos = line.entry === 'Dr' || line.entry === 'Cr' ? line.entry : undefined;
    let detail = unwrapLineDetailForPayload(line.details);
    let entry = line.entry || originalEntry;

    if (isFlatEquityDetailForCode(code, detail) && detail) {
        const fallbackAdj = code === fromCode
            ? Number(lookupToPaperMap(state.draftSimpleAdjustments, code) ?? state.fromPaper?.adjustment ?? 0)
            : Number(lookupToPaperMap(state.draftSimpleAdjustments, code) ?? lookupToPaperMap(state.draftToPaperAdjustments, code) ?? 0);
        // DTD → BS-EQ-AL flat：保留 specialAdjustFlow 配对的 render 列，勿仅靠 adjustment 符号重算 entry
        let flatRenderPos: 'Dr' | 'Cr' | undefined = undefined;
        if (code === 'BS-EQ-AL' && isDtdClFsCode(fromCode)
            && toPaperContainsCode(state.toPaper?.codes, code)) {
            flatRenderPos = renderPos;
            if (!flatRenderPos) {
                const fromAdj = Number(
                    state.fromPaper?.adjustment
                    ?? lookupToPaperMap(state.draftSimpleAdjustments, fromCode)
                    ?? 0,
                );
                flatRenderPos = resolveToPaperFieldsFromPairing(fromCode, fromAdj, code, {
                    specialDtdPathCodes: state.specialDtdPathCodes,
                }).entry;
            }
        }
        const normalized = normalizeDetailForSubmit(
            {
                perClient: Number(detail?.perClient) || 0,
                adjustment: resolveFlatEquityAdjustment(detail, fallbackAdj),
            },
            originalEntry,
            flatRenderPos ?? renderPos,
        );
        return {detail: normalized.detail, entry: normalized.effectiveEntry};
    }

    // BS-EQ-IC + WP bag：非 DTD — 只同步 details.adjustment，保留 shareholders/directors/…
    // toPaper 组装后的 `{adjustment}`（无 bag）：同样只同步 adjustment + entry
    // fromPaper 原 NO_WP 空 details：不进此分支
    if (
        code === 'BS-EQ-IC'
        && detail
        && typeof detail === 'object'
        && !Array.isArray(detail)
        && (
            isIssuedCapitalWpBagDetail(detail)
            || (
                toPaperContainsCode(state.toPaper?.codes, code)
                && Object.prototype.hasOwnProperty.call(detail, 'adjustment')
            )
        )
    ) {
        const signedAdj = Number(
            (isSameLineCode(fromCode, code)
                ? (state.fromPaper?.adjustment
                    ?? lookupToPaperMap(state.draftSimpleAdjustments, code))
                : (lookupToPaperMap(state.draftToPaperAdjustments, code)
                    ?? lookupToPaperMap(state.draftSimpleAdjustments, code)))
            ?? detail?.adjustment
            ?? line.adjustmentAmount
            ?? 0,
        );
        const fromEntry = state.fromPaper?.entry === 'Dr' || state.fromPaper?.entry === 'Cr'
            ? state.fromPaper.entry
            : null;
        const toEntry = coerceDrCrEntry(lookupToPaperMap(state.toPaper?.entries, code));
        const icRenderPos = renderPos
            ?? (isSameLineCode(fromCode, code) ? fromEntry : toEntry)
            ?? undefined;
        return {
            detail: {...detail, adjustment: signedAdj},
            entry: icRenderPos ?? entry,
        };
    }

    const isDtdFromToPaper = isDtdClFsCode(fromCode)
        && isDtdClFsCode(code)
        && toPaperContainsCode(state.toPaper?.codes, code);
    if (isDtdFromToPaper && detail) {
        const normalized = normalizeDetailForSubmit(detail, originalEntry, renderPos);
        return {detail: normalized.detail, entry: normalized.effectiveEntry};
    }

    if (TAX_FS_CODES.has(code) && fromCode && !isSameLineCode(fromCode, code)
        && toPaperContainsCode(state.toPaper?.codes, code)) {
        // Tabs 已编辑 TAXR/TAXP/PL-TAX：保留用户金额与目标行，按 refundable 同款 Dr-side 组装
        const toUserEdited = !!state.toPaperUserEditedCodes?.[code];
        if (toUserEdited) {
            const signedAdj = Number(
                lookupToPaperMap(state.draftToPaperAdjustments, code)
                ?? line.adjustmentAmount
                ?? 0,
            );
            const lineEntry = (renderPos === 'Dr' || renderPos === 'Cr')
                ? renderPos
                : (coerceDrCrEntry(lookupToPaperMap(state.toPaper?.entries, code)) ?? 'Cr');
            if (isTaxBfSpecialDtdCode(code)) {
                const aligned = alignTaxPayableBfToPaperAdjustment({
                    entry: lineEntry,
                    adjustment: signedAdj,
                });
                if (detail) {
                    const syncedDetail = applyPairedAdjustmentToDetail(
                        detail,
                        aligned.adjustment,
                        'Dr',
                        aligned.entry,
                        {toPaperCode: code},
                    );
                    return {detail: syncedDetail, entry: aligned.entry};
                }
                return {detail, entry: aligned.entry};
            }
            if (detail) {
                const syncedDetail = applyPairedAdjustmentToDetail(
                    detail,
                    signedAdj,
                    originalEntry,
                    lineEntry,
                    {toPaperCode: code},
                );
                return {detail: syncedDetail, entry: lineEntry};
            }
            return {detail, entry: lineEntry};
        }

        const fromAdj = Number(
            lookupToPaperMap(state.draftSimpleAdjustments, fromCode)
            ?? state.fromPaper?.adjustment
            ?? 0,
        );
        const paired = resolveToPaperFieldsFromPairing(fromCode, fromAdj, code, {
            specialDtdPathCodes: state.specialDtdPathCodes,
        });
        if (detail) {
            const syncedDetail = applyPairedAdjustmentToDetail(
                detail,
                paired.adjustment,
                isTaxBfSpecialDtdCode(code) ? 'Dr' : originalEntry,
                paired.entry,
                {toPaperCode: code},
            );
            const outDetail = usesDtdAdjustmentPathScoped(fromCode, state.specialDtdPathCodes)
                ? sanitizeSpecialAdjustFlowToPaperDetail(syncedDetail)
                : syncedDetail;
            return {detail: outDetail, entry: paired.entry};
        }
        return {detail, entry: paired.entry};
    }

    // Bank/Vehicle loan / PL-FC-LI：须在通用 toPaper 分支之前处理（否则 bankLoans 走不到 top-level adjustment 同步）
    if (isLoanFieldSyntheticDetail(detail, code)) {
        const signedAdj = Number(
            (isSameLineCode(fromCode, code)
                ? (state.fromPaper?.adjustment
                    ?? lookupToPaperMap(state.draftSimpleAdjustments, code))
                : (lookupToPaperMap(state.draftToPaperAdjustments, code)
                    ?? lookupToPaperMap(state.draftSimpleAdjustments, code)))
            ?? detail.adjustment
            ?? line.adjustmentAmount
            ?? 0,
        );
        const fromEntry = state.fromPaper?.entry === 'Dr' || state.fromPaper?.entry === 'Cr'
            ? state.fromPaper.entry
            : null;
        const toEntry = coerceDrCrEntry(lookupToPaperMap(state.toPaper?.entries, code));
        const loanRenderPos = renderPos
            ?? (isSameLineCode(fromCode, code) ? fromEntry : toEntry)
            ?? undefined;
        // 仅 PL-FC-LI：确保 details 为 bankLoans|vehicleLoans + top-level adjustment（inAdj）
        const loanDetail = normalizeLineCode(code) === 'PL-FC-LI'
            ? ensurePlFcLiLoanFieldAdjustmentDetail(detail, signedAdj, {
                wpFetchCode: lookupToPaperMap(state.toPaper?.wpFetchCodes, code),
            })
            : detail;
        const detailWithSigned = {...loanDetail, adjustment: signedAdj};
        const normalized = normalizeDetailForSubmit(detailWithSigned, originalEntry, loanRenderPos, code);
        return {
            detail: normalized.detail,
            entry: loanRenderPos ?? normalized.effectiveEntry,
        };
    }

    // PL-AE-REN field WP→WP only：同步金额符号，保留 rentalMonthAdjustments 等 rowClass marker maps
    if (isRentalFieldSyntheticDetail(detail, code)) {
        const signedAdj = Number(
            (isSameLineCode(fromCode, code)
                ? (state.fromPaper?.adjustment
                    ?? lookupToPaperMap(state.draftSimpleAdjustments, code))
                : (lookupToPaperMap(state.draftToPaperAdjustments, code)
                    ?? lookupToPaperMap(state.draftSimpleAdjustments, code)))
            ?? detail.adjustment
            ?? line.adjustmentAmount
            ?? 0,
        );
        const fromEntry = state.fromPaper?.entry === 'Dr' || state.fromPaper?.entry === 'Cr'
            ? state.fromPaper.entry
            : null;
        const toEntry = coerceDrCrEntry(lookupToPaperMap(state.toPaper?.entries, code));
        const rentalRenderPos = renderPos
            ?? (isSameLineCode(fromCode, code) ? fromEntry : toEntry)
            ?? undefined;
        // Rescale marker maps to signedAdj only when map/agg out of sync (REN field WP→WP)
        const detailWithMaps = rentalFieldAttrMapsOutOfSync(detail, signedAdj)
            ? rescaleRentalFieldAttrMapsForAmount(detail, signedAdj)
            : {...detail, adjustment: signedAdj};
        const normalized = normalizeDetailForSubmit(detailWithMaps, originalEntry, rentalRenderPos, code);
        return {
            detail: normalized.detail,
            entry: rentalRenderPos ?? normalized.effectiveEntry,
        };
    }

    // PPE field WP→WP：draft/shared 保持顶层 markers；仅最终提交再 nest 到 xxxRow
    if (isPpeFieldSyntheticDetail(detail, code)) {
        const nestedScalars = collectNestedPpeFieldAdjScalars(detail);
        let nestedFieldSum = 0;
        for (const k of PPE_FIELD_ADJ_KEYS) {
            const fromRow = Number(nestedScalars.fieldSums[k]) || 0;
            const fromTop = Number(detail?.[k]) || 0;
            nestedFieldSum += Math.abs(fromRow) >= 0.001 ? fromRow : fromTop;
        }
        const signedAdj = Number(
            (isSameLineCode(fromCode, code)
                ? (state.fromPaper?.adjustment
                    ?? lookupToPaperMap(state.draftSimpleAdjustments, code))
                : (lookupToPaperMap(state.draftToPaperAdjustments, code)
                    ?? lookupToPaperMap(state.draftSimpleAdjustments, code)))
            ?? detail.adjustment
            ?? (Math.abs(nestedFieldSum) >= 0.001 ? nestedFieldSum : undefined)
            ?? line.adjustmentAmount
            ?? 0,
        );
        const fromEntry = state.fromPaper?.entry === 'Dr' || state.fromPaper?.entry === 'Cr'
            ? state.fromPaper.entry
            : null;
        const toEntry = coerceDrCrEntry(lookupToPaperMap(state.toPaper?.entries, code));
        const ppeRenderPos = renderPos
            ?? (isSameLineCode(fromCode, code) ? fromEntry : toEntry)
            ?? undefined;
        // Hoist/rescale to top-level first so normalizeDetailForSubmit keeps field markers
        // (must NOT nest before normalize — nested has no detail.adjustment → rescale(0) wipes nbvCfAdj)
        const draftShaped = rescalePpeFieldAdjKeysForAmount(detail, signedAdj);
        const normalized = normalizeDetailForSubmit(draftShaped, originalEntry, ppeRenderPos, code);
        const finalSigned = Number(normalized.detail?.adjustment);
        const amountForNest = Number.isFinite(finalSigned) && Math.abs(finalSigned) >= 0.001
            ? finalSigned
            : signedAdj;
        const finalDetail = toPpeFieldAdjSubmitDetail(normalized.detail, amountForNest, code);
        delete finalDetail[PPE_NBV_SPLIT_FLAG];
        return {
            detail: finalDetail,
            entry: ppeRenderPos ?? normalized.effectiveEntry,
        };
    }

    // IA field WP→WP：提交 slim {intangibleRowId, xxxAdj}，只更新 entry / amount / attr
    // PL-AE-DEP Taxi nest：同样提交 slim（移除 adjustments，保留 adjustment / amorChgAdj）
    if (isIaFieldSyntheticDetail(detail, code) || isItlDepIaFieldTabsNonDtdDetail(detail, code)) {
        const signedAdj = Number(
            (isSameLineCode(fromCode, code)
                ? (state.fromPaper?.adjustment
                    ?? lookupToPaperMap(state.draftSimpleAdjustments, code))
                : (lookupToPaperMap(state.draftToPaperAdjustments, code)
                    ?? lookupToPaperMap(state.draftSimpleAdjustments, code)))
            ?? detail.adjustment
            ?? detail.nbvCfAdj
            ?? detail.amorChgAdj
            ?? line.adjustmentAmount
            ?? 0,
        );
        const fromEntry = state.fromPaper?.entry === 'Dr' || state.fromPaper?.entry === 'Cr'
            ? state.fromPaper.entry
            : null;
        const toEntry = coerceDrCrEntry(lookupToPaperMap(state.toPaper?.entries, code));
        const iaRenderPos = renderPos
            ?? (isSameLineCode(fromCode, code) ? fromEntry : toEntry)
            ?? undefined;
        const draftShaped = rescaleIaFieldAdjKeysForAmount(detail, signedAdj, code);
        const normalized = normalizeDetailForSubmit(draftShaped, originalEntry, iaRenderPos, code);
        const finalSigned = Number(normalized.detail?.adjustment);
        const amountForSlim = Number.isFinite(finalSigned) && Math.abs(finalSigned) >= 0.001
            ? finalSigned
            : signedAdj;
        return {
            detail: toIaFieldAdjSubmitDetail(normalized.detail, amountForSlim, code),
            entry: iaRenderPos ?? normalized.effectiveEntry,
        };
    }

    const isToPaperLine = fromCode && !isSameLineCode(fromCode, code)
        && toPaperContainsCode(state.toPaper?.codes, code);

    // REN/loan → CommonWp toPaper（如 BS-CA-PPM）：非 DTD 配对写入 commonRow（对齐 SINGLE）
    // 须在通用 isToPaperLine 分支之前，否则空壳/零金额 commonRow 无法补齐
    if (
        isToPaperLine
        && getMainArrayNameForCode(code) === 'commonRow'
        && !usesDtdAdjustmentPathScoped(code, state.specialDtdPathCodes)
        && !usesDtdAdjustmentPathScoped(fromCode, state.specialDtdPathCodes)
        && (stateInvolvesFieldWpNonDtdSign(state) || stateInvolvesItlDepCommonCompanion(state))
        && !state.toPaperUserEditedCodes?.[code]
    ) {
        const fromAdj = Number(
            state.fromPaper?.adjustment
            ?? lookupToPaperMap(state.draftSimpleAdjustments, fromCode)
            ?? 0,
        );
        const paired = resolveToPaperFieldsFromPairing(fromCode, fromAdj, code, {
            specialDtdPathCodes: state.specialDtdPathCodes,
        });
        const draftAdj = lookupToPaperMap(state.draftToPaperAdjustments, code);
        const pairedAdj = (draftAdj !== undefined && Math.abs(Number(draftAdj)) >= 0.001)
            ? Number(draftAdj)
            : paired.adjustment;
        const pairedEntry = (renderPos === 'Dr' || renderPos === 'Cr')
            ? renderPos
            : paired.entry;

        let workingDetail = detail;
        workingDetail = applyPairedAdjustmentToDetail(
            workingDetail && typeof workingDetail === 'object' ? workingDetail : {},
            pairedAdj,
            originalEntry,
            pairedEntry,
            {toPaperCode: code},
        );
        const normalized = normalizeDetailForSubmit(
            workingDetail,
            originalEntry,
            pairedEntry,
            code,
        );
        return {detail: normalized.detail, entry: pairedEntry};
    }

    if (isToPaperLine && detail) {
        const normalized = normalizeDetailForSubmit(detail, originalEntry, renderPos, code);
        // specialAdjustFlow：toPaper 调整只落在 details.*.adjustment，不带 finalAmount
        const outDetail = usesDtdAdjustmentPathScoped(fromCode, state.specialDtdPathCodes)
            ? sanitizeSpecialAdjustFlowToPaperDetail(normalized.detail)
            : normalized.detail;
        return {detail: outDetail, entry: normalized.effectiveEntry};
    }

    // CommonWp↔loan / CommonWp↔REN / CommonWp↔Taxi DEP 配对时：按渲染列同步 commonRow entry/adjustment（非 DTD）
    // 不扩大到全部 CommonWp 或 IA/AIA 配对，避免影响常规 OP↔ORC / PL-OI-DI 等调整
    if (
        hasAlCommonRowDetail(detail)
        && renderPos
        && !usesDtdAdjustmentPathScoped(code, state.specialDtdPathCodes)
        && (stateInvolvesFieldWpNonDtdSign(state) || stateInvolvesItlDepCommonCompanion(state))
    ) {
        const normalized = normalizeDetailForSubmit(detail, originalEntry, renderPos, code);
        return {
            detail: normalized.detail,
            entry: renderPos,
        };
    }

    // BS-EQ-AL / BS-CL-RND 发起（commonRow）：提交时按渲染列规范化
    if (isSameLineCode(fromCode, code) && isCommonRowSyntheticEditDetail(code, detail)) {
        const normalized = normalizeDetailForSubmit(detail, originalEntry, renderPos, code);
        return {
            detail: normalized.detail,
            entry: renderPos ?? normalized.effectiveEntry,
        };
    }

    const isFromDtdLine = isSameLineCode(fromCode, code) && isDtdClFsCode(code);
    if (isFromDtdLine && !state.fromPaperUserEdited) {
        const signedAdj = Number(
            state.fromPaper?.adjustment
            ?? lookupToPaperMap(state.draftSimpleAdjustments, code)
            ?? line.adjustmentAmount
            ?? 0,
        );
        return {detail, entry: resolveDueFromToDirectorsFromEntry(signedAdj)};
    }
    if (isFromDtdLine && state.fromPaperUserEdited) {
        const signedAdj = Number(
            state.fromPaper?.adjustment
            ?? lookupToPaperMap(state.draftSimpleAdjustments, code)
            ?? line.adjustmentAmount
            ?? 0,
        );
        // 仅当 Tabs 编辑后的 payload 已带 line.entry（renderPos）时采信，避免
        // resolveEntryFromFsCategory（非 DTD）把 DTD Cr 列冲成 Dr。
        // 无 renderPos 时保持原逻辑，不扩大影响面。
        if (renderPos) {
            return {detail, entry: renderPos};
        }
        return {detail, entry: resolveEntryFromFsCategory(code, signedAdj)};
    }

    // 仅 BS-CL-TAXP（Tax payable b/f）special：details 已 flip，采信 payload entry
    // 不改 TAXR / 其它节点原有 computeEffectiveEntry 路径
    if (
        isSameLineCode(fromCode, code)
        && isTaxPayableBfDtdCode(code)
        && usesDtdAdjustmentPathScoped(code, state.specialDtdPathCodes)
        && renderPos
    ) {
        return {detail, entry: renderPos};
    }

    // 非 DTD fromPaper 用户已在 Tabs 编辑：按渲染列同步 entry / details.adjustment
    // 仅 fromPaperUserEdited 时生效，避免影响未编辑回显 / 其它组件原有 computeEffectiveEntry 路径
    // （覆盖原 BS-EQ-IC 特例，并修复 BS-CL-DTS 等 CommonWp：Dr 列编辑后被 computeEffectiveEntry 冲回 Cr）
    if (
        isSameLineCode(fromCode, code)
        && state.fromPaperUserEdited
        && detail
        && renderPos
        && !usesDtdAdjustmentPathScoped(code, state.specialDtdPathCodes)
        && !state.staticPositionCodes.has(code)
    ) {
        const normalized = normalizeDetailForSubmit(detail, originalEntry, renderPos, code);
        return {
            detail: normalized.detail,
            entry: renderPos,
        };
    }

    if (detail && !state.staticPositionCodes.has(code)) {
        entry = computeEffectiveEntry(detail, originalEntry);
    }

    return {detail, entry};
}

interface AdjustDraftState {
    fromPaper: FromPaperData | null;
    toPaper: ToPaperData | null;
    adjustTabsDialogOpen: boolean;

    // ✅ 新增：支持多个 fromPaper (用于 SalariesAllowance 等多 Adj 类型组件)
    multipleFromPapers: Record<string, FromPaperData>;

    // ✅ 新增：组件详情数据存储
    componentDetails: Record<string, ComponentDetailData>;

    // ✅ 新增：数据保存成功标志
    dataSaved: boolean;
    setDataSaved: (saved: boolean) => void;

    // ✅ 新增：Header 管理
    header: AuditAdjustHeader | null;
    currentMode: AdjustmentMode | null;

    // ✅ 新增：Pair-based adjustment (§4.3)
    pairs: AdjustmentPair[];

    /** detail 变更代数，供 UI 订阅刷新 */
    detailRevision: number;

    /** fromPaper detail 是否已完成 syncFromPaperDetails（幂等标记） */
    fromPaperDetailsSynced: boolean;

    /** Phase 5: active WP session period (DialogLauncher) */
    wpSessionPeriod: WpSessionPeriod | null;
    setWpSessionPeriod: (session: WpSessionPeriod | null) => void;
    clearWpSessionPeriod: () => void;

    /** Phase 5 §5.7: Drawer period lock for current adjustment */
    periodLock: AdjPeriodLockState | null;
    beginPeriodLockFromSession: () => void;
    setPeriodLockAnchor: (sliceKey: string | null) => void;
    setPeriodLockIsAppend: (isAppend: boolean) => void;
    /** transient append flag for Drawer (not persisted in periodLock object) */
    periodLockIsAppend: boolean;
    clearPeriodLock: () => void;

    /** toPaper 组装：各 code 状态 */
    toPaperAssemblyStatus: Record<string, ToPaperAssemblyStatus>;
    /** 是否有 toPaper 组装任务进行中 */
    toPaperAssemblyBusy: boolean;
    /** 组装会话 ID，cleanAll / bump 后丢弃过期 async */
    assemblySessionId: number;
    /** 待 UI 处理的 toPaper 用户选择 */
    pendingUserAction: ToPaperPendingUserAction | null;
    /** Append Another Record 预设 adjustment */
    pendingAppendAdjustments: Record<string, number>;
    /** 提交草稿：无 detail / FS_TO_FS 预设金额 */
    draftSimpleAdjustments: Record<string, number>;
    /** 提交草稿：toPaper 预设金额 */
    draftToPaperAdjustments: Record<string, number | undefined>;
    /** 编辑 header 时固定 Dr/Cr 列 */
    staticPositionCodes: Set<string>;
    /** 运行时已注册的 scoped special codes（须在 SPECIAL_ADJUST_SCOPED_ELIGIBLE_CODES 内） */
    specialDtdPathCodes: Set<string>;
    /** 提交 remark */
    draftRemark: string;
    /** 已完成组装登记（防重复 fetch） */
    assembledCodes: string[];
    /** 组装产生的 extraRowKeys，供 AdjustTabsDialog 合并 */
    assemblyExtraRowKeys: string[];
    /** setFlatEquityDetail 产生的 adjustment，供 UI 合并 */
    assemblyFlatEquityAdjustments: Record<string, number>;
    /**
     * 用户已手动编辑过的 toPaper code。
     * 未在此集合中：渲染使用配对 effectiveEntry（resolveToPaperFieldsFromOriginalEntryPairing）；
     * 在此集合中：渲染使用 resolveEntryFromFsCategory 动态分列。
     */
    toPaperUserEditedCodes: Record<string, boolean>;
    /**
     * 用户已手动编辑过 fromPaper。
     * DTD/DIR 场景下为 true 时，toPaper 不再随 fromPaper 做配对渲染联动。
     */
    fromPaperUserEdited: boolean;

    /**
     * 编辑回显：后端 header 解析出的提交草稿（与 prepareSubmitHeader 输入对齐）
     * - backendLineIds：提交时合并 line.id / headerId
     */
    backendLineIds: Record<string, { id?: string; headerId?: string }>;
    draftHydratedFromBackend: boolean;

    /**
     * 設置來源表單數據
     * @param annualId 年度ID
     * @param code 表單代碼
     * @param adjustmentLine 調整行數據
     */
    setFromPaper: (data: FromPaperData) => void;

    /**
     * 简化版设置 fromPaper - 动态组件只需传入原始 adjustmentLine 数据
     * 内部自动从 fsAdjustStore 获取 entry、lineMeta 等信息
     * @param adjustmentLine 原始的 adjustmentLine 数据（包含 code, details, adjustmentAmount 等）
     * @param companyId 公司 ID
     * @param annualId 年度 ID
     */
    setFromPaperFromLine: (adjustmentLine: any, companyId: number, annualId: number) => void;

    /** 注册 scoped special 路径（code 须在 SPECIAL_ADJUST_SCOPED_ELIGIBLE_CODES） */
    registerSpecialPathCode: (code: string) => void;

    /**
     * IA Amor chg skip-Drawer 会话标志：AIA→IA toPaper 保持 line-only（`details: []`）。
     * fromPaper 可带指定行 amorChgAdj attr；由 IntangibleFieldAdjustPanel 在直装前开启；cleanAll / 新 fromPaper 清除。
     */
    iaAmorChgDirectAssembleActive: boolean;
    beginIaAmorChgDirectAssemble: () => void;
    clearIaAmorChgDirectAssemble: () => void;

    /**
     * PPE Depn chg Final skip-Drawer 会话标志：DEP→PPE toPaper 保持 line-only（`details: []`）。
     * fromPaper 可带指定行 depnChgAdj attr；由 PpeFieldAdjustPanel 在直装前开启；cleanAll / 新 fromPaper 清除。
     */
    ppeDepnChgDirectAssembleActive: boolean;
    beginPpeDepnChgDirectAssemble: () => void;
    clearPpeDepnChgDirectAssemble: () => void;

    /**
     * ITL Depn chg Final skip-Drawer session flag: DEP→ITL toPaper stays line-only (`details: []`).
     * Armed by IntangibleFieldAdjustPanel before direct assemble; cleared on cleanAll / new fromPaper.
     */
    itlDepnChgDirectAssembleActive: boolean;
    beginItlDepnChgDirectAssemble: () => void;
    clearItlDepnChgDirectAssemble: () => void;

    /**
     * 設置目標表單數據
     * @param code 表單代碼（可以是單個或多個）
     * @param entries 可選的 entry 映射
     */
    setToPaper: (code: string | string[], entries?: Record<string, string | null>, options?: {
        wpFetchCodes?: Record<string, string>;
    }) => void;

    /**
     * 添加目標表單代碼
     * @param code 要添加的表單代碼
     * @param entry 可選的 entry 值（Dr 或 Cr）
     * @param lineMeta 可选的 line 元数据（包含 name, scheduleName 等）
     */
    addToPaper: (code: string, entry?: string | null, lineMeta?: any) => void;

    /**
     * 移除目標表單代碼
     * @param code 要移除的表單代碼
     */
    removeToPaper: (code: string) => void;

    /**
     * 獲取調整數據
     * @returns 包含來源和目標表單數據的對象
     */
    getAdjustment: () => {
        fromPaper: FromPaperData | null;
        toPaper: ToPaperData | null;
    };

    /**
     * 獲取所有已使用的 code（from 和 to）
     * @returns code 數組
     */
    getAllCode: () => string[];

    /**
     * 設置 AdjustTabsDialog 開關狀態
     * @param open 是否打開
     */
    setAdjustTabsDialogOpen: (open: boolean) => void;

    /**
     * 注册 WP 跨组件跳转回调（由 FsAdjustment 注入，用于 NoDirectorConfirm 等场景）
     */
    onRequestOpenWpCode: ((code: string) => void) | null;
    setOnRequestOpenWpCode: (fn: ((code: string) => void) | null) => void;
    requestOpenWpCode: (code: string) => void;

    /**
     * 清空 fromPaper 临时数据（组件关闭时调用，防止残留影响其他操作）
     */
    clearFromPaper: () => void;

    /**
     * 清空所有數據
     */
    cleanAll: () => void;

    /**
     * 清理数据但保留对话框状态
     */
    cleanDataOnly: () => void;

    // ✅ 新增方法
    /**
     * 设置组件详情数据
     * @param code 表单代码
     * @param detail 详情数据
     * @deprecated 请使用 setUnifiedDetail，以便正确路由 fromPaper / toPaper 存储
     */
    setComponentDetail: (code: string, detail: any) => void;

    /**
     * 获取组件详情数据
     * @param code 表单代码
     * @returns 详情数据或 null
     * @deprecated 请使用 getUnifiedDetail
     */
    getComponentDetail: (code: string) => any | null;

    /**
     * 批量设置组件详情
     * @param details 详情数据映射
     */
    setBatchComponentDetails: (details: Record<string, any>) => void;

    /**
     * 验证所有组件数据完整性
     * @returns 验证结果
     */
    validateAllComponents: () => {
        valid: boolean;
        errors: string[];
    };

    /**
     * 获取所有组件的最新数据（用于保存）
     * @returns 所有组件数据
     */
    getAllComponentDetails: () => Record<string, any>;

    /**
     * 检查是否有组件详情数据
     * @param code 表单代码
     * @returns 是否存在数据
     */
    hasComponentDetail: (code: string) => boolean;

    // ✅ 新增：统一的数据获取方法
    /**
     * 获取任意 code 的详细数据（统一接口）
     * 优先级：FromPaper.detail > ToPaper.details > ComponentDetails
     * @param code 表单代码
     * @returns 详细数据或 null
     */
    getUnifiedDetail: (code: string) => any | null;

    /**
     * 设置任意 code 的详细数据（统一接口）
     * 自动判断应该存储到 FromPaper、ToPaper 还是 ComponentDetails
     * @param code 表单代码
     * @param detail 详细数据
     */
    setUnifiedDetail: (code: string, detail: any) => void;

    /**
     * Zustand selector：按 code 读取 unified detail
     */
    selectUnifiedDetail: (code: string) => (state: AdjustDraftState) => any | null;

    /**
     * 将 fromPaper.adjustmentLine.details 同步到 unified detail 存储（幂等）
     */
    syncFromPaperDetails: () => void;

    /** 重置 fromPaper sync 标记（header 切换 / Tabs 关闭清理时） */
    resetFromPaperDetailsSync: () => void;

    /**
     * 伴生 hidden codes（与 AdjustTabsDialog companionHiddenCodes useMemo 一致）
     */
    getCompanionHiddenCodes: () => Set<string>;

    /** 递增 assembly 会话（Tabs 关闭 / cleanAll） */
    bumpAssemblySession: () => void;

    /** 重置 toPaper 组装登记（header 切换） */
    resetToPaperAssemblyRegistry: () => void;

    /** 清除单个 code 的组装登记（endpoint depleted 后重加） */
    clearAssembledCode: (code: string) => void;

    setPendingAppendAdjustment: (code: string, amount: number) => void;
    /** Append Another Record：原子写入 pendingAppend + toPaper + draft adjustment */
    appendToPaperRecord: (
        code: string,
        balanceAmount: number,
        effectiveEntry: string,
        lineMeta?: any,
        options?: {wpFetchCode?: string},
    ) => void;
    setDraftSimpleAdjustment: (code: string, amount: number) => void;
    setDraftToPaperAdjustment: (code: string, amount: number | undefined) => void;
    setStaticPositionCodes: (codes: Set<string>) => void;
    setDraftRemark: (remark: string) => void;

    /**
     * 更新指定 line 的 detail.adjustment、entry 与 adjustmentAmount。
     * entry 由 resolveEntryFromFsCategory 根据 FS 节点 originalEntry 与行汇总 adjustment 推算。
     */
    updateLineAdjustment: (
        code: string,
        arrayName: string,
        rowIndex: number,
        adjustment: number,
        subField?: string,
    ) => void;

    /** 根据 fromPaper/toPaper 配对重算 draftToPaperAdjustments */
    recomputeToPaperAdjustments: () => void;

    /** 获取 Dr/Cr 渲染用 header JSON（结构与提交/回显 JSON 一致） */
    getDrCrRenderPayload: () => AuditAdjustHeader | null;

    /** 扫描 toPaper.codes 并组装尚未登记的 code */
    enqueueToPaperAssembly: (codes: string[]) => Promise<void>;

    syncToPaperAssembly: () => Promise<void>;

    fetchAndAssembleToPaper: (code: string) => Promise<void>;

    verifyToPaperDetail: (code: string) => Promise<void>;

    /** 消费 assemblyExtraRowKeys（UI 合并后调用） */
    consumeAssemblyExtraRowKeys: () => string[];

    /** 消费 assemblyFlatEquityAdjustments */
    consumeAssemblyFlatEquityAdjustments: () => Record<string, number>;

    clearPendingUserAction: () => void;

    finishPendingUserSelection: (code: string, detail: any, extraRowKeys?: string[]) => void;

    cancelPendingUserSelection: () => void;

    resolveToPaperUserAction: (action: any) => void;

    cancelToPaperUserAction: (fallback?: 'default_first_row' | 'initial_factory') => void;

    resolvePendingCommonInvestmentSelection: (binding: {
        rowId?: number;
        rowIndex?: number;
        rowKey?: string;
    }) => void;

    resolvePendingArraySelection: (selectedArrayKey: string) => void;

    resolvePendingRowSelection: (binding: { rowIndex?: number }) => void;

    commitAssemblyApplyResult: (
        code: string,
        applyResult: ReturnType<typeof applyToPaperAssemblyOutcome>,
        noDetailAppendAdj?: number,
    ) => void;

    /**
     * 获取任意 code 的 adjustmentLine
     * @param code 表单代码
     * @returns adjustmentLine 或 null
     */
    getAdjustmentLine: (code: string) => AuditAdjustmentLine | null;

    // ✅ 新增：Header 管理方法
    /**
     * 初始化 Header
     * @param mode 调整模式
     */
    initHeader: (mode: AdjustmentMode) => void;

    /**
     * 设置 Header
     * @param header Header 数据
     */
    setHeader: (header: AuditAdjustHeader | null) => void;

    /**
     * 设置当前模式
     * @param mode 调整模式
     */
    setCurrentMode: (mode: AdjustmentMode | null) => void;

    /**
     * 插入或更新 Header Line
     * @param line Line 数据
     */
    upsertLine: (line: AuditAdjustLine) => void;

    /**
     * 构建提交用的 Header 对象
     * @param userId 用户 ID
     * @returns 完整的 Header 对象
     */
    buildDraftHeaderLines: (mode: AdjustmentMode) => void;
    validateBeforeSubmit: () => {
        valid: boolean;
        errors: string[];
        bankFieldErrors?: Record<string, Record<number, { bankName?: string; accountNumber?: string }>>
    };
    prepareSubmitHeader: (params: { userId: number; remark?: string; mode?: AdjustmentMode }) => {
        valid: true;
        header: AuditAdjustHeader
    } | {
        valid: false;
        errors: string[];
        bankFieldErrors?: Record<string, Record<number, { bankName?: string; accountNumber?: string }>>
    };
    buildSubmitHeader: (userId: number) => AuditAdjustHeader;

    /**
     * 处理并添加 Schedule Adjustment Line
     * 将组件详情数据转换为 AuditAdjustLine 并存入 header.lines
     * @param code Schedule 代码
     * @param detail 组件详情数据
     * @param annualId 年度 ID
     * @param entry 原始 entry (Dr/Cr)，用于计算最终 entry
     */
    processAndAddLine: (code: string, detail: any, annualId: number, entry?: string) => void;

    /**
     * 批量处理多个 Schedule
     * @param items 要处理的 schedule 列表
     */
    processAllSchedules: (items: Array<{ code: string; detail: any; annualId: number; entry?: string }>) => void;

    /**
     * 从后端返回的 Header 数据加载到 store
     * 用于点击 adjustment cell 时加载已存在的调整数据
     * @param headerData 后端返回的 AuditAdjustHeader 数据
     * @param getLineByCode 可选的函数，用于查找 line 元数据（避免循环依赖）
     */
    loadFromHeader: (headerData: AuditAdjustHeader, getLineByCode?: (code: string) => any) => void;

    // ✅ Pair CRUD methods (§4.3)
    /**
     * Create a new adjustment pair
     * @param from Source endpoint data
     * @param to Target endpoint(s) data
     * @returns The created pair's id
     */
    createPair: (from: Omit<PairEndpoint, 'adjustmentAmount'> & {
        adjustmentAmount?: number
    }, to: Array<Omit<PairEndpoint, 'adjustmentAmount'> & { adjustmentAmount?: number }>) => string;

    /**
     * Update an existing pair's endpoint data
     * @param pairId The pair to update
     * @param updates Partial updates to from/to endpoints
     */
    updatePair: (pairId: string, updates: { from?: Partial<PairEndpoint>; to?: Array<Partial<PairEndpoint>> }) => void;

    /**
     * Update a specific endpoint's adjustment amount within a pair
     * @param pairId The pair id
     * @param side 'from' or index into 'to' array
     * @param amount New adjustment amount
     */
    updatePairAmount: (pairId: string, side: 'from' | number, amount: number) => void;

    /**
     * Remove a pair by id
     * @param pairId The pair to remove
     */
    removePair: (pairId: string) => void;

    /**
     * Get all pairs
     */
    getAllPairs: () => AdjustmentPair[];

    /**
     * Get a specific pair by id
     */
    getPair: (pairId: string) => AdjustmentPair | undefined;

    /**
     * Clear all pairs
     */
    clearPairs: () => void;

    /**
     * Update fromPaper entry value (for Dr/Cr sync when adjustment changes)
     * @param entry New entry value ('Dr' | 'Cr')
     */
    updateFromPaperEntry: (entry: string) => void;

    /**
     * Update a specific toPaper entry value (for Dr/Cr sync when adjustment changes)
     * @param code The schedule code
     * @param entry New entry value ('Dr' | 'Cr')
     */
    updateToPaperEntry: (code: string, entry: string) => void;

    // ✅ 新增：多个 fromPaper 管理方法 (用于 SalariesAllowance 等组件)
    /**
     * 设置多个 fromPaper 数据
     * @param code 调整类型对应的 code (如 'PL-AE-SAL', 'PL-AE-WAG' 等)
     * @param data fromPaper 数据
     */
    setMultipleFromPaper: (code: string, data: FromPaperData) => void;

    /**
     * 获取所有 multipleFromPapers
     */
    getMultipleFromPapers: () => Record<string, FromPaperData>;

    /**
     * 清空所有 multipleFromPapers
     */
    clearMultipleFromPapers: () => void;

    /**
     * 检查是否有多个 fromPaper
     */
    hasMultipleFromPapers: () => boolean;
}

export const useAdjustDraftStore = create<AdjustDraftState>((set, get) => ({
    fromPaper: null,
    toPaper: null,
    adjustTabsDialogOpen: false,
    onRequestOpenWpCode: null,
    multipleFromPapers: {}, // ✅ 初始化多个 fromPaper
    componentDetails: {}, // ✅ 初始化
    dataSaved: false, // ✅ 初始化数据保存标志
    header: null, // ✅ 初始化 Header
    currentMode: null, // ✅ 初始化当前模式
    pairs: [], // ✅ 初始化 Pair 数组
    detailRevision: 0,
    fromPaperDetailsSynced: false,
    toPaperAssemblyStatus: {},
    toPaperAssemblyBusy: false,
    assemblySessionId: 0,
    pendingUserAction: null,
    pendingAppendAdjustments: {},
    draftSimpleAdjustments: {},
    draftToPaperAdjustments: {},
    staticPositionCodes: new Set(),
    specialDtdPathCodes: new Set(),
    iaAmorChgDirectAssembleActive: false,
    ppeDepnChgDirectAssembleActive: false,
    itlDepnChgDirectAssembleActive: false,
    draftRemark: 'To reclassify the accounts',
    assembledCodes: [],
    assemblyExtraRowKeys: [],
    assemblyFlatEquityAdjustments: {},
    backendLineIds: {},
    draftHydratedFromBackend: false,
    toPaperUserEditedCodes: {},
    fromPaperUserEdited: false,

    wpSessionPeriod: null,
    periodLock: null,
    periodLockIsAppend: false,

    setWpSessionPeriod: (session) => set({wpSessionPeriod: session}),
    clearWpSessionPeriod: () => set({wpSessionPeriod: null}),

    beginPeriodLockFromSession: () => {
        const session = get().wpSessionPeriod;
        if (!session || session.spanType !== 'MULTI' || !session.periods?.length) {
            set({periodLock: null, periodLockIsAppend: false});
            return;
        }
        const existing = get().periodLock;
        // Keep anchor across append within same session if initiator matches
        const sameInitiator = existing
            && existing.initiator.code === session.code
            && existing.initiator.statement === session.statement;
        set({
            periodLock: {
                spanType: 'MULTI',
                initiator: {
                    statement: session.statement,
                    code: session.code,
                    sliceKey: session.sliceKey,
                },
                anchorSliceKey: sameInitiator
                    ? (existing!.anchorSliceKey ?? session.sliceKey)
                    : session.sliceKey,
                periods: session.periods,
            },
            periodLockIsAppend: false,
        });
    },

    setPeriodLockAnchor: (sliceKey) => {
        const lock = get().periodLock;
        if (!lock) return;
        set({periodLock: {...lock, anchorSliceKey: sliceKey}});
    },

    setPeriodLockIsAppend: (isAppend) => set({periodLockIsAppend: !!isAppend}),

    clearPeriodLock: () => set({periodLock: null, periodLockIsAppend: false}),

    setFromPaper: (data: FromPaperData) => {
        const normalizedCode = normalizeLineCode(data.code);
        if (!normalizedCode) {
            set({fromPaper: null});
            return;
        }

        // entry / adjustment 已在 WP mergeDataForAdjust（或 unifiedMergeData）阶段解析完毕，此处仅落库
        const lineLike = data.adjustmentLine as any;
        const entry = lineLike?.entry ?? data.entry ?? null;
        const adjustment = lineLike?.adjustment !== undefined && lineLike?.adjustment !== null
            ? Number(lineLike.adjustment)
            : Number(data.adjustment ?? lineLike?.adjustmentAmount) || 0;
        const adjustmentAmount = lineLike?.adjustmentAmount !== undefined && lineLike?.adjustmentAmount !== null
            ? Number(lineLike.adjustmentAmount)
            : Math.abs(adjustment);

        const fromPaperPayload = {
            ...data,
            code: normalizedCode,
            entry,
            adjustment,
            adjustmentLine: {
                ...data.adjustmentLine,
                scheduleCode: lineLike?.scheduleCode || normalizedCode,
                entry,
                adjustment,
                adjustmentAmount,
            },
        };

        // console.log('[adjustDraftStore] fromPaper', fromPaperPayload);

        set({
            fromPaper: fromPaperPayload,
            fromPaperDetailsSynced: false,
            draftHydratedFromBackend: false,
            fromPaperUserEdited: false,
            // New fromPaper starts a fresh session; Amor/Depn panels re-arm via begin*DirectAssemble
            iaAmorChgDirectAssembleActive: false,
            ppeDepnChgDirectAssembleActive: false,
            itlDepnChgDirectAssembleActive: false,
        });
        // Phase 5 §5.7: seed Drawer period lock from open WP session
        get().beginPeriodLockFromSession();
    },

    setFromPaperFromLine: (adjustmentLine: any, companyId: number, annualId: number) => {
        const code = normalizeLineCode(adjustmentLine?.code || adjustmentLine?.scheduleCode || '');
        if (!code) {
            return;
        }

        const fsLine = useFsAdjustStore.getState().getLineByCode(code);
        const lineMeta = {
            name: fsLine?.name || fsLine?.scheduleName || '',
            scheduleName: fsLine?.scheduleName || '',
            fsId: adjustmentLine?.fsId || fsLine?.fsId || 0,
        };

        const entry = adjustmentLine?.entry ?? null;
        const adjustment = adjustmentLine?.adjustment !== undefined && adjustmentLine?.adjustment !== null
            ? Number(adjustmentLine.adjustment)
            : Number(adjustmentLine?.adjustmentAmount) || 0;
        const adjustmentAmount = adjustmentLine?.adjustmentAmount !== undefined && adjustmentLine?.adjustmentAmount !== null
            ? Number(adjustmentLine.adjustmentAmount)
            : Math.abs(adjustment);

        get().setFromPaper({
            companyId,
            annualId,
            code,
            adjustment,
            entry,
            adjustmentLine: {
                ...adjustmentLine,
                scheduleCode: adjustmentLine?.scheduleCode || code,
                entry,
                adjustment,
                adjustmentAmount,
            },
            lineMeta,
        });

        if (isTaxWpBfSpecialAdjustmentLine(code, adjustmentLine?.details)) {
            get().registerSpecialPathCode(code);
        }
    },

    registerSpecialPathCode: (code: string) => {
        const normalized = normalizeLineCode(code);
        if (!normalized || !isSpecialAdjustScopedEligible(normalized)) return;
        set((state) => {
            if (state.specialDtdPathCodes.has(normalized)) return state;
            const next = new Set(state.specialDtdPathCodes);
            next.add(normalized);
            return {specialDtdPathCodes: next};
        });
    },

    beginIaAmorChgDirectAssemble: () => {
        set({iaAmorChgDirectAssembleActive: true});
    },

    clearIaAmorChgDirectAssemble: () => {
        set({iaAmorChgDirectAssembleActive: false});
    },

    beginPpeDepnChgDirectAssemble: () => {
        set({ppeDepnChgDirectAssembleActive: true});
    },

    clearPpeDepnChgDirectAssemble: () => {
        set({ppeDepnChgDirectAssembleActive: false});
    },

    beginItlDepnChgDirectAssemble: () => {
        set({itlDepnChgDirectAssembleActive: true});
    },

    clearItlDepnChgDirectAssemble: () => {
        set({itlDepnChgDirectAssembleActive: false});
    },

    setToPaper: (code: string | string[], entries?: Record<string, string | null>, options?: {
        wpFetchCodes?: Record<string, string>;
    }) => {
        const codeArr = Array.isArray(code) ? code : [code];
        const normalizedCodes = Array.from(new Set(codeArr.map(normalizeLineCode).filter(Boolean)));
        const normalizedEntries: Record<string, string | null> = {};
        for (const [k, v] of Object.entries(entries || {})) {
            const nk = normalizeLineCode(k);
            if (!nk) continue;
            normalizedEntries[nk] = v;
        }
        const normalizedOriginalEntries: Record<string, string | null> = {};
        for (const nk of normalizedCodes) {
            normalizedOriginalEntries[nk] = resolveFsNodeOriginalEntry(nk, normalizedEntries[nk]) ?? null;
        }

        const wpFetchCodes: Record<string, string> = {};
        for (const [k, v] of Object.entries(options?.wpFetchCodes || {})) {
            const nk = normalizeLineCode(k);
            const fetchCode = normalizeWpFetchCodeForStore(v);
            if (nk && fetchCode) wpFetchCodes[nk] = fetchCode;
        }

        // Fresh toPaper selection must re-fetch WP and offer Empty record rowSelection.
        // Stale assembledCodes / componentDetails / prior pending would skip into verify→ready.
        set((s) => {
            const nextComponentDetails = {...s.componentDetails};
            const nextStatus = {...s.toPaperAssemblyStatus};
            let nextAssembled = [...(s.assembledCodes || [])];
            for (const nk of normalizedCodes) {
                delete nextComponentDetails[nk];
                delete nextStatus[nk];
                nextAssembled = filterAssembledCodes(nextAssembled, nk);
            }
            return {
                toPaper: {
                    codes: normalizedCodes,
                    entries: normalizedEntries,
                    originalEntries: normalizedOriginalEntries,
                    ...(Object.keys(wpFetchCodes).length > 0 ? {wpFetchCodes} : {}),
                },
                draftHydratedFromBackend: false,
                toPaperUserEditedCodes: {},
                draftToPaperAdjustments: {},
                fromPaperUserEdited: false,
                componentDetails: nextComponentDetails,
                toPaperAssemblyStatus: nextStatus,
                assembledCodes: nextAssembled,
                // Only drop pending if it targets one of the codes being replaced.
                pendingUserAction: (
                    s.pendingUserAction
                    && normalizedCodes.includes(
                        normalizeLineCode(
                            'code' in s.pendingUserAction ? s.pendingUserAction.code : '',
                        ),
                    )
                ) ? null : s.pendingUserAction,
                // Keep other codes' extraRowKeys (multi-toPaper / non-empty bindings intact).
                assemblyExtraRowKeys: (s.assemblyExtraRowKeys || []).filter(
                    (k) => !normalizedCodes.some((nk) => String(k).startsWith(`${nk}:`)),
                ),
            };
        });
        // TAXP / TAXR toPaper：提前注册 scoped special（与 TaxWp fromPaper 对称）
        for (const nk of normalizedCodes) {
            if (isTaxBfSpecialDtdCode(nk)) {
                get().registerSpecialPathCode(nk);
            }
        }
        // 同步配对 Dr/Cr，避免 Dialog 首帧渲染时 toPaper.entries 仍为空
        get().recomputeToPaperAdjustments();
        void get().enqueueToPaperAssembly(normalizedCodes);
    },

    addToPaper: (code: string, entry?: string | null, lineMeta?: any) => {
        const state = get();
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return;
        const currentCodes = state.toPaper?.codes || [];
        const currentEntries = state.toPaper?.entries || {};
        const currentOriginalEntries = state.toPaper?.originalEntries || {};
        const currentLineMetas = state.toPaper?.lineMetas || {};
        if (!toPaperContainsCode(currentCodes, normalizedCode)) {
            const newEntries = {...currentEntries};
            const newOriginalEntries = {...currentOriginalEntries};
            const newLineMetas = {...currentLineMetas};
            const fsOriginalEntry = resolveFsNodeOriginalEntry(normalizedCode, lineMeta?.entry)
                || resolveFsNodeOriginalEntry(normalizedCode);
            if (entry) {
                newEntries[normalizedCode] = entry;
            }
            if (!newOriginalEntries[normalizedCode] && fsOriginalEntry) {
                newOriginalEntries[normalizedCode] = fsOriginalEntry;
            }
            if (lineMeta) {
                newLineMetas[normalizedCode] = lineMeta;
            }
            const newToPaper = {
                ...state.toPaper,
                codes: [...currentCodes, normalizedCode],
                entries: newEntries,
                originalEntries: newOriginalEntries,
                lineMetas: newLineMetas,
            };

            set({
                toPaper: newToPaper,
            });
            if (isTaxBfSpecialDtdCode(normalizedCode)) {
                get().registerSpecialPathCode(normalizedCode);
            }
            get().clearAssembledCode(normalizedCode);
            void get().enqueueToPaperAssembly([normalizedCode]);
        }
    },

    removeToPaper: (code: string) => {
        const state = get();
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return;
        const currentCodes = state.toPaper?.codes || [];
        const currentEntries = {...(state.toPaper?.entries || {})};
        const currentOriginalEntries = {...(state.toPaper?.originalEntries || {})};
        const newCodes = currentCodes.filter((c) => normalizeLineCode(c) !== normalizedCode);
        delete currentEntries[normalizedCode];
        delete currentOriginalEntries[normalizedCode];

        // 清理 adjustmentLines、details、lineMetas、wpFetchCodes 中对应 code 的数据
        const newAdjustmentLines = {...(state.toPaper?.adjustmentLines || {})};
        delete newAdjustmentLines[normalizedCode];
        const newToPaperDetails = {...(state.toPaper?.details || {})};
        delete newToPaperDetails[normalizedCode];
        const newLineMetas = {...(state.toPaper?.lineMetas || {})};
        delete newLineMetas[normalizedCode];
        const newWpFetchCodes = {...(state.toPaper?.wpFetchCodes || {})};
        delete newWpFetchCodes[normalizedCode];

        set({
            toPaper: {
                ...state.toPaper,
                codes: newCodes,
                entries: currentEntries,
                originalEntries: currentOriginalEntries,
                adjustmentLines: newAdjustmentLines,
                details: newToPaperDetails,
                lineMetas: newLineMetas,
                wpFetchCodes: newWpFetchCodes,
            }
        });

        // ✅ 同时删除对应的组件数据
        const newComponentDetails = {...state.componentDetails};
        delete newComponentDetails[normalizedCode];

        const newAssemblyStatus = {...state.toPaperAssemblyStatus};
        delete newAssemblyStatus[normalizedCode];
        const newPendingAppend = {...state.pendingAppendAdjustments};
        delete newPendingAppend[normalizedCode];
        const pendingUserAction = state.pendingUserAction?.code === normalizedCode
            ? null
            : state.pendingUserAction;
        const nextUserEdited = {...state.toPaperUserEditedCodes};
        delete nextUserEdited[normalizedCode];

        const newDraftToPaperAdj = {...state.draftToPaperAdjustments};
        delete newDraftToPaperAdj[normalizedCode];
        for (const key of Object.keys(newDraftToPaperAdj)) {
            if (normalizeLineCode(key) === normalizedCode) {
                delete newDraftToPaperAdj[key];
            }
        }

        set({
            componentDetails: newComponentDetails,
            toPaperAssemblyStatus: newAssemblyStatus,
            pendingAppendAdjustments: newPendingAppend,
            pendingUserAction,
            assembledCodes: filterAssembledCodes(state.assembledCodes, normalizedCode),
            toPaperUserEditedCodes: nextUserEdited,
            draftToPaperAdjustments: newDraftToPaperAdj,
        });
    },

    getAdjustment: () => {
        const state = get();
        return {
            fromPaper: state.fromPaper,
            toPaper: state.toPaper,
        };
    },

    getAllCode: () => {
        const state = get();
        const codeArr: string[] = [];

        // 收集 fromPaper 的 code
        if (state.fromPaper?.code) {
            codeArr.push(state.fromPaper.code);
        }

        // 收集 toPaper 的 codes
        if (state.toPaper?.codes) {
            codeArr.push(...state.toPaper.codes);
        }

        // 去重並返回
        return Array.from(new Set(codeArr));
    },

    setAdjustTabsDialogOpen: (open: boolean) => {
        set({adjustTabsDialogOpen: open});
    },

    setOnRequestOpenWpCode: (fn) => {
        set({onRequestOpenWpCode: fn});
    },

    requestOpenWpCode: (code: string) => {
        get().onRequestOpenWpCode?.(code);
    },

    clearFromPaper: () => {
        set({fromPaper: null});
    },

    cleanAll: () => {
        const currentState = get();
        set({
            fromPaper: null,
            toPaper: null,
            adjustTabsDialogOpen: false,
            multipleFromPapers: {}, // ✅ 清空多 fromPaper
            componentDetails: {}, // ✅ 清空组件数据
            dataSaved: false, // ✅ 重置保存标志
            header: null, // ✅ 清空 Header
            currentMode: null, // ✅ 清空模式
            pairs: [], // ✅ 清空 Pairs
            detailRevision: 0,
            fromPaperDetailsSynced: false,
            toPaperAssemblyStatus: {},
            toPaperAssemblyBusy: false,
            assemblySessionId: currentState.assemblySessionId + 1,
            pendingUserAction: null,
            pendingAppendAdjustments: {},
            draftSimpleAdjustments: {},
            draftToPaperAdjustments: {},
            staticPositionCodes: new Set(),
            specialDtdPathCodes: new Set(),
            iaAmorChgDirectAssembleActive: false,
            ppeDepnChgDirectAssembleActive: false,
            itlDepnChgDirectAssembleActive: false,
            draftRemark: 'To reclassify the accounts',
            assembledCodes: [],
            assemblyExtraRowKeys: [],
            assemblyFlatEquityAdjustments: {},
            backendLineIds: {},
            draftHydratedFromBackend: false,
            toPaperUserEditedCodes: {},
            fromPaperUserEdited: false,
            periodLock: null,
            periodLockIsAppend: false,
        });
    },

    getDrCrRenderPayload: () => buildDrCrRenderPayload(get(), (code) => get().getUnifiedDetail(code)),

    // 新增：清理数据但保留对话框状态
    cleanDataOnly: () => {
        const currentState = get();
        set({
            fromPaper: null,
            toPaper: null,
            adjustTabsDialogOpen: currentState.adjustTabsDialogOpen, // ✅ 保留对话框状态
            multipleFromPapers: {}, // ✅ 清空多 fromPaper
            componentDetails: {}, // ✅ 清空组件数据
            dataSaved: false, // ✅ 重置保存标志
            header: null, // ✅ 清空 Header
            currentMode: null, // ✅ 清空模式
            pairs: [], // ✅ 清空 Pairs
            detailRevision: 0,
            fromPaperDetailsSynced: false,
            toPaperAssemblyStatus: {},
            toPaperAssemblyBusy: false,
            assemblySessionId: currentState.assemblySessionId + 1,
            pendingUserAction: null,
            pendingAppendAdjustments: {},
            draftSimpleAdjustments: {},
            draftToPaperAdjustments: {},
            staticPositionCodes: new Set(),
            specialDtdPathCodes: new Set(),
            iaAmorChgDirectAssembleActive: false,
            ppeDepnChgDirectAssembleActive: false,
            itlDepnChgDirectAssembleActive: false,
            draftRemark: 'To reclassify the accounts',
            assembledCodes: [],
            assemblyExtraRowKeys: [],
            assemblyFlatEquityAdjustments: {},
            backendLineIds: {},
            draftHydratedFromBackend: false,
            toPaperUserEditedCodes: {},
            fromPaperUserEdited: false,
        });
    },

    setDataSaved: (saved: boolean) => {
        set({dataSaved: saved});
    },

    // ✅ 新方法实现（添加数据比较，避免重复设置）
    /** @deprecated 请使用 setUnifiedDetail */
    setComponentDetail: (code: string, detail: any) => {
        get().setUnifiedDetail(code, detail);
    },

    getComponentDetail: (code: string) => {
        return get().getUnifiedDetail(code);
    },

    setBatchComponentDetails: (details: Record<string, any>) => {
        const now = Date.now();
        const componentDetails: Record<string, ComponentDetailData> = {};

        Object.entries(details).forEach(([code, detail]) => {
            const normalizedCode = normalizeLineCode(code);
            if (!normalizedCode) return;
            componentDetails[normalizedCode] = {
                code: normalizedCode,
                detail,
                lastUpdated: now,
            };
        });

        set({componentDetails});
    },

    validateAllComponents: () => {
        const state = get();
        const errors: string[] = [];

        // 验证 fromPaper
        const fromCode = normalizeLineCode(state.fromPaper?.code);
        const fromDetailForSchedule = resolveFromDetailForScheduleContext(
            state.fromPaper,
            (c) => get().getUnifiedDetail(c),
        );
        if (!fromCode) {
            errors.push('缺少 fromPaper code');
        } else if (isNoWpDetailCode(fromCode) && !isNoWpDetailCategoryFetchPath(fromCode, state)) {
            // no-op: 无 WP detail 的 code 不要求 detail（REN/PPE 分类路径除外）
        } else if (
            isScheduleLevelEmptyDetailContext(fromCode, fromDetailForSchedule, fromCode)
            || isIaScheduleLevelNbvOrAmorContext(fromCode, fromDetailForSchedule)
            || isPpeScheduleLevelNbvOrDepnContext(fromCode, fromDetailForSchedule)
            || (state.ppeDepnChgDirectAssembleActive && fromCode === 'PL-AE-DEP')
            || (state.itlDepnChgDirectAssembleActive && fromCode === 'PL-AE-DEP')
        ) {
            // Schedule-level NBV/Amor/Depn: line-level amount only (`details: []`)
        } else if (!hasNonEmptyUnifiedDetail(get().getUnifiedDetail(fromCode))) {
            errors.push(`fromPaper 数据缺失: ${fromCode}`);
        }

        // 验证 toPaper
        if (!state.toPaper?.codes || state.toPaper.codes.length === 0) {
            errors.push('缺少 toPaper codes');
        } else {
            state.toPaper.codes.forEach((code) => {
                const normalizedCode = normalizeLineCode(code);
                if (!normalizedCode) return;
                if (isNoWpDetailCode(normalizedCode) && !isNoWpDetailCategoryFetchPath(normalizedCode, state)) {
                    return;
                }
                // Schedule-level Amor↔NBV / Depn↔NBV: empty toPaper detail is intentional
                if (isScheduleLevelEmptyDetailContext(fromCode, fromDetailForSchedule, normalizedCode, {
                    allowBlankAiaToIa: state.iaAmorChgDirectAssembleActive,
                    allowBlankDepToPpe: state.ppeDepnChgDirectAssembleActive,
                    allowBlankDepToItl: state.itlDepnChgDirectAssembleActive,
                })) {
                    return;
                }
                // 仅：其它组件 → BS-NCA-PPE 根节点 line-only（details: []），不要求 WP detail
                // IA/ITL 仍走上方 isNoWpDetailCode；PPE 分类 xxxRow / PPE↔DEP 自身不变
                if (
                    isInboundPpeRootLineOnlyToPaper(
                        normalizedCode,
                        fromCode,
                        lookupToPaperMap(state.toPaper?.wpFetchCodes, normalizedCode),
                    )
                ) {
                    return;
                }
                if (!hasNonEmptyUnifiedDetail(get().getUnifiedDetail(normalizedCode))) {
                    errors.push(`toPaper 数据缺失: ${normalizedCode || code}`);
                }
            });
        }

        const valid = errors.length === 0;

        return {valid, errors};
    },

    getAllComponentDetails: () => {
        const state = get();
        const details: Record<string, any> = {};

        // Helper: unwrap {component, version, payload} format
        const unwrap = (d: any): any => {
            if (d && typeof d === 'object' && !Array.isArray(d) && d.payload && typeof d.payload === 'object') {
                return d.payload;
            }
            return d;
        };

        // 1. 从 componentDetails 获取（向后兼容）
        Object.values(state.componentDetails).forEach((item) => {
            details[item.code] = unwrap(item.detail);
        });

        // 2. 从 fromPaper.adjustmentLine.details 获取
        if (state.fromPaper?.code && state.fromPaper.adjustmentLine?.details) {
            let fromDetails = state.fromPaper.adjustmentLine.details;
            if (Array.isArray(fromDetails) && fromDetails.length > 0) {
                fromDetails = fromDetails[0];
            }
            const normalizedCode = normalizeLineCode(state.fromPaper.code);
            if (normalizedCode) {
                details[normalizedCode] = unwrap(fromDetails);
            }
        }

        // 3. 从 toPaper.details 获取
        if (state.toPaper?.details) {
            Object.entries(state.toPaper.details).forEach(([code, detail]) => {
                const normalizedCode = normalizeLineCode(code);
                if (!normalizedCode) return;
                details[normalizedCode] = unwrap(detail);
            });
        }

        return details;
    },

    hasComponentDetail: (code: string) => {
        return hasNonEmptyUnifiedDetail(get().getUnifiedDetail(code));
    },

    // ✅ 统一数据获取方法实现
    getUnifiedDetail: (code: string) => {
        const state = get();
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return null;

        // Helper: unwrap {component, version, payload} format
        const unwrapPayload = (d: any): any => {
            if (d && typeof d === 'object' && !Array.isArray(d) && d.payload && typeof d.payload === 'object') {
                return d.payload;
            }
            return d;
        };

        // 1. 优先从 FromPaper 的 adjustmentLine.details 获取
        const fromPaper = state.fromPaper;
        if (fromPaper && isSameLineCode(fromPaper.code, normalizedCode) && fromPaper.adjustmentLine?.details) {
            let details = fromPaper.adjustmentLine.details;

            // ✅ 空数组视为无数据，跳过此路径继续查找其他来源
            if (Array.isArray(details) && details.length === 0) {
                // fall through to other sources
            } else {
                // Handle case where details is wrapped in an array (from buildAdjustmentLine)
                if (Array.isArray(details) && details.length > 0) {
                    details = details[0];
                }

                return unwrapPayload(details);
            }
        }

        // 2. 其次从 ToPaper 获取
        const toPaperDetail = lookupToPaperMap(state.toPaper?.details, normalizedCode);
        if (toPaperDetail) {
            return unwrapPayload(toPaperDetail);
        }

        // 3. 从 ComponentDetails 获取（向后兼容）
        const componentDetail = state.componentDetails[normalizedCode];
        if (componentDetail?.detail) {
            return unwrapPayload(componentDetail.detail);
        }

        // 4. Fallback: 从 ToPaper.adjustmentLines 获取原始后端数据
        // 当 AdjustDrCrView 不渲染 DynamicComponent 时，toPaper.details 不会被填充，
        // 但原始数据存在于 adjustmentLines 中
        if (state.toPaper?.adjustmentLines?.[normalizedCode]?.details) {
            let details = state.toPaper.adjustmentLines[normalizedCode].details;
            // ✅ 空数组视为无数据
            if (Array.isArray(details) && details.length === 0) {
                return null;
            }
            // Handle array wrapping (from buildAdjustmentLine)
            if (Array.isArray(details) && details.length > 0) {
                details = details[0];
            }
            // Handle {component, version, payload} wrapping
            const d = details as any;
            if (d && typeof d === 'object' && !Array.isArray(d) && d.payload && typeof d.payload === 'object') {
                details = d.payload;
            }
            return details;
        }

        return null;
    },

    setUnifiedDetail: (code: string, detail: any) => {
        const state = get();
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return;

        const existingDetail = get().getUnifiedDetail(normalizedCode);
        if (existingDetail && JSON.stringify(existingDetail) === JSON.stringify(detail)) {
            return;
        }

        const bumpRevision = () => {
            set((s) => ({detailRevision: s.detailRevision + 1}));
        };

        // 1. 如果是 FromPaper，更新 adjustmentLine.details
        if (isSameLineCode(state.fromPaper?.code, normalizedCode)) {
            set((state) => ({
                fromPaper: state.fromPaper ? {
                    ...state.fromPaper,
                    adjustmentLine: {
                        ...state.fromPaper.adjustmentLine,
                        details: detail,
                    },
                } : null,
            }));
            bumpRevision();
            return;
        }

        // 2. 如果是 ToPaper
        const toPaper = state.toPaper;
        if (toPaper && toPaperContainsCode(toPaper.codes, normalizedCode)) {
            const {companyId, annualId} = getPaperContextIds(state);
            logToPaperAssembly({
                source: 'setUnifiedDetail',
                code: normalizedCode,
                companyId,
                annualId,
                entry: lookupToPaperMap(toPaper.entries, normalizedCode) ?? null,
                lineMeta: lookupToPaperMap(toPaper.lineMetas, normalizedCode),
                adjustmentLine: lookupToPaperMap(toPaper.adjustmentLines, normalizedCode),
                adjustmentAmount: lookupToPaperMap(toPaper.adjustmentLines, normalizedCode)?.adjustmentAmount,
            }, detail);
            set((state) => ({
                toPaper: state.toPaper ? {
                    ...state.toPaper,
                    details: {
                        ...state.toPaper.details,
                        [normalizedCode]: detail,
                    },
                } : null,
                componentDetails: {
                    ...state.componentDetails,
                    [normalizedCode]: {
                        code: normalizedCode,
                        detail,
                        lastUpdated: Date.now(),
                    },
                },
            }));
            bumpRevision();
            return;
        }

        // 3. 否则存储到 ComponentDetails（向后兼容）
        set((state) => ({
            componentDetails: {
                ...state.componentDetails,
                [normalizedCode]: {
                    code: normalizedCode,
                    detail,
                    lastUpdated: Date.now(),
                },
            },
        }));
        bumpRevision();
    },

    selectUnifiedDetail: (code: string) => (state: AdjustDraftState) => {
        return state.getUnifiedDetail(code);
    },

    syncFromPaperDetails: () => {
        const state = get();
        const fromPaper = state.fromPaper;
        if (!fromPaper?.code) {
            return;
        }

        const fromCode = normalizeLineCode(fromPaper.code);
        if (!fromCode) {
            return;
        }

        const rawDetails = fromPaper.adjustmentLine?.details;
        const hasValidDetails = rawDetails
            && !(Array.isArray(rawDetails) && rawDetails.length === 0);

        if (!hasValidDetails) {
            set({fromPaperDetailsSynced: true});
            return;
        }

        const existingDetail = get().getUnifiedDetail(fromCode);
        if (hasNonEmptyUnifiedDetail(existingDetail)) {
            set({fromPaperDetailsSynced: true});
            return;
        }

        let detailsToSet = unwrapDetailsPayload(rawDetails);

        // BS-EQ-AL 发起（已带 commonRow）跳过 flat normalize；其余 flat equity 保持原逻辑
        if (isFlatEquityDetailForCode(fromCode, detailsToSet)) {
            const originalEntry = resolveFsNodeOriginalEntry(fromCode) || 'Cr';
            const fallbackAdj = Number(
                fromPaper.adjustment ?? fromPaper.adjustmentLine?.adjustmentAmount ?? 0,
            );
            const renderPos = fromPaper.entry === 'Dr' || fromPaper.entry === 'Cr'
                ? fromPaper.entry
                : undefined;
            const {detail: normalizedFromDetail} = normalizeDetailForSubmit(
                {
                    perClient: Number(detailsToSet?.perClient) || 0,
                    adjustment: resolveFlatEquityAdjustment(detailsToSet, fallbackAdj),
                },
                originalEntry,
                renderPos,
            );
            detailsToSet = normalizedFromDetail;
        } else if (isLoanFieldSyntheticDetail(detailsToSet, fromCode)) {
            // Bank/Vehicle loan / PL-FC-LI interestSummary：按渲染列同步 detail.adjustment 符号
            const originalEntry = resolveFsNodeOriginalEntry(fromCode) || 'Cr';
            const renderPos = fromPaper.entry === 'Dr' || fromPaper.entry === 'Cr'
                ? fromPaper.entry
                : undefined;
            const fallbackAdj = Number(
                fromPaper.adjustment ?? fromPaper.adjustmentLine?.adjustmentAmount ?? 0,
            );
            const {detail: normalizedLoanDetail} = normalizeDetailForSubmit(
                {...detailsToSet, adjustment: Number(detailsToSet.adjustment) || fallbackAdj},
                originalEntry,
                renderPos,
                fromCode,
            );
            detailsToSet = normalizedLoanDetail;
        } else if (isRentalFieldSyntheticDetail(detailsToSet, fromCode)) {
            // PL-AE-REN field WP→WP only — keep attr marker maps
            const originalEntry = resolveFsNodeOriginalEntry(fromCode) || 'Cr';
            const renderPos = fromPaper.entry === 'Dr' || fromPaper.entry === 'Cr'
                ? fromPaper.entry
                : undefined;
            const fallbackAdj = Number(
                fromPaper.adjustment ?? fromPaper.adjustmentLine?.adjustmentAmount ?? 0,
            );
            const withAmount = rentalFieldAttrMapsOutOfSync(
                detailsToSet,
                Number(detailsToSet.adjustment) || fallbackAdj,
            )
                ? rescaleRentalFieldAttrMapsForAmount(
                    detailsToSet,
                    Number(detailsToSet.adjustment) || fallbackAdj,
                )
                : {...detailsToSet, adjustment: Number(detailsToSet.adjustment) || fallbackAdj};
            const {detail: normalizedRentalDetail} = normalizeDetailForSubmit(
                withAmount,
                originalEntry,
                renderPos,
                fromCode,
            );
            detailsToSet = normalizedRentalDetail;
        } else if (isPpeFieldSyntheticDetail(detailsToSet, fromCode)) {
            // PPE field WP→WP — keep costAdj/addAdj/… + ppeRowId
            const originalEntry = resolveFsNodeOriginalEntry(fromCode) || 'Cr';
            const renderPos = fromPaper.entry === 'Dr' || fromPaper.entry === 'Cr'
                ? fromPaper.entry
                : undefined;
            const fallbackAdj = Number(
                fromPaper.adjustment ?? fromPaper.adjustmentLine?.adjustmentAmount ?? 0,
            );
            const withAmount = rescalePpeFieldAdjKeysForAmount(
                detailsToSet,
                Number(detailsToSet.adjustment) || fallbackAdj,
            );
            const {detail: normalizedPpeDetail} = normalizeDetailForSubmit(
                withAmount,
                originalEntry,
                renderPos,
                fromCode,
            );
            detailsToSet = normalizedPpeDetail;
        } else if (isIaFieldSyntheticDetail(detailsToSet, fromCode)) {
            // IA field WP→WP — slim amorChgAdj/nbvCfAdj + intangibleRowId
            const originalEntry = resolveFsNodeOriginalEntry(fromCode) || 'Cr';
            const renderPos = fromPaper.entry === 'Dr' || fromPaper.entry === 'Cr'
                ? fromPaper.entry
                : undefined;
            const fallbackAdj = Number(
                fromPaper.adjustment ?? fromPaper.adjustmentLine?.adjustmentAmount ?? 0,
            );
            const withAmount = rescaleIaFieldAdjKeysForAmount(
                detailsToSet,
                Number(detailsToSet.adjustment) || fallbackAdj,
                fromCode,
            );
            const {detail: normalizedIaDetail} = normalizeDetailForSubmit(
                withAmount,
                originalEntry,
                renderPos,
                fromCode,
            );
            detailsToSet = normalizedIaDetail;
        }

        get().setUnifiedDetail(fromCode, detailsToSet);
        set({fromPaperDetailsSynced: true});
    },

    resetFromPaperDetailsSync: () => {
        set({fromPaperDetailsSynced: false});
    },

    getCompanionHiddenCodes: () => {
        const state = get();
        const codes = collectLineCodes(state.header?.lines, [state.fromPaper?.code]);
        return resolveCompanionHiddenCodes(codes);
    },

    bumpAssemblySession: () => {
        set((s) => ({assemblySessionId: s.assemblySessionId + 1}));
    },

    resetToPaperAssemblyRegistry: () => {
        set({
            assembledCodes: [],
            toPaperAssemblyStatus: {},
            pendingUserAction: null,
        });
    },

    enqueueToPaperAssembly: async (_codes: string[]) => {
        await get().syncToPaperAssembly();
    },

    clearAssembledCode: (code: string) => {
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return;
        set((state) => {
            const nextStatus = {...state.toPaperAssemblyStatus};
            delete nextStatus[normalizedCode];
            return {
                assembledCodes: filterAssembledCodes(state.assembledCodes, normalizedCode),
                toPaperAssemblyStatus: nextStatus,
            };
        });
    },

    setPendingAppendAdjustment: (code: string, amount: number) => {
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return;
        set((state) => ({
            pendingAppendAdjustments: {
                ...state.pendingAppendAdjustments,
                [normalizedCode]: amount,
            },
        }));
    },

    appendToPaperRecord: (
        code: string,
        balanceAmount: number,
        effectiveEntry: string,
        lineMeta?: any,
        options?: {wpFetchCode?: string},
    ) => {
        const state = get();
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return;
        const currentCodes = state.toPaper?.codes || [];
        if (toPaperContainsCode(currentCodes, normalizedCode)) return;

        const currentEntries = state.toPaper?.entries || {};
        const currentOriginalEntries = state.toPaper?.originalEntries || {};
        const currentLineMetas = state.toPaper?.lineMetas || {};
        const currentWpFetchCodes = {...(state.toPaper?.wpFetchCodes || {})};
        const newEntries = {...currentEntries, [normalizedCode]: effectiveEntry};
        const newOriginalEntries = {...currentOriginalEntries};
        const newLineMetas = {...currentLineMetas};
        const fsOriginalEntry = resolveFsNodeOriginalEntry(normalizedCode, lineMeta?.entry)
            || resolveFsNodeOriginalEntry(normalizedCode);
        if (!newOriginalEntries[normalizedCode] && fsOriginalEntry) {
            newOriginalEntries[normalizedCode] = fsOriginalEntry;
        }
        if (lineMeta) {
            newLineMetas[normalizedCode] = lineMeta;
        }
        const fetchCode = normalizeWpFetchCodeForStore(options?.wpFetchCode || '');
        if (fetchCode) {
            currentWpFetchCodes[normalizedCode] = fetchCode;
        }

        set((s) => {
            const nextComponentDetails = {...s.componentDetails};
            delete nextComponentDetails[normalizedCode];
            const nextStatus = {...s.toPaperAssemblyStatus};
            delete nextStatus[normalizedCode];
            const pendingCode = s.pendingUserAction && 'code' in s.pendingUserAction
                ? normalizeLineCode(s.pendingUserAction.code)
                : '';
            return {
                pendingAppendAdjustments: {
                    ...s.pendingAppendAdjustments,
                    [normalizedCode]: balanceAmount,
                },
                draftToPaperAdjustments: {
                    ...s.draftToPaperAdjustments,
                    [normalizedCode]: balanceAmount,
                },
                toPaper: {
                    ...s.toPaper,
                    codes: [...currentCodes, normalizedCode],
                    entries: newEntries,
                    originalEntries: newOriginalEntries,
                    lineMetas: newLineMetas,
                    wpFetchCodes: currentWpFetchCodes,
                },
                componentDetails: nextComponentDetails,
                toPaperAssemblyStatus: nextStatus,
                // Do not clear pending for other codes (preserves an in-flight non-empty row pick).
                pendingUserAction: pendingCode === normalizedCode ? null : s.pendingUserAction,
                detailRevision: s.detailRevision + 1,
            };
        });
        get().clearAssembledCode(normalizedCode);
        void get().enqueueToPaperAssembly([normalizedCode]);
    },

    setDraftSimpleAdjustment: (code: string, amount: number) => {
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return;
        set((state) => ({
            draftSimpleAdjustments: {
                ...state.draftSimpleAdjustments,
                [normalizedCode]: amount,
            },
            detailRevision: state.detailRevision + 1,
        }));
    },

    setDraftToPaperAdjustment: (code: string, amount: number | undefined) => {
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return;
        set((state) => ({
            draftToPaperAdjustments: {
                ...state.draftToPaperAdjustments,
                [normalizedCode]: amount,
            },
            detailRevision: state.detailRevision + 1,
        }));
    },

    setStaticPositionCodes: (codes: Set<string>) => {
        set({staticPositionCodes: new Set(Array.from(codes || []))});
    },

    setDraftRemark: (remark: string) => {
        set({draftRemark: remark});
    },

    updateLineAdjustment: (code, arrayName, rowIndex, adjustment, subField?) => {
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return;

        const state = get();
        const currentDetail = get().getUnifiedDetail(normalizedCode);
        let updatedDetail: any = null;
        let lineTotal = adjustment;

        // BS-EQ-AL / BS-CL-RND 由 WP 发起：detail 为 commonRow。
        // 若渲染回退为 `_synthetic`（与 commonRow 并存），勿静默 return，改写 commonRow[target]。
        // 仅 CommonWp↔Taxi DEP：同样非 DTD 回写 commonRow.adjustment（不扩大到 IA/AIA）。
        // 其余 CommonWp 节点仍走下方常规数组路径，避免误改 lineTotal / entry。
        const commonRowSyntheticEdit = isCommonRowSyntheticEditDetail(normalizedCode, currentDetail)
            || (
                stateInvolvesItlDepCommonCompanion(state)
                && hasAlCommonRowDetail(currentDetail)
            );

        if (isFlatEquityDetailForCode(normalizedCode, currentDetail) && arrayName === '_synthetic') {
            const base = currentDetail && typeof currentDetail === 'object'
                ? currentDetail
                : {perClient: 0};
            updatedDetail = {...base, adjustment};
            lineTotal = adjustment;
        } else if (commonRowSyntheticEdit && (arrayName === '_synthetic' || arrayName === 'commonRow')) {
            const rows = [...(currentDetail.commonRow as any[])];
            // WHY: _synthetic 默认写 [0] 会把 line adj 污染到非输入行（对齐 mergeDataForAdjust 多行修复）
            let targetIndex = -1;
            if (arrayName === 'commonRow' && rowIndex >= 0 && rowIndex < rows.length) {
                targetIndex = rowIndex;
            } else if (rows.length <= 1) {
                targetIndex = 0;
            } else {
                const hits = rows
                    .map((r, i) => (Math.abs(Number(r?.adjustment) || 0) > 0.01 ? i : -1))
                    .filter((i) => i >= 0);
                targetIndex = hits.length === 1 ? hits[0] : -1;
            }
            if (targetIndex >= 0 && targetIndex < rows.length) {
                rows[targetIndex] = {
                    ...rows[targetIndex],
                    adjustment,
                };
            }
            // targetIndex < 0（多行且多处已有 adj）：不改写各行，仅更新 lineTotal
            updatedDetail = {...currentDetail, commonRow: rows};
            // 单行 AL/RND / Common↔Taxi DEP：以用户输入带符号值为准（勿依赖 scanAllRows，避免与 flat 扫描交叉）
            lineTotal = adjustment;
        } else if (
            isCommonRowSyntheticEditCode(normalizedCode)
            && arrayName === '_synthetic'
            && currentDetail
            && typeof currentDetail === 'object'
            && !Array.isArray(currentDetail)
            && ('adjustment' in currentDetail)
            && !Object.entries(currentDetail).some(([, v]) => Array.isArray(v))
        ) {
            // AL/RND 后端 flat attr（rowClass=commonRow 未 reshape）仍带 top-level adjustment：直接改写
            updatedDetail = {...currentDetail, adjustment};
            lineTotal = adjustment;
        } else if (arrayName === '_synthetic' && isLoanFieldSyntheticDetail(currentDetail, normalizedCode)) {
            // Bank/Vehicle loan field WP→WP + PL-FC-LI interestSummary (loan-field codes only)
            let updatedDetailBase: Record<string, any> = {...currentDetail};
            if (isLoanWpDetail(currentDetail)) {
                const loanKey = Array.isArray(currentDetail.bankLoans) ? 'bankLoans' : 'vehicleLoans';
                const loans = ((currentDetail[loanKey] as any[]) || []).map((loan: any) => {
                    if (!loan || typeof loan !== 'object') return loan;
                    const {adjustments: _adj, ...rest} = loan;
                    return rest;
                });
                updatedDetailBase = {
                    ...updatedDetailBase,
                    [loanKey]: loans,
                };
            }
            updatedDetail = {
                ...updatedDetailBase,
                adjustment,
            };
            lineTotal = adjustment;
        } else if (arrayName === '_synthetic' && isRentalFieldSyntheticDetail(currentDetail, normalizedCode)) {
            // PL-AE-REN field WP→WP：只改金额，按比例缩放既有 marker maps，不改 rowClass 结构
            updatedDetail = rescaleRentalFieldAttrMapsForAmount(currentDetail, adjustment);
            lineTotal = adjustment;
        } else if (
            arrayName === '_synthetic'
            && isPpeFieldFsCode(normalizedCode)
            && isPpeDepScheduleLevelHeader(state.header?.lines)
        ) {
            // Grand Total Depn↔NBV：编辑只改 line entry/amount，移除 details
            updatedDetail = null;
            lineTotal = adjustment;
        } else if (
            arrayName === '_synthetic'
            && (normalizedCode === 'BS-NCA-IA' || normalizedCode === 'PL-AE-AIA')
            && (
                state.iaAmorChgDirectAssembleActive
                || isIaAiaScheduleLevelHeader(state.header?.lines)
            )
        ) {
            // Grand Total Amor↔NBV：编辑只改 line entry/amount，移除 schedule adjustments
            updatedDetail = null;
            lineTotal = adjustment;
        } else if (
            arrayName === '_synthetic'
            && (normalizedCode === 'BS-NCA-ITL' || normalizedCode === 'PL-AE-DEP')
            && (
                state.itlDepnChgDirectAssembleActive
                || isItlDepScheduleLevelHeader(state.header?.lines)
            )
        ) {
            // Grand Total Taxi Depn↔NBV：编辑只改 line entry/amount，移除 schedule adjustments
            updatedDetail = null;
            lineTotal = adjustment;
        } else if (
            arrayName === '_synthetic'
            && isPpeFieldFsCode(normalizedCode)
            && isPpeDepnNbvCrossOnlyFieldHeader(state.header?.lines)
            && (
                isPpeDepnNbvCrossFieldDetail(currentDetail, normalizedCode)
                || Number((currentDetail as any)?.ppeRowId) > 0
                || Math.abs(Number((currentDetail as any)?.nbvCfAdj) || 0) >= 0.001
                || Math.abs(Number((currentDetail as any)?.depnChgAdj) || 0) >= 0.001
            )
        ) {
            // Depn↔NBV-only：非 DTD 切列只改 entry / amount / nbvCfAdj|depnChgAdj（slim）
            const ownedKey = normalizedCode === 'PL-AE-DEP' ? 'depnChgAdj' : 'nbvCfAdj';
            const base = (currentDetail && typeof currentDetail === 'object' && !Array.isArray(currentDetail))
                ? currentDetail as Record<string, any>
                : {};
            const ppeRowId = Number(base.ppeRowId) > 0
                ? Number(base.ppeRowId)
                : collectNestedPpeFieldAdjScalars(base).ppeRowId;
            updatedDetail = buildPpeSlimFieldAdjDetail({
                ppeRowId,
                fieldKey: ownedKey,
                amount: adjustment,
            });
            lineTotal = adjustment;
        } else if (arrayName === '_synthetic' && isPpeFieldSyntheticDetail(currentDetail, normalizedCode)) {
            // PPE field WP→WP：只改金额，同步 costAdj/addAdj/…，保留 ppeRowId
            updatedDetail = rescalePpeFieldAdjKeysForAmount(currentDetail, adjustment);
            lineTotal = adjustment;
        } else if (arrayName === '_synthetic' && isIaFieldSyntheticDetail(currentDetail, normalizedCode)) {
            // IA field / Amor↔NBV slim：非 DTD 切列只改 entry / amount / amorChgAdj|nbvCfAdj
            updatedDetail = rescaleIaFieldAdjKeysForAmount(currentDetail, adjustment, normalizedCode);
            lineTotal = adjustment;
        } else if (
            arrayName === '_synthetic'
            && isItlDepIaFieldTabsNonDtdDetail(currentDetail, normalizedCode)
            && isIaNamedNestShellDetail(currentDetail)
        ) {
            // PL-AE-DEP Taxi nest：非 DTD 只同步 detail.adjustment，保留 intangibleRows（WP panel）
            updatedDetail = {...currentDetail, adjustment};
            lineTotal = adjustment;
        } else if (arrayName === '_synthetic' && normalizedCode === 'BS-EQ-IC') {
            // WP bag：非 DTD 只写 details.adjustment，保留其余字段
            // toPaper 组装后的 object detail（含空 WP 仅 {adjustment}）：同样只改 adjustment
            // fromPaper 原 NO_WP 无 detail：updatedDetail=null，只改 line entry/amount
            if (isIssuedCapitalWpBagDetail(currentDetail)) {
                updatedDetail = {...currentDetail, adjustment};
            } else if (
                toPaperContainsCode(state.toPaper?.codes, normalizedCode)
                && currentDetail
                && typeof currentDetail === 'object'
                && !Array.isArray(currentDetail)
            ) {
                updatedDetail = {...currentDetail, adjustment};
            } else {
                updatedDetail = null;
            }
            lineTotal = adjustment;
        } else if (
            arrayName === '_synthetic'
            && currentDetail
            && typeof currentDetail === 'object'
            && !Array.isArray(currentDetail)
            && stateInvolvesRentalField(state)
            && !isLoanFieldSyntheticDetail(currentDetail, normalizedCode)
            && !isPpeFieldSyntheticDetail(currentDetail, normalizedCode)
            && !isIaFieldSyntheticDetail(currentDetail, normalizedCode)
        ) {
            // REN 配对的另一端：Classify 走 line-level `_synthetic` 时仍可写 top-level adjustment
            // （保留 WP row 数组；仅 REN 配对启用，不影响其它调整）
            updatedDetail = {...currentDetail, adjustment};
            lineTotal = adjustment;
        } else if (arrayName === '_synthetic' && (!currentDetail || typeof currentDetail !== 'object')) {
            updatedDetail = null;
            lineTotal = adjustment;
        } else {
            if (!currentDetail || typeof currentDetail !== 'object') return;
            const rowArray = currentDetail[arrayName];
            if (!Array.isArray(rowArray) || rowIndex >= rowArray.length) return;

            const updatedArray = [...rowArray];
            if (subField === 'mpf') {
                updatedArray[rowIndex] = {
                    ...updatedArray[rowIndex],
                    mpf: {
                        ...updatedArray[rowIndex].mpf,
                        adjustment,
                    },
                };
            } else {
                // TAXR/TAXP toPaper：与 refundable fromPaper 相同，detail 存 flip 后值
                const useSpecialFlipStore = usesDtdAdjustmentPathScoped(normalizedCode, state.specialDtdPathCodes)
                    && (
                        isSameLineCode(state.fromPaper?.code, normalizedCode)
                        || isTaxBfSpecialDtdCode(normalizedCode)
                    );
                const rowAdjToStore = useSpecialFlipStore
                    ? flipDtdDetailRowAdjustment(adjustment)
                    : adjustment;
                updatedArray[rowIndex] = {
                    ...updatedArray[rowIndex],
                    adjustment: rowAdjToStore,
                };
            }
            updatedDetail = {...currentDetail, [arrayName]: updatedArray};
            lineTotal = scanAllRows(updatedDetail).reduce((sum, row) => sum + row.adjustment, 0);
        }

        const isFromCode = isSameLineCode(state.fromPaper?.code, normalizedCode);
        const isToCode = toPaperContainsCode(state.toPaper?.codes, normalizedCode);

        let entry: 'Dr' | 'Cr';
        if (
            usesDtdAdjustmentPathScoped(normalizedCode, state.specialDtdPathCodes)
            && (isFromCode || isTaxBfSpecialDtdCode(normalizedCode))
        ) {
            // specialAdjustFlow fromPaper / TAXR·TAXP toPaper：line 级为带符号值，detail 行内为 flip 后存储
            lineTotal = adjustment;
            entry = resolveAdjustLineEntry(normalizedCode, lineTotal, true, undefined, state.specialDtdPathCodes);
        } else {
            const toOrigEntry = isToCode
                ? (coerceDrCrEntry(lookupToPaperMap(state.toPaper?.originalEntries, normalizedCode))
                    ?? resolveFsNodeOriginalEntry(normalizedCode))
                : undefined;
            entry = resolveAdjustLineEntry(
                normalizedCode,
                lineTotal,
                true,
                toOrigEntry,
                state.specialDtdPathCodes,
            );
        }
        const ppeDepScheduleLineOnly = isPpeFieldFsCode(normalizedCode)
            && isPpeDepScheduleLevelHeader(state.header?.lines);
        const iaAiaScheduleLineOnly = (normalizedCode === 'BS-NCA-IA' || normalizedCode === 'PL-AE-AIA')
            && (state.iaAmorChgDirectAssembleActive || isIaAiaScheduleLevelHeader(state.header?.lines));
        const itlDepScheduleLineOnly = (normalizedCode === 'BS-NCA-ITL' || normalizedCode === 'PL-AE-DEP')
            && (state.itlDepnChgDirectAssembleActive || isItlDepScheduleLevelHeader(state.header?.lines));
        // CommonInvestment NBV C/F fromPaper：与 IA Grand Total 同 line-only，勿把 Tabs WP 壳写回
        const invNbvScheduleLineOnly = isCommonInvestmentCode(normalizedCode)
            && isFromPaperIntentionalLineOnly(state.fromPaper)
            && isSameLineCode(normalizedCode, state.fromPaper?.code);
        const scheduleLineOnly = ppeDepScheduleLineOnly
            || iaAiaScheduleLineOnly
            || itlDepScheduleLineOnly
            || invNbvScheduleLineOnly;
        const adjustmentAmount = (ppeDepScheduleLineOnly || itlDepScheduleLineOnly)
            ? resolvePpeDepScheduleSubmitAmount(entry, lineTotal)
            : Math.abs(lineTotal);
        const {companyId, annualId} = getPaperContextIds(state);

        const clearStaticPosition = (codes: Set<string>) => {
            const next = new Set(codes);
            if (next.has(normalizedCode)) {
                next.delete(normalizedCode);
            }
            return next;
        };

        if (isFromCode) {
            set((s) => {
                const nextComponentDetails = {...s.componentDetails};
                if (scheduleLineOnly) {
                    delete nextComponentDetails[normalizedCode];
                } else if (updatedDetail != null) {
                    nextComponentDetails[normalizedCode] = {
                        code: normalizedCode,
                        detail: updatedDetail,
                        lastUpdated: Date.now(),
                    };
                }
                return {
                fromPaper: s.fromPaper ? {
                    ...s.fromPaper,
                    entry,
                    adjustment: lineTotal,
                    adjustmentLine: s.fromPaper.adjustmentLine ? {
                        ...s.fromPaper.adjustmentLine,
                        entry,
                        adjustment: lineTotal,
                        adjustmentAmount,
                        details: scheduleLineOnly
                            ? []
                            : (updatedDetail != null
                                ? [updatedDetail]
                                : s.fromPaper.adjustmentLine.details),
                    } : s.fromPaper.adjustmentLine,
                } : null,
                componentDetails: nextComponentDetails,
                draftSimpleAdjustments: {
                    ...s.draftSimpleAdjustments,
                    [normalizedCode]: lineTotal,
                },
                staticPositionCodes: clearStaticPosition(s.staticPositionCodes),
                fromPaperUserEdited: true,
                detailRevision: s.detailRevision + 1,
            };
            });
            // 仅 toPaper 含 BS-EQ-IC：发起方编辑后同步 IC（不走通用 recompute，避免影响其它 to）
            const icSync = buildIssuedCapitalToPaperSyncPatch(get(), lineTotal);
            if (icSync) {
                set((s) => ({
                    draftToPaperAdjustments: icSync.draftToPaperAdjustments,
                    toPaper: icSync.toPaper,
                    detailRevision: s.detailRevision + 1,
                }));
            }
        } else if (isToCode) {
            const fsAdjustState = useFsAdjustStore.getState();
            const toLineFromStore = fsAdjustState.getLineByCode(normalizedCode);
            const existingAdjLine = lookupToPaperMap(state.toPaper?.adjustmentLines, normalizedCode);
            const detailForLine = scheduleLineOnly
                ? null
                : (updatedDetail ?? get().getUnifiedDetail(normalizedCode));

            set((s) => {
                const nextComponentDetails = {...s.componentDetails};
                if (scheduleLineOnly) {
                    delete nextComponentDetails[normalizedCode];
                } else if (updatedDetail != null) {
                    nextComponentDetails[normalizedCode] = {
                        code: normalizedCode,
                        detail: updatedDetail,
                        lastUpdated: Date.now(),
                    };
                }
                const nextToDetails = {...(s.toPaper?.details || {})};
                if (scheduleLineOnly) {
                    delete nextToDetails[normalizedCode];
                } else if (updatedDetail != null) {
                    nextToDetails[normalizedCode] = updatedDetail;
                }
                return {
                toPaper: s.toPaper ? {
                    ...s.toPaper,
                    entries: {
                        ...(s.toPaper.entries || {}),
                        [normalizedCode]: entry,
                    },
                    details: nextToDetails,
                    adjustmentLines: {
                        ...(s.toPaper.adjustmentLines || {}),
                        [normalizedCode]: {
                            ...(existingAdjLine || {}),
                            companyId: existingAdjLine?.companyId ?? companyId ?? s.fromPaper?.companyId ?? 0,
                            annualId: existingAdjLine?.annualId ?? annualId ?? s.fromPaper?.annualId ?? 0,
                            scheduleCode: normalizedCode,
                            fsId: existingAdjLine?.fsId
                                ?? toLineFromStore?.fsId
                                ?? lookupToPaperMap(s.toPaper.lineMetas, normalizedCode)?.fsId
                                ?? 0,
                            currency: existingAdjLine?.currency || 'HKD',
                            adjustmentAmount,
                            entry,
                            adjustment: lineTotal,
                            details: scheduleLineOnly
                                ? []
                                : (detailForLine != null ? [detailForLine] : (existingAdjLine?.details || [])),
                            statement: existingAdjLine?.statement
                                ?? (normalizedCode.startsWith('PL') ? 'PL' : 'BS'),
                        },
                    },
                } : null,
                componentDetails: nextComponentDetails,
                draftToPaperAdjustments: {
                    ...s.draftToPaperAdjustments,
                    [normalizedCode]: lineTotal,
                },
                toPaperUserEditedCodes: {
                    ...s.toPaperUserEditedCodes,
                    [normalizedCode]: true,
                },
                staticPositionCodes: clearStaticPosition(s.staticPositionCodes),
                detailRevision: s.detailRevision + 1,
            };
            });
        } else if (updatedDetail) {
            get().setUnifiedDetail(normalizedCode, updatedDetail);
        }
    },

    recomputeToPaperAdjustments: () => {
        const state = get();
        const fromPaper = state.fromPaper;
        const toPaper = state.toPaper;
        if (!fromPaper?.code || !toPaper?.codes?.length) return;

        const currentFromAdjustment = fromPaper.adjustment ?? 0;
        const fsAdjustState = useFsAdjustStore.getState();
        const fromLine = fsAdjustState.getLineByCode(fromPaper.code);
        const fromEntry = fromLine?.entry || fromPaper.entry;

        const newAdjustments: Record<string, number | undefined> = {};
        const newEntries: Record<string, string | null> = {...(toPaper.entries || {})};

        toPaper.codes.forEach((toCode) => {
            const normalizedToCode = normalizeLineCode(toCode);
            if (!normalizedToCode) return;
            // 用户已编辑的 toPaper 保留其 adjustment / entry，不再用配对覆盖
            if (state.toPaperUserEditedCodes[normalizedToCode]) {
                return;
            }
            // Append Another Record 流程：保留差额 adjustment，等待组装写入 detail
            if (lookupToPaperMap(state.pendingAppendAdjustments, normalizedToCode) !== undefined) {
                return;
            }
            // 已有草稿 adjustment（含首次配对写入、用户编辑后）不再被 fromPaper 联动覆盖
            if (lookupToPaperMap(state.draftToPaperAdjustments, normalizedToCode) !== undefined) {
                return;
            }
            // 后端回显的静态行不参与配对重算
            if (state.staticPositionCodes.has(normalizedToCode)) {
                return;
            }

            const backendAdjustmentAmount = lookupToPaperMap(toPaper.adjustmentLines, toCode)?.adjustmentAmount
                ?? lookupToPaperMap(toPaper.adjustmentLines, normalizedToCode)?.adjustmentAmount;
            if (backendAdjustmentAmount !== undefined
                && backendAdjustmentAmount !== null
                && Math.abs(Number(backendAdjustmentAmount)) > 0.001) {
                newAdjustments[normalizedToCode] = Number(backendAdjustmentAmount) || 0;
            } else if (currentFromAdjustment !== undefined) {
                const toLineForEntry = fsAdjustState.getLineByCode(normalizedToCode);
                const originalToEntry = toLineForEntry?.entry
                    || lookupToPaperMap(toPaper.entries, normalizedToCode)
                    || null;
                const resolved = computeToPaperFieldsForAssembly(
                    fromPaper.code,
                    currentFromAdjustment,
                    fromEntry,
                    normalizedToCode,
                    lookupToPaperMap(toPaper.entries, normalizedToCode) || originalToEntry,
                    originalToEntry,
                    state.specialDtdPathCodes,
                );
                newAdjustments[normalizedToCode] = resolved.adjustment;
                newEntries[normalizedToCode] = resolved.entry;
            } else {
                newAdjustments[normalizedToCode] = undefined;
            }
        });

        const entriesChanged = toPaper.codes.some((toCode) => {
            const nk = normalizeLineCode(toCode);
            if (!nk) return false;
            const prev = lookupToPaperMap(toPaper.entries, nk) ?? null;
            const next = newEntries[nk] ?? prev;
            return prev !== next;
        });
        const adjustmentsChanged = toPaper.codes.some((toCode) => {
            const nk = normalizeLineCode(toCode);
            if (!nk) return false;
            const prev = lookupToPaperMap(state.draftToPaperAdjustments, nk);
            const next = newAdjustments[nk];
            return prev !== next;
        });
        if (!entriesChanged && !adjustmentsChanged) {
            return;
        }

        set((s) => ({
            draftToPaperAdjustments: adjustmentsChanged
                ? {...s.draftToPaperAdjustments, ...newAdjustments}
                : s.draftToPaperAdjustments,
            toPaper: entriesChanged && s.toPaper
                ? {...s.toPaper, entries: {...(s.toPaper.entries || {}), ...newEntries}}
                : s.toPaper,
            detailRevision: (entriesChanged || adjustmentsChanged)
                ? s.detailRevision + 1
                : s.detailRevision,
        }));
    },

    consumeAssemblyExtraRowKeys: () => {
        const keys = get().assemblyExtraRowKeys;
        if (keys.length === 0) return [];
        set({assemblyExtraRowKeys: []});
        return keys;
    },

    consumeAssemblyFlatEquityAdjustments: () => {
        const adj = get().assemblyFlatEquityAdjustments;
        if (Object.keys(adj).length === 0) return {};
        set({assemblyFlatEquityAdjustments: {}});
        return adj;
    },

    clearPendingUserAction: () => {
        set({pendingUserAction: null, toPaperAssemblyBusy: false});
    },

    resolveToPaperUserAction: (action: any) => {
        if (!action) return;
        if (action.kind === 'commonInvestmentSelection') {
            get().resolvePendingCommonInvestmentSelection(action.binding || action);
            return;
        }
        if (action.kind === 'arraySelection') {
            get().resolvePendingArraySelection(action.selectedArrayKey || action.codeName || action.code);
            return;
        }
        if (action.kind === 'rowSelection') {
            get().resolvePendingRowSelection(action.binding || action);
        }
    },

    cancelToPaperUserAction: (fallback?: 'default_first_row' | 'initial_factory') => {
        const pending = get().pendingUserAction;
        if (!pending) {
            set({toPaperAssemblyBusy: false});
            return;
        }
        if (fallback === 'initial_factory') {
            const fsLine = useFsAdjustStore.getState().getLineByCode(pending.code);
            const adjustmentAmount = 'adjustmentAmount' in pending
                ? Number(pending.adjustmentAmount) || 0
                : 0;
            // 仅 PL-AE-MPF 需要 wpFetchCode 决定 salary|wages|directorSalary 初始结构
            //（PL-AE-REN createInitialDetailForCode 不消费 wpFetchCode，保持原路径）
            const isMpfLine = String(pending.code || '').trim().toUpperCase() === 'PL-AE-MPF';
            const wpFetchCode = isMpfLine
                ? lookupToPaperMap(get().toPaper?.wpFetchCodes, pending.code)
                : '';
            const detail = createInitialDetailForCode(
                pending.code,
                fsLine,
                adjustmentAmount,
                wpFetchCode ? {wpFetchCode} : undefined,
            );
            get().finishPendingUserSelection(pending.code, detail);
            return;
        }
        get().cancelPendingUserSelection();
    },

    finishPendingUserSelection: (code: string, detail: any, extraRowKeys: string[] = []) => {
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return;
        const state = get();
        const detailToSet = usesDtdAdjustmentPathScoped(state.fromPaper?.code, state.specialDtdPathCodes)
            ? sanitizeSpecialAdjustFlowToPaperDetail(detail)
            : detail;
        get().setUnifiedDetail(normalizedCode, detailToSet);
        set((s) => ({
            pendingUserAction: null,
            toPaperAssemblyBusy: false,
            toPaperAssemblyStatus: {
                ...s.toPaperAssemblyStatus,
                [normalizedCode]: 'ready',
            },
            assemblyExtraRowKeys: extraRowKeys.length > 0
                ? [...s.assemblyExtraRowKeys, ...extraRowKeys]
                : s.assemblyExtraRowKeys,
        }));
    },

    cancelPendingUserSelection: () => {
        const pending = get().pendingUserAction;
        if (!pending) {
            set({toPaperAssemblyBusy: false});
            return;
        }

        if (pending.kind === 'commonInvestmentSelection') {
            const detail = createCommonInvestmentInitialData(
                pending.code,
                pending.perClient,
                pending.adjustmentAmount,
            );
            get().finishPendingUserSelection(pending.code, detail);
            return;
        }

        if (pending.kind === 'arraySelection') {
            const {code, parDetail, adjustmentAmount, perClient} = pending;
            if (parDetail && typeof parDetail === 'object') {
                const defaultArrayName = getMainArrayNameForCode(code);
                const existingArr = parDetail[defaultArrayName] || [];
                const updatedDetail = {
                    ...parDetail,
                    [defaultArrayName]: [...existingArr, {
                        id: existingArr.length + 1,
                        item: '',
                        itemEdit: true,
                        perClient: perClient || 0,
                        adjustment: adjustmentAmount,
                        lastYearFinal: 0,
                    }],
                };
                get().finishPendingUserSelection(
                    code,
                    updatedDetail,
                    [`${code}:${defaultArrayName}:${existingArr.length}`],
                );
            } else {
                const initialDetail = createInitialDetailForCode(code, {amount: perClient}, adjustmentAmount);
                get().finishPendingUserSelection(
                    code,
                    initialDetail,
                    [`${code}:${getMainArrayNameForCode(code)}:0`],
                );
            }
            return;
        }

        if (pending.kind === 'rowSelection') {
            // Prefer first real DB row; Empty record is UI-only and sits last.
            const firstRow = pending.rows.find((r) => !isEmptyRecordSelectionRow(r))
                || pending.rows[0];
            if (firstRow) {
                if (isEmptyRecordSelectionRow(firstRow)) {
                    const wpFetchCode = lookupToPaperMap(get().toPaper?.wpFetchCodes, pending.code);
                    const applied = applyEmptyRecordToPaperDetail({
                        code: pending.code,
                        parDetail: pending.parDetail,
                        adjustmentAmount: pending.adjustmentAmount,
                        arrayName: firstRow.arrayName,
                        subField: firstRow.subField,
                        wpFetchCode,
                    });
                    get().finishPendingUserSelection(
                        pending.code,
                        applied.detail,
                        applied.extraRowKeys,
                    );
                    return;
                }
                const isRen = isPlAeRenCode(pending.code);
                // 仅 FsSelectionDrawer PPE 分类路径（wpFetchCodes 为 xxxRow）才走 applyPpe
                const ppeCategory = lookupToPaperMap(get().toPaper?.wpFetchCodes, pending.code);
                const isPpeCategoryPath = isPpeOrDepToPaperCode(pending.code)
                    && isPpeCategoryFetchCode(ppeCategory);
                const isIaOrAiaPath = isIaOrAiaToPaperCode(pending.code)
                    || isItlTaxiDepToPaper(pending.code, ppeCategory)
                    || isCommonInvestmentCode(pending.code);
                const updatedDetail = isPpeCategoryPath
                    ? applyPpeToPaperRowDetail(
                        pending.parDetail,
                        firstRow.arrayName,
                        firstRow.rowIndex,
                        pending.adjustmentAmount,
                    )
                    : isIaOrAiaPath
                        ? applyIaToPaperRowDetail(
                            pending.parDetail,
                            firstRow.rowIndex,
                            pending.adjustmentAmount,
                            pending.code,
                        )
                    : applyToPaperRowDetail(
                        pending.parDetail,
                        pending.code,
                        firstRow.arrayName,
                        firstRow.rowIndex,
                        pending.adjustmentAmount,
                        isRen ? {selectedRow: firstRow} : undefined,
                    );
                // 仅 PL-AE-MPF：extraRowKeys 带 :mpf，供 Dr/Cr 渲染 mpf.adjustment
                // PL-AE-REN submenu：带 :rentalMonth|:otherExpense
                const codeUpper = String(pending.code || '').trim().toUpperCase();
                const extraKey = codeUpper === 'PL-AE-MPF'
                    ? `${pending.code}:${firstRow.arrayName}:${firstRow.rowIndex}:mpf`
                    : isRen && firstRow.subField
                        ? `${pending.code}:${firstRow.arrayName}:${firstRow.rowIndex}:${firstRow.subField}`
                        : `${pending.code}:${firstRow.arrayName}:${firstRow.rowIndex}`;
                get().finishPendingUserSelection(
                    pending.code,
                    updatedDetail,
                    [extraKey],
                );
            } else {
                get().clearPendingUserAction();
            }
            return;
        }

        get().clearPendingUserAction();
    },

    resolvePendingCommonInvestmentSelection: (binding) => {
        const pending = get().pendingUserAction;
        if (!pending || pending.kind !== 'commonInvestmentSelection') return;

        const options = getCommonInvestmentRowOptions(pending.code);
        const selectedOption = findCommonInvestmentOptionByBinding(options, binding);
        if (!selectedOption) return;

        const detail = createCommonInvestmentInitialDataWithSelection(
            pending.code,
            pending.perClient,
            pending.adjustmentAmount,
            selectedOption,
        );
        get().finishPendingUserSelection(pending.code, detail);
    },

    resolvePendingArraySelection: (selectedArrayKey: string) => {
        const pending = get().pendingUserAction;
        if (!pending || pending.kind !== 'arraySelection') return;

        const {code, parDetail, adjustmentAmount, perClient} = pending;

        if (parDetail && typeof parDetail === 'object') {
            const existingArr = parDetail[selectedArrayKey] || [];
            const updatedDetail = {
                ...parDetail,
                [selectedArrayKey]: [...existingArr, {
                    id: existingArr.length + 1,
                    item: '',
                    itemEdit: true,
                    perClient: perClient || 0,
                    adjustment: adjustmentAmount,
                    lastYearFinal: 0,
                }],
            };
            get().finishPendingUserSelection(
                code,
                updatedDetail,
                [`${code}:${selectedArrayKey}:${existingArr.length}`],
            );
            return;
        }

        const initialDetail = createInitialDetailForCode(code, {amount: perClient}, adjustmentAmount);
        const defaultArrayName = getMainArrayNameForCode(code);
        if (defaultArrayName !== selectedArrayKey && initialDetail && typeof initialDetail === 'object') {
            const defaultRows = initialDetail[defaultArrayName] || [];
            initialDetail[defaultArrayName] = [];
            initialDetail[selectedArrayKey] = defaultRows;
        }
        get().finishPendingUserSelection(
            code,
            initialDetail,
            [`${code}:${selectedArrayKey}:0`],
        );
    },

    resolvePendingRowSelection: (binding) => {
        const pending = get().pendingUserAction;
        if (!pending || pending.kind !== 'rowSelection') return;

        const selectedIdx = binding.rowIndex ?? 0;
        const selectedRow = pending.rows[selectedIdx];
        if (!selectedRow) return;

        const ppeCategory = lookupToPaperMap(get().toPaper?.wpFetchCodes, pending.code);

        if (isEmptyRecordSelectionRow(selectedRow)) {
            const applied = applyEmptyRecordToPaperDetail({
                code: pending.code,
                parDetail: pending.parDetail,
                adjustmentAmount: pending.adjustmentAmount,
                arrayName: selectedRow.arrayName,
                subField: selectedRow.subField,
                wpFetchCode: ppeCategory,
            });
            if (isTaxBfSpecialDtdCode(pending.code)) {
                get().registerSpecialPathCode(pending.code);
            }
            get().finishPendingUserSelection(
                pending.code,
                applied.detail,
                applied.extraRowKeys,
            );
            return;
        }

        const isRen = isPlAeRenCode(pending.code);
        // 仅 FsSelectionDrawer PPE 分类路径（wpFetchCodes 为 xxxRow）才走 applyPpe
        const isPpeCategoryPath = isPpeOrDepToPaperCode(pending.code)
            && isPpeCategoryFetchCode(ppeCategory);
        const isIaOrAiaPath = isIaOrAiaToPaperCode(pending.code)
            || isItlTaxiDepToPaper(pending.code, ppeCategory)
            || isCommonInvestmentCode(pending.code);
        const updatedDetail = isPpeCategoryPath
            ? applyPpeToPaperRowDetail(
                pending.parDetail,
                selectedRow.arrayName,
                selectedRow.rowIndex,
                pending.adjustmentAmount,
            )
            : isIaOrAiaPath
                ? applyIaToPaperRowDetail(
                    pending.parDetail,
                    selectedRow.rowIndex,
                    pending.adjustmentAmount,
                    pending.code,
                )
            : applyToPaperRowDetail(
                pending.parDetail,
                pending.code,
                selectedRow.arrayName,
                selectedRow.rowIndex,
                pending.adjustmentAmount,
                isRen ? {selectedRow} : undefined,
            );
        // TAXP / TAXR toPaper：注册 scoped special，entry/adjustment 与 TaxWp 节点一致
        if (isTaxBfSpecialDtdCode(pending.code)) {
            get().registerSpecialPathCode(pending.code);
        }
        // 仅 PL-AE-MPF：extraRowKeys 带 :mpf，供 Dr/Cr 渲染 mpf.adjustment
        // PL-AE-REN submenu：带 :rentalMonth|:otherExpense
        const codeUpper = String(pending.code || '').trim().toUpperCase();
        const extraKey = codeUpper === 'PL-AE-MPF'
            ? `${pending.code}:${selectedRow.arrayName}:${selectedRow.rowIndex}:mpf`
            : isRen && selectedRow.subField
                ? `${pending.code}:${selectedRow.arrayName}:${selectedRow.rowIndex}:${selectedRow.subField}`
                : `${pending.code}:${selectedRow.arrayName}:${selectedRow.rowIndex}`;
        get().finishPendingUserSelection(
            pending.code,
            updatedDetail,
            [extraKey],
        );
    },

    commitAssemblyApplyResult: (code: string, applyResult: ReturnType<typeof applyToPaperAssemblyOutcome>, noDetailAppendAdj?: number) => {
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return;

        if (applyResult.type === 'none') {
            set((s) => ({
                toPaperAssemblyStatus: {...s.toPaperAssemblyStatus, [normalizedCode]: 'ready'},
            }));
            return;
        }

        if (applyResult.type === 'removed') {
            get().removeToPaper(applyResult.code);
            get().clearAssembledCode(normalizedCode);
            set({
                pendingUserAction: {kind: 'noDirectorConfirm', code: normalizedCode},
                toPaperAssemblyBusy: false,
            });
            return;
        }

        if (applyResult.type === 'pending') {
            // 仅 PL-AE-MPF 源为空：移除 toPaper 并结束 busy；其他 pending（选行/选数组等）保持原行为
            if (applyResult.action.kind === 'mpfSourceEmptyConfirm') {
                get().removeToPaper(applyResult.action.code);
                get().clearAssembledCode(normalizedCode);
                set({
                    pendingUserAction: applyResult.action,
                    toPaperAssemblyStatus: {
                        ...get().toPaperAssemblyStatus,
                        [normalizedCode]: 'pending_user',
                    },
                    toPaperAssemblyBusy: false,
                });
                return;
            }
            set((s) => ({
                pendingUserAction: applyResult.action,
                toPaperAssemblyStatus: {...s.toPaperAssemblyStatus, [normalizedCode]: 'pending_user'},
            }));
            return;
        }

        // Parallel assemble race: do not let a later ready wipe an active rowSelection pending.
        const activePending = get().pendingUserAction;
        if (
            activePending
            && activePending.kind === 'rowSelection'
            && normalizeLineCode(activePending.code) === normalizedCode
        ) {
            set((s) => ({
                toPaperAssemblyStatus: {...s.toPaperAssemblyStatus, [normalizedCode]: 'pending_user'},
            }));
            return;
        }

        if (applyResult.detail !== undefined) {
            const fromCode = get().fromPaper?.code;
            const detailToSet = usesDtdAdjustmentPathScoped(fromCode, get().specialDtdPathCodes)
                ? sanitizeSpecialAdjustFlowToPaperDetail(applyResult.detail)
                : applyResult.detail;
            get().setUnifiedDetail(normalizedCode, detailToSet);
        }

        set((s) => {
            const nextFlat = {...s.assemblyFlatEquityAdjustments};
            if (applyResult.flatEquityAdjustment !== undefined) {
                nextFlat[normalizedCode] = applyResult.flatEquityAdjustment;
            }
            if (noDetailAppendAdj !== undefined) {
                nextFlat[normalizedCode] = noDetailAppendAdj;
            }
            return {
                pendingUserAction: null,
                toPaperAssemblyStatus: {...s.toPaperAssemblyStatus, [normalizedCode]: 'ready'},
                assemblyExtraRowKeys: applyResult.extraRowKeys?.length
                    ? [...s.assemblyExtraRowKeys, ...applyResult.extraRowKeys]
                    : s.assemblyExtraRowKeys,
                assemblyFlatEquityAdjustments: nextFlat,
            };
        });
    },

    fetchAndAssembleToPaper: async (code: string) => {
        const state = get();
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return;

        const fromPaper = state.fromPaper;
        const toPaper = state.toPaper;
        const {companyId, annualId} = getPaperContextIds(state);
        if (!fromPaper || !companyId || !annualId) return;

        const capturedSession = state.assemblySessionId;

        set((s) => ({
            toPaperAssemblyBusy: true,
            toPaperAssemblyStatus: {
                ...s.toPaperAssemblyStatus,
                [normalizedCode]: 'loading',
            },
        }));

        const fsAdjustState = useFsAdjustStore.getState();
        const fromLine = fsAdjustState.getLineByCode(fromPaper.code || '');
        const fromEntry = fromLine?.entry || fromPaper.entry || null;
        const fromAdjustment = fromPaper.adjustment ?? 0;
        const toLine = fsAdjustState.getLineByCode(normalizedCode);
        let toEntry: string | null = toPaper?.entries?.[normalizedCode] || toLine?.entry || null;

        const pendingAppendAdj = lookupToPaperMap(state.pendingAppendAdjustments, normalizedCode);
        const draftAdj = lookupToPaperMap(state.draftToPaperAdjustments, normalizedCode);
        const toCodes = state.toPaper?.codes || [];
        const firstToCode = toCodes.length > 0 ? normalizeLineCode(toCodes[0]) : '';
        const isAdditionalTo = !!firstToCode
            && normalizedCode !== firstToCode
            && toPaperContainsCode(toCodes, normalizedCode);
        // 附加 to（Append）优先 pending；pending 已消费时回退 draft line 级 adjustment
        const appendAdjustment = pendingAppendAdj
            ?? (isAdditionalTo && draftAdj !== undefined ? draftAdj : undefined);
        const consumedAppendAdj = appendAdjustment;
        const isAppendFlow = appendAdjustment !== undefined;
        const storedAppendEntry = lookupToPaperMap(toPaper?.entries, normalizedCode);
        const appendOrigEntry = lookupToPaperMap(toPaper?.originalEntries, normalizedCode)
            ?? resolveFsNodeOriginalEntry(normalizedCode);
        const calcFields = isAppendFlow
            ? {
                entry: storedAppendEntry === 'Dr' || storedAppendEntry === 'Cr'
                    ? storedAppendEntry
                    : resolveAppendEffectiveEntry(
                        normalizedCode,
                        appendAdjustment!,
                        appendOrigEntry,
                    ),
                adjustment: appendAdjustment!,
            }
            : computeToPaperFieldsForAssembly(
                fromPaper.code,
                fromAdjustment,
                fromEntry,
                normalizedCode,
                toEntry,
                toLine?.entry,
                state.specialDtdPathCodes,
            );

        // 仅在首次组装前写入配对值；Append 流程强制写入差额 entry/adjustment
        const assemblyReady = state.toPaperAssemblyStatus[normalizedCode] === 'ready';
        const hasExistingDetail = hasNonEmptyUnifiedDetail(get().getUnifiedDetail(normalizedCode));
        if (isAppendFlow || !assemblyReady || !hasExistingDetail) {
            get().updateToPaperEntry(normalizedCode, calcFields.entry);
            get().setDraftToPaperAdjustment(normalizedCode, calcFields.adjustment);
        }
        toEntry = calcFields.entry;

        if (isAppendFlow) {
            const nextPending = {...get().pendingAppendAdjustments};
            delete nextPending[normalizedCode];
            for (const key of Object.keys(nextPending)) {
                if (normalizeLineCode(key) === normalizedCode) {
                    delete nextPending[key];
                }
            }
            set({pendingAppendAdjustments: nextPending});
        }

        const wpFetchCode = lookupToPaperMap(state.toPaper?.wpFetchCodes, normalizedCode);
        const fromDetailForCross = fromPaper?.code
            ? get().getUnifiedDetail(fromPaper.code)
            : null;
        const crossSlimOutcome = tryBuildPpeDepnNbvCrossToPaperResult({
            toCode: normalizedCode,
            calcAdj: calcFields.adjustment,
            wpFetchCode,
            fromCode: fromPaper?.code,
            fromDetail: fromDetailForCross,
            allowBlankDepToPpe: state.ppeDepnChgDirectAssembleActive,
        });
        // Amor↔NBV / ITL Depn↔NBV：原 slim；另仅 Total NBV schedule（IA/ITL + blank details）
        const iaCrossOutcome = (
            !crossSlimOutcome
            && (
                isIaAmorNbvCrossFieldDetail(fromDetailForCross, fromPaper?.code)
                || isIaScheduleLevelNbvOrAmorContext(fromPaper?.code, fromDetailForCross)
            )
        )
            ? tryBuildIaAmorNbvCrossToPaperResult({
                toCode: normalizedCode,
                calcAdj: calcFields.adjustment,
                fromCode: fromPaper?.code,
                fromDetail: fromDetailForCross,
                allowBlankAiaToIa: state.iaAmorChgDirectAssembleActive,
                allowBlankDepToItl: state.itlDepnChgDirectAssembleActive,
            })
            : null;
        // MULTI：按 Drawer 选定 / initiator / bridge YOA 拉 toPaper WP（BankLoan 等 bs-multi）
        const assembleYoa = resolveAdjAssembleYearOfAssessment({
            targetCode: normalizedCode,
            spanType: getActiveWpSpanType(),
            bridgeYoa: getActiveWpYearOfAssessment(),
            periodLock: get().periodLock,
        });
        const wpGetOpts = assembleYoa ? {yearOfAssessment: assembleYoa} : undefined;

        // IA/AIA/ITL / CommonInvestment toPaper；或 PL-AE-DEP + Taxi License submenu
        let iaOutcome: ReturnType<typeof tryBuildIaToPaperWpFetchResult> = null;
        const isItlDepPath = isItlTaxiDepToPaper(normalizedCode, wpFetchCode);
        const isInvToPaper = isCommonInvestmentCode(normalizedCode);
        if (!crossSlimOutcome && !iaCrossOutcome
            && (isIaOrAiaToPaperCode(normalizedCode) || isItlDepPath || isInvToPaper)) {
            const codeUpper = String(normalizedCode || '').toUpperCase();
            const fetchWpCode = (
                codeUpper === 'BS-NCA-ITL' || isItlDepPath
            ) ? 'BS-NCA-ITL'
                : isInvToPaper
                    ? normalizedCode
                    : 'BS-NCA-IA';
            let iaWpData: any[] | null = null;
            try {
                iaWpData = await fsAdjustState.getWpDetail(
                    companyId,
                    annualId,
                    fetchWpCode,
                    wpGetOpts,
                );
            } catch {
                iaWpData = null;
            }
            iaOutcome = tryBuildIaToPaperWpFetchResult({
                code: normalizedCode,
                calcAdj: calcFields.adjustment,
                detail: unwrapWorkingPaperDetail(iaWpData) || {},
                wpFetchCode,
            });
        }
        // BS-EQ-IC toPaper（其它组件→IC）：拉 WP bag，仅顶层 adjustment；不走 shared resolve（避免绑股东行）
        let icOutcome: ReturnType<typeof tryBuildIcToPaperWpFetchResult> = null;
        if (!crossSlimOutcome && !iaCrossOutcome && !iaOutcome && isIssuedCapitalToPaperCode(normalizedCode)) {
            let icWpData: any[] | null = null;
            try {
                icWpData = await fsAdjustState.getWpDetail(
                    companyId,
                    annualId,
                    'BS-EQ-IC',
                    wpGetOpts,
                );
            } catch {
                icWpData = null;
            }
            icOutcome = tryBuildIcToPaperWpFetchResult({
                code: normalizedCode,
                calcAdj: calcFields.adjustment,
                detail: unwrapWorkingPaperDetail(icWpData) || {},
            });
        }
        // 其它组件 → BS-NCA-PPE 根节点：同 Grand Total NBV，line-only，不弹 RowSelection
        const inboundPpeRootOutcome = (
            !crossSlimOutcome && !iaCrossOutcome && !iaOutcome && !icOutcome
        )
            ? tryBuildInboundPpeRootToPaperResult({
                toCode: normalizedCode,
                fromCode: fromPaper?.code,
                wpFetchCode,
            })
            : null;
        // PL-AE-REN 无 submenu：拉真实 WP 保留 rows，仅挂 top-level adjustment（禁止 invent 空壳冲库）
        let plainRenFieldOutcome: ReturnType<typeof tryBuildPlainRenFieldToPaperResult> = null;
        if (
            !crossSlimOutcome && !iaCrossOutcome && !iaOutcome && !icOutcome && !inboundPpeRootOutcome
            && isPlAeRenCode(normalizedCode)
            && !isPlAeRenSubmenuFetchCode(wpFetchCode)
        ) {
            let renWpData: any[] | null = null;
            try {
                renWpData = await fsAdjustState.getWpDetail(
                    companyId,
                    annualId,
                    'PL-AE-REN',
                    wpGetOpts,
                );
            } catch {
                renWpData = null;
            }
            plainRenFieldOutcome = tryBuildPlainRenFieldToPaperResult({
                toCode: normalizedCode,
                calcAdj: calcFields.adjustment,
                wpFetchCode,
                toLine,
                perClient: Number(toLine?.openingAmount ?? 0) || 0,
                wpDetail: unwrapWorkingPaperDetail(renWpData) || {},
            });
        }
        const wpOutcome = crossSlimOutcome
            ?? iaCrossOutcome
            ?? iaOutcome
            ?? icOutcome
            ?? inboundPpeRootOutcome
            ?? plainRenFieldOutcome
            ?? await fetchToPaperWpOutcome(
                companyId,
                annualId,
                normalizedCode,
                calcFields.adjustment,
                {
                    ...(wpFetchCode ? {wpFetchCode} : {}),
                    ...(assembleYoa ? {yearOfAssessment: assembleYoa} : {}),
                },
            );

        if (get().assemblySessionId !== capturedSession) {
            set({toPaperAssemblyBusy: false});
            return;
        }

        const applyResult = applyToPaperAssemblyOutcome(wpOutcome);
        get().commitAssemblyApplyResult(
            normalizedCode,
            applyResult,
            consumedAppendAdj,
        );

        if (consumedAppendAdj !== undefined) {
            set((s) => ({
                toPaperAssemblyBusy: false,
                toPaperUserEditedCodes: {
                    ...s.toPaperUserEditedCodes,
                    [normalizedCode]: true,
                },
                detailRevision: s.detailRevision + 1,
            }));
        } else {
            set({toPaperAssemblyBusy: false});
        }
    },

    verifyToPaperDetail: async (code: string) => {
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return;

        const {companyId, annualId} = getPaperContextIds(get());
        if (!companyId || !annualId) return;

        const capturedSession = get().assemblySessionId;
        const existingDetail = get().getUnifiedDetail(normalizedCode);
        // Only skip verify for Depn↔NBV slim toPaper (ppeRowId + opposite field); category/full WP unchanged
        const fromCodeForVerify = get().fromPaper?.code;
        const fromDetailForVerify = fromCodeForVerify
            ? get().getUnifiedDetail(fromCodeForVerify)
            : null;
        if (
            get().ppeDepnChgDirectAssembleActive
            && normalizedCode === 'BS-NCA-PPE'
        ) {
            set((s) => ({
                toPaperAssemblyStatus: {...s.toPaperAssemblyStatus, [normalizedCode]: 'ready'},
            }));
            return;
        }
        // 其它组件 → PPE 根节点：保持 line-only，勿 verify 拉回完整 WP shell
        if (
            isInboundPpeRootLineOnlyToPaper(
                normalizedCode,
                fromCodeForVerify,
                lookupToPaperMap(get().toPaper?.wpFetchCodes, normalizedCode),
            )
        ) {
            set((s) => ({
                toPaperAssemblyStatus: {...s.toPaperAssemblyStatus, [normalizedCode]: 'ready'},
            }));
            return;
        }
        if (
            get().itlDepnChgDirectAssembleActive
            && normalizedCode === 'BS-NCA-ITL'
        ) {
            set((s) => ({
                toPaperAssemblyStatus: {...s.toPaperAssemblyStatus, [normalizedCode]: 'ready'},
            }));
            return;
        }
        if (
            isPpeDepnNbvCrossFieldDetail(existingDetail, normalizedCode)
            && isPpeDepnNbvCrossFieldDetail(fromDetailForVerify, fromCodeForVerify)
        ) {
            set((s) => ({
                toPaperAssemblyStatus: {...s.toPaperAssemblyStatus, [normalizedCode]: 'ready'},
            }));
            return;
        }
        if (
            isIaAmorNbvCrossFieldDetail(existingDetail, normalizedCode)
            && isIaAmorNbvCrossFieldDetail(fromDetailForVerify, fromCodeForVerify)
        ) {
            set((s) => ({
                toPaperAssemblyStatus: {...s.toPaperAssemblyStatus, [normalizedCode]: 'ready'},
            }));
            return;
        }
        // 仅 PL-AE-MPF：verify 时按二级菜单 wpFetchCode 拉取
        // PL-AE-REN plain field shell：跳过 verify（勿拉回完整 WP 覆盖 `{rows, adjustment}`）
        // PL-AE-REN：仅遗留 rental|other submenu 才带 wpFetchCode 走 verify
        // PPE / DEP：分类 xxxRow discriminator 才带 wpFetchCode
        const isMpfLine = normalizedCode === 'PL-AE-MPF';
        const renFetch = isPlAeRenCode(normalizedCode)
            ? lookupToPaperMap(get().toPaper?.wpFetchCodes, normalizedCode)
            : '';
        if (isPlAeRenCode(normalizedCode) && !isPlAeRenSubmenuFetchCode(renFetch)) {
            set((s) => ({
                toPaperAssemblyStatus: {...s.toPaperAssemblyStatus, [normalizedCode]: 'ready'},
            }));
            return;
        }
        const ppeFetch = isPpeOrDepToPaperCode(normalizedCode)
            ? lookupToPaperMap(get().toPaper?.wpFetchCodes, normalizedCode)
            : '';
        const wpFetchCode = isMpfLine
            ? lookupToPaperMap(get().toPaper?.wpFetchCodes, normalizedCode)
            : (isPlAeRenSubmenuFetchCode(renFetch)
                ? renFetch
                : (isPpeCategoryFetchCode(ppeFetch) ? ppeFetch : ''));
        const verifyYoa = resolveAdjAssembleYearOfAssessment({
            targetCode: normalizedCode,
            spanType: getActiveWpSpanType(),
            bridgeYoa: getActiveWpYearOfAssessment(),
            periodLock: get().periodLock,
        });
        const stripped = await verifyToPaperWpDetail(
            companyId,
            annualId,
            normalizedCode,
            existingDetail,
            {
                ...(wpFetchCode ? {wpFetchCode} : {}),
                ...(verifyYoa ? {yearOfAssessment: verifyYoa} : {}),
            },
        );

        if (get().assemblySessionId !== capturedSession) return;

        if (stripped) {
            get().setUnifiedDetail(normalizedCode, stripped);
        }

        set((s) => ({
            toPaperAssemblyStatus: {...s.toPaperAssemblyStatus, [normalizedCode]: 'ready'},
        }));
    },

    syncToPaperAssembly: async () => {
        const state = get();
        if (isToPaperAssemblyReadOnlyMode(state.header?.adjustmentMode)) return;

        const fromPaper = state.fromPaper;
        const toPaper = state.toPaper;
        if (!fromPaper || !toPaper?.codes?.length) return;

        const {companyId, annualId} = getPaperContextIds(state);
        if (!companyId || !annualId) return;

        const capturedSession = state.assemblySessionId;
        const assembledSet = new Set(
            (state.assembledCodes || []).map((c) => normalizeLineCode(c)).filter(Boolean),
        );
        const codesToFetch: string[] = [];
        const codesToVerify: string[] = [];
        let nextFlatAdjustments = {...state.assemblyFlatEquityAdjustments};
        let nextPendingAppend = {...state.pendingAppendAdjustments};

        for (const code of toPaper.codes) {
            const normalizedCode = normalizeLineCode(code);
            if (!normalizedCode) continue;

            const pendingAppendAdj = lookupToPaperMap(nextPendingAppend, normalizedCode);
            const hasPendingAppend = pendingAppendAdj !== undefined;

            // PPE Depn skip-Drawer: always assemble empty BS-NCA-PPE (do not verify existing WP shell)
            if (state.ppeDepnChgDirectAssembleActive && normalizedCode === 'BS-NCA-PPE') {
                codesToFetch.push(normalizedCode);
                assembledSet.add(normalizedCode);
                continue;
            }
            if (state.itlDepnChgDirectAssembleActive && normalizedCode === 'BS-NCA-ITL') {
                codesToFetch.push(normalizedCode);
                assembledSet.add(normalizedCode);
                continue;
            }

            if (isCodeAssembled(state.assembledCodes, normalizedCode) && !hasPendingAppend) {
                continue;
            }

            if (isNoWpDetailCode(normalizedCode)) {
                const wpFetchCode = lookupToPaperMap(toPaper.wpFetchCodes, normalizedCode);
                // 遗留 REN submenu / plain REN field shell / PPE 分类 / IA·AIA / IC：需组装
                // plain REN 不经 isNoWpDetailCategoryFetchPath，避免扩大 validate/submit 公共口径
                const needsWpFetch = (
                    isPlAeRenCode(normalizedCode) && isPlAeRenSubmenuFetchCode(wpFetchCode)
                ) || needsPlainRenFieldShellAssemble(normalizedCode, state)
                    || (
                        isPpeOrDepToPaperCode(normalizedCode) && isPpeCategoryFetchCode(wpFetchCode)
                    ) || isIaOrAiaToPaperCode(normalizedCode)
                    || isCommonInvestmentCode(normalizedCode)
                    || isItlTaxiDepToPaper(normalizedCode, wpFetchCode)
                    || isIssuedCapitalToPaperCode(normalizedCode);
                if (!needsWpFetch) {
                    assembledSet.add(normalizedCode);
                    if (hasPendingAppend) {
                        nextFlatAdjustments[normalizedCode] = pendingAppendAdj;
                        delete nextPendingAppend[normalizedCode];
                    }
                    continue;
                }
            }

            // Keep verify for already-bound details (incl. non-empty row picks).
            // Fresh Empty-record prompts only via setToPaper/append clearing assembled state → fetch.
            const existingDetail = get().getUnifiedDetail(normalizedCode);
            if (existingDetail && !hasPendingAppend) {
                assembledSet.add(normalizedCode);
                codesToVerify.push(normalizedCode);
            } else {
                codesToFetch.push(normalizedCode);
                assembledSet.add(normalizedCode);
            }
        }

        if (codesToFetch.length === 0 && codesToVerify.length === 0) {
            if (Object.keys(nextFlatAdjustments).length !== Object.keys(state.assemblyFlatEquityAdjustments).length
                || Object.keys(nextPendingAppend).length !== Object.keys(state.pendingAppendAdjustments).length) {
                set({
                    assemblyFlatEquityAdjustments: nextFlatAdjustments,
                    pendingAppendAdjustments: nextPendingAppend,
                    assembledCodes: Array.from(assembledSet),
                });
            }
            return;
        }

        set({
            assembledCodes: Array.from(assembledSet),
            toPaperAssemblyBusy: true,
            assemblyFlatEquityAdjustments: nextFlatAdjustments,
            pendingAppendAdjustments: nextPendingAppend,
        });

        for (const code of codesToFetch) {
            if (get().assemblySessionId !== capturedSession) {
                set({toPaperAssemblyBusy: false});
                return;
            }
            await get().fetchAndAssembleToPaper(code);
        }

        for (const code of codesToVerify) {
            if (get().assemblySessionId !== capturedSession) {
                set({toPaperAssemblyBusy: false});
                return;
            }
            await get().verifyToPaperDetail(code);
        }

        if (get().assemblySessionId !== capturedSession) {
            set({toPaperAssemblyBusy: false});
            return;
        }

        set({toPaperAssemblyBusy: false});
    },

    getAdjustmentLine: (code: string) => {
        const state = get();
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return null;

        // 1. 从 FromPaper 获取
        const fromPaper = state.fromPaper;
        if (fromPaper && isSameLineCode(fromPaper.code, normalizedCode)) {
            return fromPaper.adjustmentLine;
        }

        // 2. 从 ToPaper 获取
        const toAdjLine = lookupToPaperMap(state.toPaper?.adjustmentLines, normalizedCode);
        if (toAdjLine) {
            return toAdjLine;
        }

        return null;
    },

    // ✅ Header 管理方法实现
    initHeader: (mode: AdjustmentMode) => {
        const state = get();
        if (!state.fromPaper) {
            return;
        }

        // ✅ 保留现有 header 的 id、refNo 和 lines（如果存在且模式匹配）
        const existingHeader = state.header;
        const shouldPreserveHeader = existingHeader &&
            existingHeader.companyId === state.fromPaper.companyId &&
            existingHeader.annualId === state.fromPaper.annualId;

        set({
            currentMode: mode,
            header: {
                // 保留后端返回的 id（正数）
                id: shouldPreserveHeader && existingHeader.id
                    ? existingHeader.id : undefined,
                companyId: state.fromPaper.companyId,
                annualId: state.fromPaper.annualId,
                adjustmentMode: mode,
                // 保留 refNo
                refNo: shouldPreserveHeader ? existingHeader.refNo : undefined,
                status: 0, // 草稿状态
                // 保留现有 lines（包含后端返回的 id）
                lines: shouldPreserveHeader && existingHeader.lines ? existingHeader.lines : [],
            },
        });
    },

    setHeader: (header: AuditAdjustHeader | null) => {
        set({header});
    },

    setCurrentMode: (mode: AdjustmentMode | null) => {
        set({currentMode: mode});
    },

    upsertLine: (line: AuditAdjustLine) => {
        const state = get();
        if (!state.header) {
            return;
        }

        const incomingCode = getBackendLineCode(line);
        const incomingLine: AuditAdjustLine = {
            ...line,
            code: String((line as any)?.code || '').trim() || incomingCode,
        };

        const existingLines = state.header.lines || [];
        const existingIndex = existingLines.findIndex((l: any) => {
            const existingCode = getBackendLineCode(l);
            return existingCode === incomingCode && l.fsId === incomingLine.fsId;
        });

        let newLines: AuditAdjustLine[];
        if (existingIndex >= 0) {
            // 更新现有的 line，保留后端返回的 id 信息
            const existingLine = existingLines[existingIndex];

            // 合并 line，保留正数 id（后端返回的）
            const existingId = (existingLine as any).id;
            const existingHeaderId = (existingLine as any).headerId;

            const mergedLine: AuditAdjustLine = {
                ...incomingLine,
                // 保留 line id：如果 incoming line 没有 id 或 id <= 0，使用 existing line 的 id
                id: incomingLine.id || existingId || undefined,
                // 保留 headerId
                headerId: (incomingLine as any).headerId ||
                    (existingHeaderId || undefined),
            };

            // 合并 details 中的 id 信息
            if (Array.isArray(incomingLine.details) && Array.isArray((existingLine as any).details)) {
                mergedLine.details = incomingLine.details.map((newDetail: any) => {
                    // 查找对应的 existing detail
                    const existingDetail = ((existingLine as any).details as any[]).find((d: any) =>
                        d.component === newDetail.component || d.code === newDetail.code
                    );

                    if (!existingDetail) return newDetail;

                    // 合并 detail，保留正数 id
                    const mergedDetail: any = {...newDetail};

                    // 保留 detail 的 id
                    if ((!newDetail.id || newDetail.id <= 0) && existingDetail.id && existingDetail.id > 0) {
                        mergedDetail.id = existingDetail.id;
                    }

                    // 递归处理 payload 中的数组字段（如 commonRow, bankRow 等）
                    if (newDetail.payload && existingDetail.payload) {
                        mergedDetail.payload = mergePayloadIds(newDetail.payload, existingDetail.payload);
                    }

                    return mergedDetail;
                });
            }

            newLines = [...existingLines];
            newLines[existingIndex] = mergedLine;
        } else {
            // 添加新的 line
            newLines = [...existingLines, incomingLine];
        }

        set({
            header: {
                ...state.header,
                lines: newLines,
            },
        });
    },

    buildDraftHeaderLines: (mode: AdjustmentMode) => {
        const state = get();
        get().initHeader(mode);

        const payload = buildDrCrRenderPayload(get(), (code) => get().getUnifiedDetail(code));
        if (!payload?.lines?.length) return;

        const backendLineIds = Object.keys(state.backendLineIds || {}).length > 0
            ? state.backendLineIds
            : extractBackendLineIds(state.header?.lines);

        // Capture schedule flags before clearing header.lines (is*ScheduleLevelHeader needs them)
        const ppeDepScheduleLineOnly = state.ppeDepnChgDirectAssembleActive
            || isPpeDepScheduleLevelHeader(state.header?.lines)
            || isPpeDepScheduleLevelHeader(payload.lines);
        const itlDepScheduleLineOnly = state.itlDepnChgDirectAssembleActive
            || isItlDepScheduleLevelHeader(state.header?.lines)
            || isItlDepScheduleLevelHeader(payload.lines);
        const iaAiaScheduleLineOnly = state.iaAmorChgDirectAssembleActive
            || isIaAiaScheduleLevelHeader(state.header?.lines)
            || isIaAiaScheduleLevelHeader(payload.lines)
            || (
                isIaAmorNbvCrossOnlyHeader(payload.lines)
                && isScheduleLevelEmptyDetailContext(
                    state.fromPaper?.code,
                    resolveFromDetailForScheduleContext(
                        state.fromPaper,
                        (c) => get().getUnifiedDetail(c),
                    ),
                    (state.toPaper?.codes || [])[0],
                    {
                        allowBlankAiaToIa: state.iaAmorChgDirectAssembleActive,
                        allowBlankDepToPpe: state.ppeDepnChgDirectAssembleActive,
                    allowBlankDepToItl: state.itlDepnChgDirectAssembleActive,
                    },
                )
            );

        if (state.header?.lines && state.header.lines.length > 0) {
            set((s) => ({
                header: s.header ? {...s.header, lines: []} : null,
            }));
        }

        const fromCode = state.fromPaper?.code;
        const fsAdjustState = useFsAdjustStore.getState();

        const resolveLineAdj = (code: string, line: AuditAdjustLine, isFromLine: boolean): number => {
            if (isFromLine) {
                return Number(
                    lookupToPaperMap(state.draftSimpleAdjustments, code)
                    ?? state.fromPaper?.adjustment
                    ?? line.adjustmentAmount
                    ?? 0,
                );
            }
            return Number(
                lookupToPaperMap(state.draftSimpleAdjustments, code)
                ?? lookupToPaperMap(state.draftToPaperAdjustments, code)
                ?? line.adjustmentAmount
                ?? 0,
            );
        };

        for (const line of payload.lines) {
            const code = normalizeLineCode(line.code || '');
            if (!code) continue;
            const isFromLine = isSameLineCode(code, fromCode);

            let detail = unwrapLineDetailForPayload(line.details);
            // NBV / no-attr fromPaper only: keep fromPaper.adjustmentAmount; ignore Tabs WP shell
            if (
                isFromLine
                && isFromPaperIntentionalLineOnly(state.fromPaper)
                && (
                    code === 'BS-NCA-PPE'
                    || code === 'BS-NCA-IA'
                    || code === 'BS-NCA-ITL'
                    || isCommonInvestmentCode(code)
                )
            ) {
                const adj = resolveLineAdj(code, line, isFromLine);
                const submitEntry = line.entry || fsAdjustState.getLineByCode(code)?.entry || 'Dr';
                get().upsertLine({
                    fsId: line.fsId ?? fsAdjustState.getLineByCode(code)?.fsId ?? 0,
                    code,
                    currency: line.currency || 'HKD',
                    // Investment NBV：与 IA Grand Total 同 abs；PPE 仍走 signed helper
                    adjustmentAmount: isCommonInvestmentCode(code)
                        ? Math.abs(Number(adj))
                        : resolvePpeDepScheduleSubmitAmount(submitEntry, Number(adj)),
                    details: [],
                    entry: submitEntry,
                });
                continue;
            }
            // PPE Grand Total Depn skip-Drawer / GET schedule-level：both sides stay line-only (`details: []`).
            // PL-AE-DEP is not NO_WP_DETAIL — without this, empty fromPaper invents commonRow.
            if (
                itlDepScheduleLineOnly
                && (code === 'PL-AE-DEP' || code === 'BS-NCA-ITL')
            ) {
                const adj = resolveLineAdj(code, line, isFromLine);
                const submitEntry = line.entry || fsAdjustState.getLineByCode(code)?.entry || 'Dr';
                get().upsertLine({
                    fsId: line.fsId ?? fsAdjustState.getLineByCode(code)?.fsId ?? 0,
                    code,
                    currency: line.currency || 'HKD',
                    adjustmentAmount: resolvePpeDepScheduleSubmitAmount(submitEntry, Number(adj)),
                    details: [],
                    entry: submitEntry,
                });
                continue;
            }
            if (
                ppeDepScheduleLineOnly
                && (code === 'PL-AE-DEP' || code === 'BS-NCA-PPE')
            ) {
                const adj = resolveLineAdj(code, line, isFromLine);
                const submitEntry = line.entry || fsAdjustState.getLineByCode(code)?.entry || 'Dr';
                get().upsertLine({
                    fsId: line.fsId ?? fsAdjustState.getLineByCode(code)?.fsId ?? 0,
                    code,
                    currency: line.currency || 'HKD',
                    adjustmentAmount: resolvePpeDepScheduleSubmitAmount(submitEntry, Number(adj)),
                    details: [],
                    entry: submitEntry,
                });
                continue;
            }
            // IA Grand Total Amor↔NBV / Amor chg skip-Drawer：both sides stay line-only (`details: []`).
            if (
                iaAiaScheduleLineOnly
                && (code === 'BS-NCA-IA' || code === 'PL-AE-AIA')
            ) {
                const adj = resolveLineAdj(code, line, isFromLine);
                const submitEntry = line.entry || fsAdjustState.getLineByCode(code)?.entry || 'Dr';
                get().upsertLine({
                    fsId: line.fsId ?? fsAdjustState.getLineByCode(code)?.fsId ?? 0,
                    code,
                    currency: line.currency || 'HKD',
                    adjustmentAmount: Math.abs(Number(adj)),
                    details: [],
                    entry: submitEntry,
                });
                continue;
            }
            // 其它组件 → PPE 根节点：同 Grand Total NBV，保持 line-only（勿 invent / 勿提交空壳）
            if (
                isInboundPpeRootLineOnlyToPaper(
                    code,
                    fromCode,
                    lookupToPaperMap(state.toPaper?.wpFetchCodes, code),
                )
                && (!detail || isBlankWpDetail(detail))
            ) {
                const adj = resolveLineAdj(code, line, isFromLine);
                const submitEntry = line.entry || fsAdjustState.getLineByCode(code)?.entry || 'Dr';
                get().upsertLine({
                    fsId: line.fsId ?? fsAdjustState.getLineByCode(code)?.fsId ?? 0,
                    code,
                    currency: line.currency || 'HKD',
                    adjustmentAmount: Math.abs(Number(adj)),
                    details: [],
                    entry: submitEntry,
                });
                continue;
            }

            if (!detail && !isNoWpDetailCode(code)) {
                const adj = resolveLineAdj(code, line, isFromLine);
                if (Math.abs(Number(adj)) > 0.001) {
                    detail = createInitialDetailForCode(code, fsAdjustState.getLineByCode(code), Number(adj));
                }
            }
            // REN/loan → CommonWp：blank/empty commonRow 同样 invent（与 SINGLE 一致）
            if (
                !isFromLine
                && detail
                && isBlankWpDetail(detail)
                && !isNoWpDetailCode(code)
                && getMainArrayNameForCode(code) === 'commonRow'
                && (stateInvolvesFieldWpNonDtdSign(state) || stateInvolvesItlDepCommonCompanion(state))
            ) {
                const adj = resolveLineAdj(code, line, isFromLine);
                if (Math.abs(Number(adj)) > 0.001) {
                    detail = createInitialDetailForCode(code, fsAdjustState.getLineByCode(code), Number(adj));
                }
            }

            if (isNoWpDetailCode(code) && (!detail || isBlankWpDetail(detail))) {
                const fromDetailForSchedule = resolveFromDetailForScheduleContext(
                    state.fromPaper,
                    (c) => get().getUnifiedDetail(c),
                );
                const scheduleEmpty = isScheduleLevelEmptyDetailContext(
                    fromCode,
                    fromDetailForSchedule,
                    code,
                    {
                        allowBlankAiaToIa: state.iaAmorChgDirectAssembleActive,
                        allowBlankDepToPpe: state.ppeDepnChgDirectAssembleActive,
                    allowBlankDepToItl: state.itlDepnChgDirectAssembleActive,
                    },
                );

                // Schedule-level NBV blank → `details: []`
                // Other NO_WP (non-category): also empty details
                // REN/PPE category fetch with real WP shell: recover unified below
                if (!isNoWpDetailCategoryFetchPath(code, get()) || scheduleEmpty) {
                    const adj = resolveLineAdj(code, line, isFromLine);
                    let submitEntry = line.entry || fsAdjustState.getLineByCode(code)?.entry || 'Dr';
                    if (TAX_FS_CODES.has(code) && fromCode && !isSameLineCode(fromCode, code)) {
                        submitEntry = resolveToPaperFieldsFromPairing(fromCode, Number(adj), code, {
                            specialDtdPathCodes: state.specialDtdPathCodes,
                        }).entry;
                    }
                    get().upsertLine({
                        fsId: line.fsId ?? fsAdjustState.getLineByCode(code)?.fsId ?? 0,
                        code,
                        currency: line.currency || 'HKD',
                        adjustmentAmount: Math.abs(Number(adj)),
                        details: [],
                        entry: submitEntry,
                    });
                    continue;
                }
                // 分类路径：再取一次 unified（assembly 刚写入时 payload 可能尚未带上）
                detail = get().getUnifiedDetail(code);
                if (!detail || isBlankWpDetail(detail)) {
                    const adj = resolveLineAdj(code, line, isFromLine);
                    get().upsertLine({
                        fsId: line.fsId ?? fsAdjustState.getLineByCode(code)?.fsId ?? 0,
                        code,
                        currency: line.currency || 'HKD',
                        adjustmentAmount: Math.abs(Number(adj)),
                        details: [],
                        entry: line.entry || fsAdjustState.getLineByCode(code)?.entry || 'Dr',
                    });
                    continue;
                }
            }

            const {detail: normalizedDetail, entry} = normalizeSubmitPayloadLine(
                {...line, details: detail != null ? [detail] : []},
                get(),
            );
            get().processAndAddLine(code, normalizedDetail, payload.annualId, entry);
        }

        // Amor↔NBV pair: if one side assembled amount 0 and the other has a value, mirror abs amount.
        syncIaAmorNbvPairAdjustmentAmounts(get);
        // REN/loan → CommonWp：toPaper.adjustmentAmount 与 from 绝对值对齐
        syncFieldWpCommonPairAdjustmentAmounts(get);

        if (Object.keys(backendLineIds).length > 0) {
            const storeAfterProcess = get();
            if (storeAfterProcess.header?.lines) {
                const mergedLines = mergeBackendLineIds(
                    storeAfterProcess.header.lines,
                    backendLineIds,
                );
                set((s) => ({
                    header: s.header ? {...s.header, lines: mergedLines} : null,
                }));
            }
        }
    },

    validateBeforeSubmit: () => {
        const state = get();
        const componentValidation = get().validateAllComponents();
        const errors: string[] = [...componentValidation.errors];
        const bankFieldErrors: Record<string, Record<number, { bankName?: string; accountNumber?: string }>> = {};

        const allDetails = state.getAllComponentDetails();
        const cashCodes = [state.fromPaper?.code, ...(state.toPaper?.codes || [])].filter((c) => c === 'BS-CA-CASH');
        for (const code of cashCodes) {
            const detail = allDetails[code!];
            if (!detail || !Array.isArray(detail.bankRow)) continue;
            for (let i = 0; i < detail.bankRow.length; i++) {
                const row = detail.bankRow[i];
                if (row.adjustment === 0 && !row.bankName && !row.accountNumber) continue;
                const fieldErr: { bankName?: string; accountNumber?: string } = {};
                if (!String(row.bankName || '').trim()) fieldErr.bankName = 'Name of Bank is required';
                if (!String(row.accountNumber || '').trim()) fieldErr.accountNumber = 'Bank Account No# is required';
                if (fieldErr.bankName || fieldErr.accountNumber) {
                    if (!bankFieldErrors[code!]) bankFieldErrors[code!] = {};
                    bankFieldErrors[code!][i] = fieldErr;
                    errors.push(`${code} bank row ${i + 1} missing fields`);
                }
            }
        }

        return {valid: errors.length === 0, errors, bankFieldErrors};
    },

    prepareSubmitHeader: ({userId, remark, mode = AdjustmentMode.ADJUST_WP_TO_WP}) => {
        const validation = get().validateBeforeSubmit();
        if (!validation.valid) {
            return {
                valid: false as const,
                errors: validation.errors,
                bankFieldErrors: validation.bankFieldErrors,
            };
        }

        get().buildDraftHeaderLines(mode);

        const header = get().buildSubmitHeader(userId);
        if (remark !== undefined) header.remark = remark;

        if (!header.lines || header.lines.length === 0) {
            return {valid: false as const, errors: ['No adjustment lines to submit']};
        }

        return {valid: true as const, header};
    },

    buildSubmitHeader: (userId: number) => {
        const state = get();
        const now = new Date().toISOString();

        const normalizeCodeUpper = (c: any) => String(c || '').trim().toUpperCase();
        const isExplicitInvToPur = isInvExplicitPurchasesToPaper(
            state.fromPaper?.code,
            state.toPaper?.codes,
        );

        // ✅ 创建默认 detail 数据结构（当 details 为空但 adjustmentAmount 非零时使用）
        const buildDefaultDetailForLine = (code: string, adjustmentAmount: number): any => {
            const name = code;
            const baseRow = {
                id: 1,
                item: name,
                itemEdit: true,
                perClient: 0,
                adjustment: adjustmentAmount,
                finalAmount: adjustmentAmount,
                lastYearFinal: 0,
            };

            if (code === 'BS-CL-BOD' || code === 'BS-CA-CASH') {
                // Unique negative key — avoid colliding with other empty bank shells on attr.rowId
                return {
                    bankRow: [{
                        bankId: -1,
                        id: -1,
                        bankName: '',
                        accountNumber: '',
                        perClient: 0,
                        adjustment: adjustmentAmount,
                        lastYearFinal: 0
                    }], commonRow: []
                };
            }
            if (isCommonInvestmentCode(code)) {
                return createCommonInvestmentInitialData(code, 0, adjustmentAmount);
            }
            if (isTurnoverIncomeFsCode(code)) {
                const baseDetail = {
                    salesIncome: [baseRow],
                    transactionTest: [],
                    cutOffThisYearTest: [],
                    cutOffNextYearTest: [],
                };
                // 仅回填 draft 中已有 annualInfo（通常来自 fromPaper SalesIncomeWp），不发明默认值
                return withTurnoverAnnualInfoIfMissing(
                    baseDetail,
                    collectTurnoverAnnualInfoCandidates(
                        get().getUnifiedDetail,
                        state.fromPaper,
                        state.header,
                        code,
                    ),
                    state.fromPaper?.annualId ?? state.header?.annualId,
                );
            }
            if (code === 'PL-COT-PUR') {
                return {purchases: [baseRow], transactionTest: [], cutOffThisYearTest: [], cutOffNextYearTest: []};
            }
            if (code === 'BS-CL-BLS' || code === 'BS-NCL-BLS') {
                return {bankLoans: [{id: 1, perClient: 0, adjustment: adjustmentAmount, lastYearFinal: 0}]};
            }
            if (code === 'PL-FC-LI') {
                return createPlFcLiAdjustmentDetail(adjustmentAmount, {
                    wpFetchCode: lookupToPaperMap(state.toPaper?.wpFetchCodes, code),
                });
            }
            if (code === 'BS-CL-OFL' || code === 'BS-NCL-OFL') {
                return {vehicleLoans: [{id: 1, perClient: 0, adjustment: adjustmentAmount, lastYearFinal: 0}]};
            }
            // PL-AE-REN: line-only — never invent blank rows (backend markAllInvalid would wipe leases)
            if (code === 'PL-AE-REN') {
                return {adjustment: adjustmentAmount};
            }
            if (code === 'PL-OI-II' || code === 'PL-OI-SFG') {
                return {bankInterestIncome: [baseRow], otherInterestIncome: []};
            }
            if (code === 'PL-AE-DON') {
                return {taxDeductible: [baseRow], nonTaxDeductible: []};
            }
            if (code === 'PL-AE-COM') {
                return {commissionPayments: [baseRow]};
            }
            if (code === 'PL-OI-EXG' || code === 'PL-AE-EXL') {
                return {gainExchange: [baseRow], lossExchange: []};
            }
            if (code === 'BS-CA-DIR' || code === 'BS-CL-DTD') {
                return {
                    dueFromToDirRow: [normalizeDueFromToDirRow({
                        code,
                        directorName: name,
                        perClient: 0,
                        adjustment: adjustmentAmount,
                        lastYearFinal: 0,
                    })],
                };
            }
            // DividendWp flat 默认；toPaper commonRow 仅由 resolveToPaper* 写入
            if (code === 'BS-EQ-DIV') {
                return {
                    dividendPaid: 0,
                    dividendCurrency: 'HKD',
                    amountPerEachShare: 0,
                    totalDividendAmount: 0,
                    adjustment: adjustmentAmount,
                };
            }
            if (code === 'BS-EQ-AL') {
                return {perClient: 0, adjustment: adjustmentAmount};
            }
            return {commonRow: [baseRow]};
        };

        // 处理 lines 中 details 的 id：
        // - 后端返回的正整数 id (> 0) 保留
        // - 非后端 id (负数或0) 统一移除（不发送给后端）
        const sourceLines = state.header?.lines || [];
        const hasInvLine = sourceLines.some(
            (l: any) => normalizeCodeUpper(l.code) === 'BS-CA-INV',
        );
        const hasPurLine = sourceLines.some(
            (l: any) => normalizeCodeUpper(l.code) === 'PL-COT-PUR',
        );
        const INV_EMPTY_DETAIL_CODES = new Set(['PL-COT-PUR', 'PL-COT-CI']);
        const PUR_EMPTY_DETAIL_CODES = new Set(['PL-COT-COS']);

        const ppeDepScheduleSubmit = state.ppeDepnChgDirectAssembleActive
            || isPpeDepScheduleLevelHeader(sourceLines);
        const itlDepScheduleSubmit = state.itlDepnChgDirectAssembleActive
            || isItlDepScheduleLevelHeader(sourceLines);
        const iaAiaScheduleSubmit = state.iaAmorChgDirectAssembleActive
            || isIaAiaScheduleLevelHeader(sourceLines);

        const cleanedLines = sourceLines.map(line => {
            const shouldCleanRelatedRows = isTurnoverIncomeFsCode((line as any)?.code);
            const lineCodeEarly = normalizeLineCode(String((line as any)?.code || '').trim());
            // NBV / no-attr fromPaper only: strip Tabs WP shell; keep line.adjustmentAmount
            if (
                isFromPaperIntentionalLineOnly(state.fromPaper)
                && isSameLineCode(lineCodeEarly, state.fromPaper?.code)
                && (
                    lineCodeEarly === 'BS-NCA-PPE'
                    || lineCodeEarly === 'BS-NCA-IA'
                    || lineCodeEarly === 'BS-NCA-ITL'
                    || isCommonInvestmentCode(lineCodeEarly || '')
                )
            ) {
                const submitEntry = (line as any).entry === 'Cr' || (line as any).entry === 'Dr'
                    ? (line as any).entry
                    : ((line as any).entry || 'Dr');
                const signedAdj = Number(
                    (line as any).adjustment ?? (line as any).adjustmentAmount ?? 0,
                );
                const {
                    companyId: _companyId,
                    annualId: _annualId,
                    scheduleName: _scheduleName,
                    openingAmount: _openingAmount,
                    closingAmount: _closingAmount,
                    ...restLine
                } = line as any;
                return {
                    ...restLine,
                    entry: submitEntry,
                    adjustmentAmount: isCommonInvestmentCode(lineCodeEarly || '')
                        ? Math.abs(signedAdj)
                        : resolvePpeDepScheduleSubmitAmount(submitEntry, signedAdj),
                    details: [],
                };
            }
            if (
                itlDepScheduleSubmit
                && (lineCodeEarly === 'PL-AE-DEP' || lineCodeEarly === 'BS-NCA-ITL')
            ) {
                const submitEntry = (line as any).entry === 'Cr' || (line as any).entry === 'Dr'
                    ? (line as any).entry
                    : ((line as any).entry || 'Dr');
                const signedAdj = Number(
                    (line as any).adjustment ?? (line as any).adjustmentAmount ?? 0,
                );
                const {
                    companyId: _companyId,
                    annualId: _annualId,
                    scheduleName: _scheduleName,
                    openingAmount: _openingAmount,
                    closingAmount: _closingAmount,
                    ...restLine
                } = line as any;
                return {
                    ...restLine,
                    entry: submitEntry,
                    adjustmentAmount: resolvePpeDepScheduleSubmitAmount(submitEntry, signedAdj),
                    details: [],
                };
            }
            if (
                ppeDepScheduleSubmit
                && (lineCodeEarly === 'PL-AE-DEP' || lineCodeEarly === 'BS-NCA-PPE')
            ) {
                const submitEntry = (line as any).entry === 'Cr' || (line as any).entry === 'Dr'
                    ? (line as any).entry
                    : ((line as any).entry || 'Dr');
                const signedAdj = Number(
                    (line as any).adjustment ?? (line as any).adjustmentAmount ?? 0,
                );
                const {
                    companyId: _companyId,
                    annualId: _annualId,
                    scheduleName: _scheduleName,
                    openingAmount: _openingAmount,
                    closingAmount: _closingAmount,
                    ...restLine
                } = line as any;
                return {
                    ...restLine,
                    entry: submitEntry,
                    adjustmentAmount: resolvePpeDepScheduleSubmitAmount(submitEntry, signedAdj),
                    details: [],
                };
            }
            if (
                iaAiaScheduleSubmit
                && (lineCodeEarly === 'BS-NCA-IA' || lineCodeEarly === 'PL-AE-AIA')
            ) {
                const submitEntry = (line as any).entry === 'Cr' || (line as any).entry === 'Dr'
                    ? (line as any).entry
                    : ((line as any).entry || 'Dr');
                const signedAdj = Number(
                    (line as any).adjustment ?? (line as any).adjustmentAmount ?? 0,
                );
                const {
                    companyId: _companyId,
                    annualId: _annualId,
                    scheduleName: _scheduleName,
                    openingAmount: _openingAmount,
                    closingAmount: _closingAmount,
                    ...restLine
                } = line as any;
                return {
                    ...restLine,
                    entry: submitEntry,
                    adjustmentAmount: Math.abs(signedAdj),
                    details: [],
                };
            }

            // ✅ 安全兜底：如果 details 为空/null，尝试从 store 获取或创建默认 detail
            if (!line.details || !Array.isArray(line.details) || line.details.length === 0) {
                const lineCode = normalizeLineCode(String((line as any)?.code || '').trim());
                if (lineCode) {
                    // PPE Grand Total Depn skip-Drawer: keep line-only empty details
                    // (do not invent commonRow for PL-AE-DEP / recover WP shell for BS-NCA-PPE)
                    if (
                        state.ppeDepnChgDirectAssembleActive
                        && (lineCode === 'PL-AE-DEP' || lineCode === 'BS-NCA-PPE')
                    ) {
                        return {...line, details: []};
                    }
                    // 其它组件 → PPE 根节点：同 Grand Total NBV，提交仅 adjustLine
                    if (
                        isInboundPpeRootLineOnlyToPaper(
                            lineCode,
                            state.fromPaper?.code,
                            lookupToPaperMap(state.toPaper?.wpFetchCodes, lineCode),
                        )
                    ) {
                        return {...line, details: []};
                    }
                    // IA Amor↔NBV schedule / Amor chg skip-Drawer: keep line-only empty details
                    if (
                        iaAiaScheduleSubmit
                        && (lineCode === 'BS-NCA-IA' || lineCode === 'PL-AE-AIA')
                    ) {
                        return {...line, details: []};
                    }
                    // 普通 NO_WP_DETAIL 保持空；REN/PPE 分类路径允许从 unified 恢复完整 WP
                    // BS-EQ-IC：仅当 store 中是 WP bag（shareholders/…）才恢复；否则保持原 NO_WP 空 details
                    // plain PL-AE-REN field shell：仅恢复 `{rows, adjustment}`，不扩大 category 路径
                    if (isNoWpDetailCode(lineCode) && !isNoWpDetailCategoryFetchPath(lineCode, state)) {
                        if (lineCode === 'BS-EQ-IC') {
                            const recoveredIc = get().getUnifiedDetail(lineCode);
                            if (isIssuedCapitalWpBagDetail(recoveredIc)) {
                                line = {...line, details: [recoveredIc]} as any;
                            } else {
                                return {...line, details: []};
                            }
                        } else if (isPlAeRenCode(lineCode)) {
                            const recoveredRen = get().getUnifiedDetail(lineCode);
                            if (isRentalFieldSyntheticDetail(recoveredRen, lineCode)) {
                                line = {...line, details: [recoveredRen]} as any;
                            } else {
                                return {...line, details: []};
                            }
                        } else {
                            return {...line, details: []};
                        }
                    }

                    const lineCodeUpper = normalizeCodeUpper(lineCode);
                    // 与 dev AdjustTabsDialog submitHeader 二次 map 规则 3/4 对齐
                    if (hasInvLine && INV_EMPTY_DETAIL_CODES.has(lineCodeUpper)) {
                        if (lineCodeUpper === 'PL-COT-PUR' && isExplicitInvToPur) {
                            // INV→PUR 显式配对：允许恢复 Purchases WP details
                        } else {
                            return line;
                        }
                    }
                    if (hasPurLine && PUR_EMPTY_DETAIL_CODES.has(lineCodeUpper)) {
                        return line;
                    }

                    // 规则1：优先从 store 获取统一 detail（后端数据 / PPE 分类组装）
                    let recoveredDetail = get().getUnifiedDetail(lineCode);

                    // 规则2：store 中也没有数据时，根据 code + adjustmentAmount 创建默认 detail
                    // （PPE 分类路径不 invent 空壳，保持由 assembly 写入）
                    if (
                        !recoveredDetail
                        && !isNoWpDetailCode(lineCode)
                    ) {
                        const adjustmentAmount = line.adjustmentAmount || 0;
                        if (Math.abs(adjustmentAmount) > 0.001) {
                            recoveredDetail = buildDefaultDetailForLine(lineCode, adjustmentAmount);
                        }
                    }

                    if (recoveredDetail) {
                        // 将恢复的 detail 包装到 line 中，继续走正常清理流程
                        line = {...line, details: [recoveredDetail]} as any;
                    }
                }

                // 如果仍然没有 details，返回原始 line
                if (!line.details || !Array.isArray(line.details) || line.details.length === 0) {
                    return line;
                }
            }

            const cleanedDetails = line.details.map((detail: any) => {
                if (!detail || typeof detail !== 'object') return detail;

                const lineCode = normalizeLineCode(String((line as any)?.code || '').trim());
                const isPlFcLi = normalizeCodeUpper(lineCode) === 'PL-FC-LI';
                // 仅 PL-FC-LI：提交前确保 details 带 top-level adjustment（供后端建 attr → inAdj）
                let sourceDetail = detail;
                if (isPlFcLi) {
                    sourceDetail = ensurePlFcLiLoanFieldAdjustmentDetail(
                        detail,
                        Number((line as any).adjustmentAmount) || Number(detail.adjustment) || 0,
                        {wpFetchCode: lookupToPaperMap(state.toPaper?.wpFetchCodes, lineCode)},
                    );
                }

                const cleanedDetail: Record<string, any> = {};
                for (const [key, value] of Object.entries(sourceDetail)) {
                    // ✅ 跳过元数据键（SME-FRS / WOKE_DONE / glInvoiceDetails），不发送给后端
                    if (WP_METADATA_ONLY_KEYS.has(key)) {
                        continue;
                    }
                    // 仅 PL-FC-LI：interestSummary 为展示字段，不随 adjustment 提交
                    if (isPlFcLi && key === 'interestSummary') {
                        continue;
                    }
                    // BS-EQ-DIV / BS-EQ-AL: adjustments[] 仅为 WP 展示 VO，不提交
                    if (isFlatEquityAdjustmentCode((line as any)?.code)
                        && (key === 'adjustments' || key === 'mpfAdjustments')) {
                        continue;
                    }
                    if (Array.isArray(value)) {
                        let nextArray: any[] = value;
                        if (shouldCleanRelatedRows && key === 'relatedRows') {
                            nextArray = value.filter((item: any) => {
                                if (!item || typeof item !== 'object') return true;
                                const natureOfTransaction = (item as any).natureOfTransaction;
                                const relationship = (item as any).relationship;
                                const thisYear = (item as any).thisYear;
                                const lastYear = (item as any).lastYear;
                                const allNull = natureOfTransaction == null && relationship == null && thisYear == null && lastYear == null;
                                return !allNull;
                            });

                            // 如果 relatedRows 全部为空对象，则不发送 relatedRows 字段
                            if (nextArray.length === 0) {
                                continue;
                            }
                        }
                        // 处理数组中每个对象的 id 和 adjustments
                        cleanedDetail[key] = nextArray.map((item: any) => {
                            if (item && typeof item === 'object') {
                                const consolidated = consolidateWpRowAdjustments(item, state.header?.id);
                                const {mpfAdjustments: _mpfAdj, adjustments: _adj, ...rest} = consolidated;

                                // 处理 id：非后端 id（负数或0）移除 id 字段
                                if ('id' in rest) {
                                    const id = Number(rest.id);
                                    if (!Number.isFinite(id) || id <= 0) {
                                        const {id: _, ...withoutId} = rest;
                                        return withoutId;
                                    }
                                }
                                return rest;
                            }
                            return item;
                        });
                    } else {
                        cleanedDetail[key] = value;
                    }
                }
                // PL-TURN-SI/SE：提交前若缺 annualInfo，从 fromPaper / 对端 Turnover detail 回填（不发明默认值）
                if (shouldCleanRelatedRows && cleanedDetail.annualInfo == null) {
                    const annualInfo = pickExistingTurnoverAnnualInfo(
                        collectTurnoverAnnualInfoCandidates(
                            get().getUnifiedDetail,
                            state.fromPaper,
                            state.header,
                            lineCode,
                        ),
                        state.fromPaper?.annualId ?? state.header?.annualId,
                    );
                    if (annualInfo) {
                        cleanedDetail.annualInfo = annualInfo;
                    }
                }
                return cleanedDetail;
            });

            // 移除不需要提交的字段
            const {
                companyId: _companyId,
                annualId: _annualId,
                scheduleName: _scheduleName,
                openingAmount: _openingAmount,
                closingAmount: _closingAmount,
                ...restLine
            } = line as any;

            return {...restLine, details: cleanedDetails};
        });

        // Amor↔NBV pair safety: mirror non-zero abs amount onto the empty sibling before INV/PUR hooks
        if (isIaAmorNbvCrossOnlyHeader(cleanedLines)) {
            const findIdx = (code: string) =>
                cleanedLines.findIndex(
                    (l: any) => normalizeCodeUpper(l.code) === code,
                );
            const pairs: Array<[string, string]> = [
                ['BS-NCA-IA', 'PL-AE-AIA'],
                ['BS-NCA-ITL', 'PL-AE-DEP'],
            ];
            for (const [a, b] of pairs) {
                const idxA = findIdx(a);
                const idxB = findIdx(b);
                if (idxA < 0 || idxB < 0) continue;
                const amtA = Math.abs(Number((cleanedLines[idxA] as any).adjustmentAmount) || 0);
                const amtB = Math.abs(Number((cleanedLines[idxB] as any).adjustmentAmount) || 0);
                if (amtA >= 0.001 && amtB < 0.001) {
                    cleanedLines[idxB] = {
                        ...cleanedLines[idxB],
                        adjustmentAmount: amtA,
                    } as any;
                } else if (amtB >= 0.001 && amtA < 0.001) {
                    cleanedLines[idxA] = {
                        ...cleanedLines[idxA],
                        adjustmentAmount: amtB,
                    } as any;
                }
            }
        }

        // BS-CA-INV 特殊处理：自动追加/更新 PL-COT-PUR 和 PL-COT-CI 两条 line，details 强制为空
        const invLine = cleanedLines.find((l: any) => normalizeCodeUpper(l.code) === 'BS-CA-INV');
        if (invLine) {
            const invAmount = (invLine as any).adjustmentAmount || 0;
            const invEntry = (invLine as any).entry || 'Dr';
            const oppositeEntry = invEntry === 'Dr' ? 'Cr' : 'Dr';
            const fsAdjustState = useFsAdjustStore.getState();

            // 1. PL-COT-PUR：伴生 line 与 BS-CA-INV 相同 entry、details 为空；
            //    若用户显式选择 Purchases 作为 toPaper，保留其 WP details 与配对 entry。
            const existingPurIdx = cleanedLines.findIndex((l: any) => normalizeCodeUpper(l.code) === 'PL-COT-PUR');
            if (existingPurIdx >= 0) {
                if (!isExplicitInvToPur) {
                    cleanedLines[existingPurIdx] = {
                        ...cleanedLines[existingPurIdx],
                        adjustmentAmount: invAmount,
                        details: [],
                        entry: invEntry,
                    } as any;
                }
            } else if (!isExplicitInvToPur) {
                const purFsLine = fsAdjustState.getLineByCode('PL-COT-PUR');
                if (purFsLine) {
                    cleanedLines.push({
                        fsId: purFsLine.fsId || 0,
                        code: 'PL-COT-PUR',
                        currency: 'HKD',
                        adjustmentAmount: invAmount,
                        details: [],
                        entry: invEntry,
                    } as any);
                }
            }

            // 2. PL-COT-CI：与 BS-CA-INV 相反 entry，details 强制为空
            const existingCiIdx = cleanedLines.findIndex((l: any) => normalizeCodeUpper(l.code) === 'PL-COT-CI');
            if (existingCiIdx >= 0) {
                // 已存在：强制清空 details，更新 adjustmentAmount 和 entry
                cleanedLines[existingCiIdx] = {
                    ...cleanedLines[existingCiIdx],
                    adjustmentAmount: invAmount,
                    details: [],
                    entry: oppositeEntry,
                } as any;
            } else {
                // 不存在：追加新 line
                const ciFsLine = fsAdjustState.getLineByCode('PL-COT-CI');
                if (ciFsLine) {
                    cleanedLines.push({
                        fsId: ciFsLine.fsId || 0,
                        code: 'PL-COT-CI',
                        currency: 'HKD',
                        adjustmentAmount: invAmount,
                        details: [],
                        entry: oppositeEntry,
                    } as any);
                }
            }
        }

        // PL-COT-PUR 特殊处理：自动追加/更新 PL-COT-COS 一条 line，details 强制为空
        // ✅ BS-CA-INV 发起时不需要追加 PL-COT-COS（仅在 PurchaseWp 发起时追加）
        const purLine = cleanedLines.find((l: any) => normalizeCodeUpper(l.code) === 'PL-COT-PUR');
        if (purLine && !invLine) {
            const purEntry = (purLine as any).entry || 'Dr';
            const fsAdjustState = useFsAdjustStore.getState();

            // ✅ 仅当 BS-CL-OP 存在时，PL-COT-PUR / PL-COT-COS 的 adjustmentAmount 使用 BS-CL-OP 的值
            // 若 BS-CL-OP 不存在（toPaper 为其他 code），保留 fromPaper 原始值
            const opLine = cleanedLines.find((l: any) => normalizeCodeUpper(l.code) === 'BS-CL-OP');
            const opAmount = opLine ? ((opLine as any).adjustmentAmount || 0) : null;

            // ✅ 仅当 BS-CL-OP 存在时，覆盖 PL-COT-PUR 的 adjustmentAmount 和 purchases adjustment
            if (opAmount !== null) {
                const purIdx = cleanedLines.findIndex((l: any) => normalizeCodeUpper(l.code) === 'PL-COT-PUR');
                if (purIdx >= 0) {
                    const purDetails = (cleanedLines[purIdx] as any).details;
                    if (Array.isArray(purDetails)) {
                        const updatedDetails = purDetails.map((detail: any) => {
                            if (!detail || typeof detail !== 'object') return detail;
                            const result: any = {...detail};
                            if (Array.isArray(result.purchases)) {
                                result.purchases = result.purchases.map((row: any) => ({
                                    ...row,
                                    adjustment: opAmount,
                                }));
                            }
                            return result;
                        });
                        cleanedLines[purIdx] = {
                            ...cleanedLines[purIdx],
                            adjustmentAmount: opAmount,
                            details: updatedDetails,
                        } as any;
                    } else {
                        cleanedLines[purIdx] = {
                            ...cleanedLines[purIdx],
                            adjustmentAmount: opAmount,
                        } as any;
                    }
                }
            }

            // PL-COT-COS 的 adjustmentAmount：有 BS-CL-OP 时用其值，否则用 PL-COT-PUR 的原始值
            const cosAmount = opAmount !== null ? opAmount : ((purLine as any).adjustmentAmount || 0);

            // PL-COT-COS：与 PL-COT-PUR 相同 entry，details 强制为空
            const existingCosIdx = cleanedLines.findIndex((l: any) => normalizeCodeUpper(l.code) === 'PL-COT-COS');
            if (existingCosIdx >= 0) {
                cleanedLines[existingCosIdx] = {
                    ...cleanedLines[existingCosIdx],
                    adjustmentAmount: cosAmount,
                    details: [],
                    entry: purEntry,
                } as any;
            } else {
                const cosFsLine = fsAdjustState.getLineByCode('PL-COT-COS');
                if (cosFsLine) {
                    cleanedLines.push({
                        fsId: cosFsLine.fsId || 0,
                        code: 'PL-COT-COS',
                        currency: 'HKD',
                        adjustmentAmount: cosAmount,
                        details: [],
                        entry: purEntry,
                    } as any);
                }
            }
        }

        const fsAdjustStateForSubmit = useFsAdjustStore.getState();
        const TAX_SYNC_CODES = new Set(['BS-CA-TAXR', 'BS-CL-TAXP', 'PL-TAX']);
        const toPaperTaxCode = (state.toPaper?.codes || [])
            .map((c) => normalizeLineCode(c))
            .find((c) => c === 'BS-CA-TAXR' || c === 'BS-CL-TAXP');
        const fromPaperCode = normalizeLineCode(state.fromPaper?.code || '');

        const linesWithFsId = cleanedLines.map((line: any) => {
            let lineCode = normalizeLineCode(String(line?.code || '').trim());

            // Tax WP is stored under PL-TAX but must submit under the FS node picked in toPaper
            if (toPaperTaxCode && lineCode === 'PL-TAX' && fromPaperCode === 'BS-CA-IOT') {
                lineCode = toPaperTaxCode;
                line = {...line, code: lineCode};
            }

            if (TAX_SYNC_CODES.has(lineCode)) {
                const targetCode = (lineCode === 'PL-TAX' && toPaperTaxCode) ? toPaperTaxCode : lineCode;
                const fsLine = fsAdjustStateForSubmit.getLineByCode(targetCode);
                if (fsLine?.fsId) {
                    return {...line, fsId: fsLine.fsId, code: targetCode};
                }
            }
            const existingFsId = Number(line?.fsId) || 0;
            if (existingFsId > 0 || !lineCode) return line;
            const resolved = resolveLineFsId(lineCode);
            return resolved > 0 ? {...line, fsId: resolved} : line;
        });

        // Prefer DialogLauncher bridge; fallback periodLock / WP session (MULTI Period Tab)
        const resolveSubmitYoa = (): string | undefined => {
            const fromBridge = getActiveWpYearOfAssessment();
            if (fromBridge) return fromBridge;
            const lock = state.periodLock;
            const session = state.wpSessionPeriod;
            const periods = lock?.periods?.length ? lock.periods : session?.periods;
            const anchor = lock?.anchorSliceKey || session?.sliceKey || lock?.initiator?.sliceKey;
            if (periods?.length && anchor) {
                const hit = periods.find((p) => p.key === anchor);
                const y = (hit?.yearOfAssessment || '').trim();
                if (y) return y;
            }
            const headerYoa = (state.header as any)?.yearOfAssessment;
            return typeof headerYoa === 'string' && headerYoa.trim() ? headerYoa.trim() : undefined;
        };
        const submitYoa = resolveSubmitYoa();
        const submitCompanyId = Number(state.fromPaper?.companyId) || 0;
        const submitAnnualId = Number(state.fromPaper?.annualId) || 0;
        const submitYoaOrder = (
            state.periodLock?.periods?.length
                ? state.periodLock.periods
                : state.wpSessionPeriod?.periods
        )
            ?.map((p) => String(p?.yearOfAssessment || '').trim())
            .filter(Boolean) || undefined;

        return {
            id: state.header?.id,
            companyId: submitCompanyId,
            annualId: submitAnnualId,
            adjustmentMode: state.currentMode || AdjustmentMode.ADJUST_WP_TO_WP,
            refNo: state.header?.refNo || '',
            status: 0, // 草稿状态
            preparedUserId: userId,
            preparedDate: now,
            reviewedUserId: 0,
            reviewedDate: '',
            // MULTI PL/BS WP: stamp slice YOA so attrs/headers do not leak across Period Tabs
            yearOfAssessment: submitYoa,
            lines: linesWithFsId.map((line: any) => {
                const lineCode = normalizeLineCode(String(line?.code || '').trim());
                const details = Array.isArray(line?.details) ? line.details : [];

                // MULTI BankLoan/VehicleLoan：details[0] 改为 YOA Map，adjustment 仅挂选定 YOA
                if (submitYoa && details.length > 0) {
                    const flat = details[0];
                    if (flat && typeof flat === 'object' && !isLoanMultiYearAdjDetailsMap(flat)) {
                        const signedAdj = Number(
                            (flat as any).adjustment
                            ?? line.adjustment
                            ?? line.adjustmentAmount
                            ?? 0,
                        );
                        const yoaMap = buildLoanMultiYearAdjDetailsMap({
                            code: lineCode,
                            companyId: submitCompanyId,
                            annualId: submitAnnualId,
                            selectedYoa: submitYoa,
                            signedAdjustment: signedAdj,
                            flatDetail: flat,
                            yoaOrder: submitYoaOrder,
                        });
                        if (yoaMap) {
                            return {...line, details: [yoaMap]};
                        }
                    }
                    if (isLoanMultiYearAdjDetailsMap(flat)) {
                        return line;
                    }
                }

                if (!submitYoa) return line;
                if (details.length === 0) {
                    return {...line, details: [{yearOfAssessment: submitYoa}]};
                }
                return {
                    ...line,
                    details: details.map((d: any) =>
                        (d && typeof d === 'object' && !isLoanMultiYearAdjDetailsMap(d))
                            ? {...d, yearOfAssessment: d.yearOfAssessment || submitYoa}
                            : d
                    ),
                };
            }),
        };
    },

    processAndAddLine: (code: string, detail: any, annualId: number, entry?: string) => {
        const adjustLine = processScheduleToLine({code, detail, annualId, entry});
        if (adjustLine) {
            get().upsertLine(adjustLine);
        } else {
            // Fallback: if processScheduleToLine returned null (e.g. getLineByCode not found),
            // construct a minimal line with details so they are never lost
            const fsAdjustState = useFsAdjustStore.getState();
            const fsLine = fsAdjustState.getLineByCode(code);
            const baseEntry = entry || fsLine?.entry || 'Dr';
            const lineCode = normalizeLineCode(code);
            const TAX_SYNC_CODES = new Set(['BS-CA-TAXR', 'BS-CL-TAXP', 'PL-TAX']);
            let resolvedFsId = resolveLineFsId(code, fsLine);
            if (TAX_SYNC_CODES.has(lineCode)) {
                const taxFsLine = fsAdjustState.getLineByCode(lineCode);
                if (taxFsLine?.fsId) {
                    resolvedFsId = taxFsLine.fsId;
                }
            }
            const fallbackLine: AuditAdjustLine = {
                fsId: resolvedFsId,
                code: lineCode || code,
                currency: 'HKD',
                adjustmentAmount: 0,
                details: [detail],
                entry: baseEntry,  // 使用传入的 entry 或默认值
            };
            // Try to calculate adjustmentAmount from detail rows
            try {
                // BS-EQ-DIV / BS-EQ-AL flat：adjustmentAmount 为正；AL 发起带 commonRow 时走常规行汇总
                if (isFlatEquityDetailForCode(code, detail) && detail && typeof detail === 'object') {
                    const rawAdj = resolveFlatEquityAdjustment(detail);
                    fallbackLine.adjustmentAmount = Math.abs(rawAdj);
                    fallbackLine.entry = resolveFlatEquityRenderEntry(entry, fsLine?.entry || 'Cr', rawAdj);
                    fallbackLine.details = [{
                        perClient: Number(detail.perClient) || 0,
                        adjustment: rawAdj,
                    }];
                } else {
                    // Loan field WP→WP / PL-AE-REN field WP→WP / IA field WP→WP: top-level details.adjustment
                    const topLevelAdj = Number((detail as any)?.adjustment);
                    const isRentalFieldWpToWp = normalizeLineCode(code) === 'PL-AE-REN'
                        && Array.isArray((detail as any)?.rows)
                        && (detail as any).rows.length > 0;
                    const ownedIaKey = iaOwnedFieldKey(code);
                    const iaFieldAdj = Number((detail as any)?.[ownedIaKey]);
                    const isIaFieldWpToWp = isIaFieldSyntheticDetail(detail, code)
                        || isItlDepIaFieldTabsNonDtdDetail(detail, code)
                        || (Number.isFinite(iaFieldAdj) && Math.abs(iaFieldAdj) > 0.001);
                    if (Number.isFinite(topLevelAdj) && Math.abs(topLevelAdj) > 0.001
                        && (Array.isArray((detail as any)?.bankLoans)
                            || Array.isArray((detail as any)?.vehicleLoans)
                            || isRentalFieldWpToWp
                            || isIaFieldWpToWp)) {
                        fallbackLine.adjustmentAmount = Math.abs(topLevelAdj);
                    } else if (isIaFieldWpToWp && Number.isFinite(iaFieldAdj) && Math.abs(iaFieldAdj) > 0.001) {
                        fallbackLine.adjustmentAmount = Math.abs(iaFieldAdj);
                    } else {
                    const rows = Array.isArray(detail) ? detail :
                        (detail?.commonRow || detail?.bankRow || detail?.costRow ||
                            detail?.salesIncome || detail?.purchases || detail?.taxDeductible ||
                            detail?.gainExchange || detail?.lossExchange ||
                            detail?.dueFromToDirRow || detail?.commissionPayments ||
                            detail?.bankInterestIncome || detail?.bankLoans || detail?.vehicleLoans || []);
                    if (Array.isArray(rows) && rows.length > 0) {
                        const calculatedAmount = rows.reduce((sum: number, row: any) => {
                            const adj = Number(row.adjustment) || 0;
                            const adjTotal = Array.isArray(row.adjustments)
                                ? row.adjustments.reduce((s: number, a: any) => s + (Number(a.adjustment) || 0), 0)
                                : 0;
                            return sum + adj + adjTotal;
                        }, 0);
                        fallbackLine.adjustmentAmount = Math.abs(calculatedAmount);
                        // entry 直接使用 baseEntry（来自 AdjustDrCrView 渲染列）
                    }
                    }
                }
            } catch (e) {
                console.warn('[adjustDraftStore] fallback adjustmentAmount calculation error:', e);
            }
            get().upsertLine(fallbackLine);
        }
    },

    processAllSchedules: (items: Array<{ code: string; detail: any; annualId: number; entry?: string }>) => {
        items.forEach(item => {
            get().processAndAddLine(item.code, item.detail, item.annualId, item.entry);
        });
    },

    loadFromHeader: (headerData: AuditAdjustHeader, getLineByCode?: (code: string) => any) => {

        if (!headerData || !headerData.lines || headerData.lines.length === 0) {
            console.warn('[adjustDraftStore] No lines in header data');
            return;
        }

        const lines = headerData.lines;
        const isInnerGen = headerData.adjustmentMode === AdjustmentMode.ADJUST_AUTO_GEN;
        const headerCodes = collectLineCodes(lines);
        const isExplicitInvToPur = isInvExplicitPurchasesHeaderLines(lines);
        const hiddenCompanionCodes = resolveCompanionHiddenCodes(headerCodes);
        if (isExplicitInvToPur) {
            hiddenCompanionCodes.delete('PL-COT-PUR');
        }
        const originCode = resolveAdjustmentOrigin(headerCodes);

        let firstLine = lines[0];
        let restLines = lines.slice(1);
        if (originCode) {
            const originLine = lines.find(
                (l) => normalizeFsCode((l as any)?.code || (l as any)?.scheduleCode) === originCode,
            );
            if (originLine) {
                firstLine = originLine;
                restLines = lines.filter((l) => {
                    if (l === originLine) return false;
                    const code = normalizeFsCode((l as any)?.code || (l as any)?.scheduleCode);
                    return !hiddenCompanionCodes.has(code);
                });
            }
        } else {
            restLines = lines.slice(1).filter((l) => {
                const code = normalizeFsCode((l as any)?.code || (l as any)?.scheduleCode);
                return !hiddenCompanionCodes.has(code);
            });
        }

        const firstCode = normalizeLineCode(String((firstLine as any)?.code || (firstLine as any)?.scheduleCode || '').trim());
        const headerId = headerData.id;
        const ppeDepnNbvCrossOnly = isPpeDepnNbvCrossOnlyFieldHeader(lines);

        const consolidateBackendDetail = (detail: any, code?: string, line?: AuditAdjustLine): any =>
            consolidateWpDetailAdjustments(
                detail,
                headerId,
                code,
                Number((line as any)?.adjustmentAmount ?? (line as any)?.adjustment) || 0,
                (line as any)?.entry ?? null,
                (code && getLineByCode ? getLineByCode(code)?.entry : null) ?? null,
            );

        // ✅ 创建默认 detail 数据结构（当 details 为空但 adjustmentAmount 非零时使用）
        const createDefaultDetailForCode = (code: string, adjustmentAmount: number, lineMeta?: any): any => {
            const name = lineMeta?.name || lineMeta?.scheduleName || code;
            const baseRow = {
                id: 1,
                item: name,
                itemEdit: true,
                perClient: 0,
                adjustment: adjustmentAmount,
                finalAmount: adjustmentAmount,
                lastYearFinal: 0,
            };

            // 根据 code 匹配特定处理函数的数据结构
            if (code === 'BS-CL-BOD' || code === 'BS-CA-CASH') {
                // Unique negative key — avoid colliding with other empty bank shells on attr.rowId
                return {
                    bankRow: [{
                        bankId: -1,
                        id: -1,
                        bankName: '',
                        accountNumber: '',
                        perClient: 0,
                        adjustment: adjustmentAmount,
                        lastYearFinal: 0
                    }], commonRow: []
                };
            }
            if (isCommonInvestmentCode(code)) {
                // 使用共享的初始化函数，确保 toPaper 获取与 CommonInvestmentWp 相同的初始数据结构
                return createCommonInvestmentInitialData(code, 0, adjustmentAmount);
            }
            if (isTurnoverIncomeFsCode(code)) {
                const baseDetail = {
                    salesIncome: [baseRow],
                    transactionTest: [],
                    cutOffThisYearTest: [],
                    cutOffNextYearTest: [],
                };
                // loadFromHeader 兜底：仅回填 draft 中已有 annualInfo，不发明默认值
                const draft = get();
                return withTurnoverAnnualInfoIfMissing(
                    baseDetail,
                    collectTurnoverAnnualInfoCandidates(
                        draft.getUnifiedDetail,
                        draft.fromPaper,
                        draft.header ?? headerData,
                        code,
                    ),
                    draft.fromPaper?.annualId ?? draft.header?.annualId ?? headerData.annualId,
                );
            }
            if (code === 'PL-COT-PUR') {
                return {purchases: [baseRow], transactionTest: [], cutOffThisYearTest: [], cutOffNextYearTest: []};
            }
            if (code === 'BS-CL-BLS' || code === 'BS-NCL-BLS') {
                return {bankLoans: [{id: 1, perClient: 0, adjustment: adjustmentAmount, lastYearFinal: 0}]};
            }
            if (code === 'PL-FC-LI') {
                return createPlFcLiAdjustmentDetail(adjustmentAmount);
            }
            if (code === 'BS-CL-OFL' || code === 'BS-NCL-OFL') {
                return {vehicleLoans: [{id: 1, perClient: 0, adjustment: adjustmentAmount, lastYearFinal: 0}]};
            }
            // PL-AE-REN: line-only — never invent blank rows (backend markAllInvalid would wipe leases)
            if (code === 'PL-AE-REN') {
                return {adjustment: adjustmentAmount};
            }
            if (code === 'PL-OI-II' || code === 'PL-OI-SFG') {
                return {bankInterestIncome: [baseRow], otherInterestIncome: []};
            }
            if (code === 'PL-AE-DON') {
                return {taxDeductible: [baseRow], nonTaxDeductible: []};
            }
            if (code === 'PL-AE-COM') {
                return {commissionPayments: [baseRow]};
            }
            if (code === 'PL-OI-EXG' || code === 'PL-AE-EXL') {
                return {gainExchange: [baseRow], lossExchange: []};
            }
            if (code === 'BS-CA-DIR' || code === 'BS-CL-DTD') {
                return {
                    dueFromToDirRow: [normalizeDueFromToDirRow({
                        code,
                        directorName: name,
                        perClient: 0,
                        adjustment: adjustmentAmount,
                        lastYearFinal: 0,
                    })],
                };
            }
            // DividendWp flat 默认；toPaper commonRow 仅由 resolveToPaper* 写入
            if (code === 'BS-EQ-DIV') {
                return {
                    dividendPaid: 0,
                    dividendCurrency: 'HKD',
                    amountPerEachShare: 0,
                    totalDividendAmount: 0,
                    adjustment: adjustmentAmount,
                };
            }
            if (code === 'BS-EQ-AL') {
                return {perClient: 0, adjustment: adjustmentAmount};
            }
            // 默认：commonRow
            return {commonRow: [baseRow]};
        };

        // 检测并转换后端 flat 格式 details 为 WP bankRow 格式（针对 BS-CA-CASH / BS-CL-BOD）
        // 后端返回: [{rowClass: "bankRow", rowId: 18, adjustment: -16640, ...}]
        // 转换为:  {bankRow: [{bankId: 18, bankName: '', accountNumber: '', perClient: 0, adjustment: -16640, lastYearFinal: null}], commonRow: []}
        const transformFlatBankDetails = (details: any[]): any | null => {
            if (!details || !Array.isArray(details) || details.length === 0) return null;
            // 检测是否为 flat 格式（含 rowClass 字段）
            if (!details[0] || typeof details[0] !== 'object' || !('rowClass' in details[0])) return null;

            const bankRows: any[] = [];
            const commonRows: any[] = [];

            details.forEach((item: any) => {
                if (!item || typeof item !== 'object') return;
                if (item.rowClass === 'bankRow') {
                    bankRows.push({
                        bankId: item.rowId ?? null,
                        bankName: item.bankName || '',
                        accountNumber: item.accountNumber || '',
                        perClient: item.perClient ?? 0,
                        adjustment: item.adjustment ?? 0,
                        lastYearFinal: item.lastYearFinal ?? null,
                    });
                } else if (item.rowClass === 'commonRow') {
                    commonRows.push({
                        id: item.rowId ?? 1,
                        item: item.item || '',
                        perClient: item.perClient ?? 0,
                        adjustment: item.adjustment ?? 0,
                        lastYearFinal: item.lastYearFinal ?? null,
                    });
                }
            });

            return {bankRow: bankRows, commonRow: commonRows};
        };

        // Extract details from line - handle array format
        const extractDetail = (line: AuditAdjustLine, code?: string): any => {
            if (!line.details || !Array.isArray(line.details) || line.details.length === 0) {
                return null;
            }

            // Grand Total Depn↔NBV：不把 PPE WP / adjustments 灌进 draft，编辑只走 line entry/amount
            if (isPpeDepScheduleLevelHeader(lines) && isPpeFieldFsCode(code)) {
                return null;
            }

            // Grand Total Amor↔NBV：不把 IA schedule adjustments 灌进 draft，编辑只走 line entry/amount
            if (
                isIaAiaScheduleLevelHeader(lines)
                && (normalizeLineCode(code) === 'BS-NCA-IA' || normalizeLineCode(code) === 'PL-AE-AIA')
            ) {
                return null;
            }

            // Grand Total Taxi Depn↔NBV：不把 ITL schedule adjustments 灌进 draft，编辑只走 line entry/amount
            if (
                isItlDepScheduleLevelHeader(lines)
                && (normalizeLineCode(code) === 'BS-NCA-ITL' || normalizeLineCode(code) === 'PL-AE-DEP')
            ) {
                return null;
            }

            // Depn↔NBV-only header: force slim {ppeRowId, nbvCfAdj|depnChgAdj} (ignore full WP shell)
            if (ppeDepnNbvCrossOnly && isPpeFieldFsCode(code)) {
                const lineEntry = (line as any).entry ?? null;
                const lineAmt = Number((line as any).adjustmentAmount ?? (line as any).adjustment) || 0;
                const first = line.details[0];
                const slimFromAttrs = first && typeof first === 'object' && 'rowClass' in first
                    ? toPpeDepnNbvCrossSlimDetailFromBackend(line.details, code, {
                        headerId,
                        lineEntry,
                        lineAdjustmentAmount: lineAmt,
                    })
                    : null;
                if (slimFromAttrs) return slimFromAttrs;
                const slimFromWp = toPpeDepnNbvCrossSlimDetailFromBackend(first, code, {
                    headerId,
                    lineEntry,
                    lineAdjustmentAmount: lineAmt,
                });
                if (slimFromWp) return slimFromWp;
            }

            // IA / ITL field WP→WP: force slim {intangibleRowId, amorChgAdj|nbvCfAdj}
            // DEP only when attrs are iaField:* (toIaFieldSlim returns null for ppeField)
            if (isIaFieldFsCode(code) || String(code || '').toUpperCase() === 'PL-AE-DEP') {
                const lineEntry = (line as any).entry ?? null;
                const lineAmt = Number((line as any).adjustmentAmount ?? (line as any).adjustment) || 0;
                const first = line.details[0];
                const slim = first && typeof first === 'object' && 'rowClass' in first
                    ? toIaFieldSlimDetailFromBackend(line.details, code, {
                        headerId,
                        lineEntry,
                        lineAdjustmentAmount: lineAmt,
                    })
                    : toIaFieldSlimDetailFromBackend(first, code, {
                        headerId,
                        lineEntry,
                        lineAdjustmentAmount: lineAmt,
                    });
                if (slim) return slim;
            }

            // BS-CA-CASH / BS-CL-BOD: 后端返回的 flat 格式需要独立封装为 WP bankRow 格式
            if ((code === 'BS-CA-CASH' || code === 'BS-CL-BOD') && line.details[0]?.rowClass) {
                const transformed = transformFlatBankDetails(line.details);
                if (transformed) return transformed;
            }

            // PPE field WP→WP (BS-NCA-PPE / PL-AE-DEP + ppeField:* attrs):
            // [{rowClass:"ppeField:costAdj", ...}] → save-shaped details + field markers
            if (
                isPpeFieldFsCode(code)
                && typeof line.details[0]?.rowClass === 'string'
                && String(line.details[0].rowClass).toLowerCase().startsWith('ppefield:')
            ) {
                const transformed = transformFlatPpeFieldAttrDetails(
                    line.details,
                    (line as any).entry,
                    code,
                );
                if (transformed) return transformed;
            }

            // 仅 BS-CL-RND / BS-EQ-AL：[{rowClass:"commonRow",...}] → {commonRow:[...]}
            // 其他 CommonWp 保持原 extract 行为，避免编辑路径被意外改变
            if (
                isCommonRowSyntheticEditCode(code)
                && line.details[0]?.rowClass === 'commonRow'
            ) {
                const transformed = transformFlatBankDetails(line.details);
                if (transformed?.commonRow?.length) return transformed;
            }

            // If details is an array, take the first element, then consolidate
            // Loan WP: strip adjustments{currAdj,nonCurrAdj,inAdj} → top-level adjustment
            return consolidateBackendDetail(line.details[0], code, line);
        };

        // Look up line metadata using provided function (avoids circular dependency)
        const firstLineData = getLineByCode ? getLineByCode(firstCode) : null;
        // ✅ 优先使用后端返回的 entry，其次使用 fsAdjustStore 的 entry
        const fromEntry = (firstLine as any).entry || firstLineData?.entry || null;


        // ✅ 提取 fromPaper 的 line 元数据（name, scheduleName, fsId）
        // 当 firstLineData 为 null 时，使用 firstCode 作为 fallback 名称
        const fromLineMeta = {
            name: firstLineData?.name || firstLineData?.scheduleName || firstCode,
            scheduleName: firstLineData?.scheduleName || firstLineData?.name || firstCode,
            fsId: firstLine.fsId,
        };

        // Determine statement based on code prefix
        const getStatement = (code: string): 'BS' | 'PL' => {
            return code.startsWith('PL-') ? 'PL' : 'BS';
        };

        // Prepare all data first, then set in a single call
        const fromDetail = extractDetail(firstLine, firstCode);

        // Build componentDetails for all lines
        const allComponentDetails: Record<string, any> = {};
        if (fromDetail && firstCode) {
            allComponentDetails[firstCode] = {
                code: firstCode,
                detail: fromDetail,
                lastUpdated: Date.now(),
            };
        }

        // Build toPaper data
        let toPaperData: ToPaperData | null = null;
        if (restLines.length > 0) {
            const toPaperCodes = restLines
                .map(line => normalizeLineCode(String((line as any)?.code || (line as any)?.scheduleCode || '').trim()))
                .filter(code => code);
            const toPaperDetails: Record<string, any> = {};
            const toPaperAdjustmentLines: Record<string, any> = {};
            const toPaperEntries: Record<string, string | null> = {};
            const toPaperOriginalEntries: Record<string, string | null> = {};
            const toPaperLineMetas: Record<string, { name?: string; scheduleName?: string; fsId?: number }> = {};
            // PL-AE-DEP shared PPE/ITL: restore Taxi submenu fetch code when nest shell / iaField evidence
            const toPaperWpFetchCodes: Record<string, string> = {};

            // BS-CA-INV 存在时，PL-COT-PUR / PL-COT-CI 的 details 强制为空（不从后端恢复）
            const isFromInv = originCode === 'BS-CA-INV';
            const INV_EMPTY_DETAIL_CODES = new Set(['PL-COT-PUR', 'PL-COT-CI']);

            const isFromPur = originCode === 'PL-COT-PUR';
            const PUR_EMPTY_DETAIL_CODES = new Set(['PL-COT-COS']);

            restLines.forEach(line => {
                const code = normalizeLineCode(String((line as any)?.code || (line as any)?.scheduleCode || '').trim());
                if (code) {
                    // Look up entry and metadata using provided function (需要提前获取，用于 createDefaultDetailForCode)
                    const lineData = getLineByCode ? getLineByCode(code) : null;
                    const lineMeta = {
                        name: lineData?.name || lineData?.scheduleName || code,
                        scheduleName: lineData?.scheduleName || lineData?.name || code,
                        fsId: line.fsId,
                    };

                    let detail = extractDetail(line, code);
                    const adjustmentAmount = line.adjustmentAmount || 0;

                    if (detail && isDueToDirectorsCode(code)) {
                        detail = normalizeDueFromToDirDetail(detail);
                    }

                    // ✅ BS-CA-INV 存在时，伴生 PL-COT-PUR / PL-COT-CI 的 details 强制为空；
                    //    显式 INV→PUR（purchases 有底稿）时保留 PL-COT-PUR details。
                    if (isFromInv && INV_EMPTY_DETAIL_CODES.has(code.toUpperCase())) {
                        if (!(code.toUpperCase() === 'PL-COT-PUR' && isExplicitInvToPur)) {
                            detail = null;
                        }
                    }

                    // ✅ PL-COT-PUR 存在时，PL-COT-COS 的 details 强制为空
                    if (isFromPur && PUR_EMPTY_DETAIL_CODES.has(code.toUpperCase())) {
                        detail = null;
                    }

                    // ✅ 当 details 为空但 adjustmentAmount 非零时，创建默认 row
                    const isForceEmptyDetail = (
                        isFromInv
                        && INV_EMPTY_DETAIL_CODES.has(code.toUpperCase())
                        && !(code.toUpperCase() === 'PL-COT-PUR' && isExplicitInvToPur)
                    )
                        || (isFromPur && PUR_EMPTY_DETAIL_CODES.has(code.toUpperCase()))
                        || isNoWpDetailCode(code);
                    if (!detail && Math.abs(adjustmentAmount) > 0.001 && !isForceEmptyDetail && !isInnerGen) {
                        detail = createDefaultDetailForCode(code, adjustmentAmount, lineMeta);
                    }

                    if (detail) {
                        toPaperDetails[code] = detail;
                        allComponentDetails[code] = {
                            code,
                            detail: detail,
                            lastUpdated: Date.now(),
                        };
                        // Restore Taxi License submenu discriminator for shared PL-AE-DEP
                        if (
                            code.toUpperCase() === 'PL-AE-DEP'
                            && (isIaNamedNestShellDetail(detail) || isItlDepIaFieldCode(code, detail))
                        ) {
                            toPaperWpFetchCodes[code] = 'BS-NCA-ITL';
                        }
                    }
                    toPaperAdjustmentLines[code] = {
                        companyId: headerData.companyId,
                        annualId: headerData.annualId,
                        scheduleCode: code,
                        fsId: line.fsId,
                        currency: line.currency || 'HKD',
                        adjustmentAmount: adjustmentAmount,
                        details: detail ? [detail] : [],
                        statement: getStatement(code),
                    };

                    // 有效 entry：优先后端 line.entry；originalEntry 仅来自 FS 节点（与 FsSelectionDrawer 一致）
                    toPaperEntries[code] = (line as any).entry || lineData?.entry || null;
                    toPaperOriginalEntries[code] = resolveFsNodeOriginalEntry(code, lineData?.entry) ?? null;

                    // ✅ 存储 toPaper 的 line 元数据
                    toPaperLineMetas[code] = lineMeta;
                }
            });

            toPaperData = {
                codes: toPaperCodes,
                entries: toPaperEntries,
                originalEntries: toPaperOriginalEntries,
                details: toPaperDetails,
                adjustmentLines: toPaperAdjustmentLines,
                lineMetas: toPaperLineMetas, // ✅ 添加 line 元数据
                ...(Object.keys(toPaperWpFetchCodes).length > 0
                    ? {wpFetchCodes: toPaperWpFetchCodes}
                    : {}),
            };
        }

        const loadFromPaperAdjustmentLine = {
            companyId: headerData.companyId,
            annualId: headerData.annualId,
            scheduleCode: firstCode,
            fsId: firstLine.fsId,
            currency: firstLine.currency || 'HKD',
            adjustmentAmount: firstLine.adjustmentAmount || 0,
            entry: fromEntry,
            details: fromDetail ? [fromDetail] : [],
            statement: getStatement(firstCode),
        };

        logFromPaperAssembly({
            source: 'loadFromHeader',
            code: firstCode,
            companyId: headerData.companyId,
            annualId: headerData.annualId,
            lineMeta: fromLineMeta,
            adjustmentLine: loadFromPaperAdjustmentLine,
            entry: fromEntry,
            adjustment: firstLine.adjustmentAmount || 0,
        });

        if (toPaperData) {
            logToPaperAssemblyBatch(
                'loadFromHeader',
                headerData.companyId,
                headerData.annualId,
                toPaperData.codes.map((code) => ({
                    code,
                    lineMeta: toPaperData.lineMetas?.[code],
                    adjustmentLine: toPaperData.adjustmentLines?.[code],
                    entry: toPaperData.entries?.[code],
                    adjustmentAmount: toPaperData.adjustmentLines?.[code]?.adjustmentAmount,
                })),
                toPaperData.details,
            );
        }

        // ✅ Single set() call to update all state at once - prevents partial state issues
        const iaAiaScheduleHydrate = isIaAiaScheduleLevelHeader(lines);
        const initialAssembledCodes = toPaperData?.codes?.filter((code) => {
            // Grand Total Amor↔NBV: mark assembled so Tabs won't WP-fetch overwrite line-only state
            if (iaAiaScheduleHydrate
                && (normalizeLineCode(code) === 'BS-NCA-IA' || normalizeLineCode(code) === 'PL-AE-AIA')) {
                return true;
            }
            const detail = toPaperData.details?.[code];
            return hasNonEmptyUnifiedDetail(detail);
        }) || [];

        const lineDetailsForHydration: Record<string, any> = {};
        if (fromDetail && firstCode) {
            lineDetailsForHydration[firstCode] = fromDetail;
        }
        if (toPaperData?.details) {
            Object.assign(lineDetailsForHydration, toPaperData.details);
        }

        const submitDraft = hydrateDraftFromHeader(headerData, {
            fromCode: firstCode,
            visibleToCodes: toPaperData?.codes || [],
        });

        const fromSignedAdjustment = (() => {
            // PL-AE-REN: AdjustTabs uses line.entry + adjustmentAmount (not summed row rentalAdj)
            if (isRentalFieldSyntheticDetail(fromDetail, firstCode)) {
                return submitDraft.draftSimpleAdjustments[firstCode]
                    ?? Number(firstLine.adjustmentAmount)
                    ?? Number((fromDetail as any)?.adjustment)
                    ?? 0;
            }
            if (isLoanFieldSyntheticDetail(fromDetail, firstCode)
                || isPpeFieldSyntheticDetail(fromDetail, firstCode)
                || isIaFieldSyntheticDetail(fromDetail, firstCode)
                || isItlDepIaFieldTabsNonDtdDetail(fromDetail, firstCode)) {
                const fieldAdj = Number((fromDetail as any).adjustment);
                if (Number.isFinite(fieldAdj) && Math.abs(fieldAdj) >= 0.001) {
                    return fieldAdj;
                }
            }
            return submitDraft.draftSimpleAdjustments[firstCode]
                ?? firstLine.adjustmentAmount
                ?? 0;
        })();

        // Keep draft map in sync with consolidated loan / PPE / IA field detail.adjustment
        // PL-AE-REN: keep hydrate line.adjustmentAmount (backend stamps same rentalAdj on every lease)
        if ((isLoanFieldSyntheticDetail(fromDetail, firstCode)
            || isPpeFieldSyntheticDetail(fromDetail, firstCode)
            || isIaFieldSyntheticDetail(fromDetail, firstCode)
            || isItlDepIaFieldTabsNonDtdDetail(fromDetail, firstCode)) && firstCode) {
            submitDraft.draftSimpleAdjustments[firstCode] = fromSignedAdjustment;
        }
        if (toPaperData?.details) {
            for (const code of toPaperData.codes || []) {
                const d = toPaperData.details[code];
                if (isRentalFieldSyntheticDetail(d, code)) continue;
                if (!isLoanFieldSyntheticDetail(d, code)
                    && !isPpeFieldSyntheticDetail(d, code)
                    && !isIaFieldSyntheticDetail(d, code)
                    && !isItlDepIaFieldTabsNonDtdDetail(d, code)) continue;
                const fieldAdj = Number((d as any).adjustment);
                if (!Number.isFinite(fieldAdj) || Math.abs(fieldAdj) < 0.001) continue;
                submitDraft.draftToPaperAdjustments[code] = fieldAdj;
            }
        }

        const hydratedHeaderLines = lines.map((line) => {
            const code = normalizeLineCode(String((line as any)?.code || (line as any)?.scheduleCode || '').trim());
            const detail = extractDetail(line, code);
            const forceEmptyPpeDep = isPpeDepScheduleLevelHeader(lines) && isPpeFieldFsCode(code);
            const forceEmptyIaAia = isIaAiaScheduleLevelHeader(lines)
                && (code === 'BS-NCA-IA' || code === 'PL-AE-AIA');
            return {
                ...line,
                details: detail != null
                    ? [detail]
                    : ((forceEmptyPpeDep || forceEmptyIaAia)
                        ? []
                        : (Array.isArray(line.details) ? line.details : [])),
            };
        });

        set((state) => ({
            fromPaper: {
                companyId: headerData.companyId,
                annualId: headerData.annualId,
                code: firstCode,
                adjustmentLine: loadFromPaperAdjustmentLine,
                adjustment: fromSignedAdjustment,
                entry: fromEntry,
                lineMeta: fromLineMeta, // ✅ 添加 line 元数据
            },
            toPaper: toPaperData,
            header: {
                ...headerData,
                lines: hydratedHeaderLines,
            },
            currentMode: headerData.adjustmentMode as AdjustmentMode,
            componentDetails: {
                ...state.componentDetails,
                ...allComponentDetails,
            },
            assembledCodes: initialAssembledCodes,
            toPaperAssemblyStatus: Object.fromEntries(
                initialAssembledCodes.map((code) => [code, 'ready' as const]),
            ),
            backendLineIds: submitDraft.backendLineIds,
            draftHydratedFromBackend: true,
            toPaperUserEditedCodes: {},
            fromPaperUserEdited: false,
            draftSimpleAdjustments: submitDraft.draftSimpleAdjustments,
            draftToPaperAdjustments: submitDraft.draftToPaperAdjustments,
            staticPositionCodes: submitDraft.staticPositionCodes,
            specialDtdPathCodes: submitDraft.specialDtdPathCodes,
            draftRemark: resolveRemarkForEdit(headerData.remark, headerData.adjustmentMode),
            fromPaperDetailsSynced: Boolean(fromDetail && firstCode),
            ppeDepnChgDirectAssembleActive: isPpeDepScheduleLevelHeader(lines),
            iaAmorChgDirectAssembleActive: isIaAiaScheduleLevelHeader(lines),
            itlDepnChgDirectAssembleActive: isItlDepScheduleLevelHeader(lines),
        }));
    },

    // ✅ Pair CRUD method implementations (§4.3)
    createPair: (from, to) => {
        const pairId = generatePairId();
        const newPair: AdjustmentPair = {
            id: pairId,
            from: {
                code: normalizeLineCode(from.code) || from.code,
                rowBinding: from.rowBinding,
                adjustmentAmount: from.adjustmentAmount ?? 0,
                rowData: from.rowData,
                entry: from.entry,
            },
            to: to.map(t => ({
                code: normalizeLineCode(t.code) || t.code,
                rowBinding: t.rowBinding,
                adjustmentAmount: t.adjustmentAmount ?? 0,
                rowData: t.rowData,
                entry: t.entry,
            })),
        };
        set(state => ({pairs: [...state.pairs, newPair]}));
        return pairId;
    },

    updatePair: (pairId, updates) => {
        set(state => ({
            pairs: state.pairs.map(p => {
                if (p.id !== pairId) return p;
                const updated = {...p};
                if (updates.from) {
                    updated.from = {...p.from, ...updates.from};
                }
                if (updates.to) {
                    updated.to = p.to.map((t, i) =>
                        updates.to![i] ? {...t, ...updates.to![i]} : t
                    );
                    // If updates.to has more items than existing, append them
                    if (updates.to.length > p.to.length) {
                        for (let i = p.to.length; i < updates.to.length; i++) {
                            const newTo = updates.to[i];
                            if (newTo.code) {
                                updated.to.push({
                                    code: newTo.code,
                                    adjustmentAmount: newTo.adjustmentAmount ?? 0,
                                    ...newTo,
                                } as PairEndpoint);
                            }
                        }
                    }
                }
                return updated;
            }),
        }));
    },

    updatePairAmount: (pairId, side, amount) => {
        set(state => ({
            pairs: state.pairs.map(p => {
                if (p.id !== pairId) return p;
                if (side === 'from') {
                    return {...p, from: {...p.from, adjustmentAmount: amount}};
                }
                // side is a number index into to[]
                const toIndex = side as number;
                if (toIndex >= 0 && toIndex < p.to.length) {
                    const newTo = [...p.to];
                    newTo[toIndex] = {...newTo[toIndex], adjustmentAmount: amount};
                    return {...p, to: newTo};
                }
                return p;
            }),
        }));
    },

    removePair: (pairId) => {
        set(state => ({pairs: state.pairs.filter(p => p.id !== pairId)}));
    },

    getAllPairs: () => get().pairs,

    getPair: (pairId) => get().pairs.find(p => p.id === pairId),

    clearPairs: () => set({pairs: []}),

    updateFromPaperEntry: (entry: string) => {
        const state = get();
        if (!state.fromPaper) return;
        // Skip if entry is the same (avoid unnecessary re-renders)
        if (state.fromPaper.entry === entry) return;
        set({
            fromPaper: {
                ...state.fromPaper,
                entry,
            },
            detailRevision: state.detailRevision + 1,
        });
    },

    updateToPaperEntry: (code: string, entry: string) => {
        const state = get();
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode || !state.toPaper) return;
        const currentEntries = state.toPaper.entries || {};
        // Skip if entry is the same (avoid unnecessary re-renders)
        if (currentEntries[normalizedCode] === entry) return;
        set({
            toPaper: {
                ...state.toPaper,
                entries: {
                    ...currentEntries,
                    [normalizedCode]: entry,
                },
            },
            detailRevision: state.detailRevision + 1,
        });
    },

    // ✅ 新增：多个 fromPaper 管理方法实现
    setMultipleFromPaper: (code: string, data: FromPaperData) => {
        const normalizedCode = normalizeLineCode(code);
        if (!normalizedCode) return;

        const fsLine = useFsAdjustStore.getState().getLineByCode(normalizedCode);
        const originalEntry = fsLine?.entry ?? data.entry ?? 'Dr';
        const resolvedFields = resolveFromPaperAdjustmentFields(originalEntry, Number(data.adjustment ?? 0) || 0, normalizedCode);

        const multipleFromPaperPayload = {
            ...data,
            code: normalizedCode,
            entry: resolvedFields.entry,
            adjustment: resolvedFields.adjustment,
            adjustmentLine: data.adjustmentLine ? {
                ...data.adjustmentLine,
                entry: resolvedFields.entry,
                adjustment: resolvedFields.adjustment,
                adjustmentAmount: resolvedFields.adjustmentAmount,
            } : data.adjustmentLine,
        };
        logFromPaperAssembly({
            source: 'setMultipleFromPaper',
            code: normalizedCode,
            companyId: multipleFromPaperPayload.companyId,
            annualId: multipleFromPaperPayload.annualId,
            lineMeta: multipleFromPaperPayload.lineMeta,
            adjustmentLine: multipleFromPaperPayload.adjustmentLine,
            entry: multipleFromPaperPayload.entry,
            adjustment: multipleFromPaperPayload.adjustment,
            originalEntry,
            rawAdjustment: Number(data.adjustment ?? 0) || 0,
            resolvedFields,
        });
        set(state => ({
            multipleFromPapers: {
                ...state.multipleFromPapers,
                [normalizedCode]: multipleFromPaperPayload,
            },
        }));
    },

    getMultipleFromPapers: () => get().multipleFromPapers,

    clearMultipleFromPapers: () => set({multipleFromPapers: {}}),

    hasMultipleFromPapers: () => Object.keys(get().multipleFromPapers).length > 0,
}));
